import { LotCorrectionFields, validCorrection } from '../../components/LotCorrectionFields.js';
import { indexWarehouseLotReceipts, selectWarehouseFoods } from "../../lib/warehouse/warehouseFilters.js";
import { toUserMessage } from "../../lib/errors.js";
import {
  IonActionSheet,
  IonAlert,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from "@ionic/react";
import {
  add,
  calendarOutline,
  cameraOutline,
  checkmarkCircleOutline,
  chevronForward,
  closeOutline,
  createOutline,
  documentTextOutline,
  removeCircleOutline,
  scanOutline,
  swapHorizontalOutline,
  timeOutline,
  trashOutline,
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBackGuard } from "../../lib/hooks/useBackGuard.js";
import { usePageActivity } from "../../lib/hooks/usePageActivity.js";
import { NATIVE_PERF_TRACE_ENABLED } from "../../lib/debug/nativePerfTrace.js";
import { WarehousePerfTrace } from "../../lib/debug/WarehousePerfTrace.js";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  type CaptureSource,
  getCaptureProvider,
} from "../../lib/adapters/camera/index.js";
import { useAuth } from "../../lib/auth/AuthProvider.js";
import {
  canCreateFood,
  hasAppCapability,
} from "../../lib/auth/capabilities.js";
import {
  useCollettivaEnabled,
  useEnqueueMaster,
  useEnqueueStockReconciliation,
  useEnqueueStockCommand,
  useEvents,
  useMasters,
  useStockProjection,
  useWarehouseEnabled,
} from "../../lib/data/queries.js";
import { getSupabase, SUPABASE_URL } from "../../lib/supabase.js";
import { formatMoney, moneyInputValue, roundMoney } from "../../lib/money.js";
import {
  type EmitStockInInput,
  useLabelLoadQueue,
} from "../../lib/labelQueue/useLabelLoadQueue.js";
import { useAiSuggestions } from "../../lib/haccp/suggestions.js";
import { AiSuggestionsFab } from "../../components/AiSuggestionsFab.js";
import { scheduleExpirationNotifications } from "../../lib/notifications/expirationNotifier.js";
import { useExpirationPreferences } from "../../lib/haccp/expirationPreferences.js";
import {
  buildConsumeStockCommandV1,
  buildInventoryAdjustmentStockCommandV1,
  convertQty,
  FOOD_CATEGORIES,
  projectStock,
  type StockCommandV1,
  type StockProjectorEvent,
} from "@trackeat/shared";
import {
  FoodEditorModal,
  type FoodEditorValue,
} from "../../components/editors/FoodEditorModal.js";
import { UomSelect } from "../../components/UomSelect.js";
import {
  FeatureIntroBlock,
  isIntroDismissed,
} from "../../components/FeatureCard.js";
import {
  SupplierEditorModal,
  type SupplierEditorValue,
} from "../../components/editors/SupplierEditorModal.js";
import {
  PickerTriggerField,
  SearchPickerModal,
} from "../../components/SearchPickerModal.js";
import { CollettivaQuickNav } from "../../components/CollettivaQuickNav.js";
import { openTechSheet } from "../../lib/documents/techSheet.js";
import {
  type LegacyStockReconciliation,
  listLegacyStockReconciliations,
  resolveLegacyStockReconciliation,
} from "../../lib/sync/legacyStockReconciliation.js";
import {
  acknowledgeLegacyPermanentStockCommandRejection,
  listStockCommandReconciliations,
  listUnarchivedPermanentStockCommandRejections,
  resolveStockCommandReconciliation,
  type LegacyPermanentStockCommandRejection,
  type StockCommandReconciliation,
} from "../../lib/sync/stockCommandSqlite.js";
import { useSyncState } from "../../lib/sync/SyncProvider.js";
import {
  findExactThenLegacyStockEvent,
  stockEventMatchesBusiness,
} from "../../lib/stockEventBusinessScope.js";
import { getSyncEngine } from "../../lib/sync/engine.js";
import { shipmentLineReservesStockV1 } from "../../lib/shipmentStockPolicy.js";
import {
  canOpenUomRebase,
  uomRebaseUnitPrice,
  rebaseQuarantineBlock,
} from "../../lib/warehouse/uomRebase.js";
import type {
  StockReconciliationSnapshotRowV1,
} from "../../lib/warehouse/stockReconciliation.js";

interface Food {
  id: string;
  name: string;
  creation_source?: string | null;
  default_uom: string;
  category: string | null;
  storage_type?: string;
  target_temp_c_min?: number | null;
  target_temp_c_max?: number | null;
  barcode?: string | null;
  photo_file_id?: string | null;
  data_sheet_file_id?: string | null;
  allergens?: string[];
  current_batch_lot?: string | null;
  current_expiration_date?: string | null;
  current_document_id?: string | null;
  current_supplier_id?: string | null;
  current_lot_received_at?: string | null;
  version?: number;
  archived_at?: string | null;
}

interface Recipe {
  id: string;
  name: string;
  ingredients?: Array<{ food_id: string }>;
  archived_at?: string | null;
  // T-441: le varianti dieta (ricette figlie) replicano gli ingredienti della base
  // → escluse dal conteggio "usato in N ricette" per non contarle due volte.
  variant_of_recipe_id?: string | null;
}

function expiryBadge(
  daysLeft: number | null,
): { cls: string; text: string } | null {
  if (daysLeft == null) return null;
  if (daysLeft < 0) {
    return {
      cls: "niveo-badge niveo-badge--danger",
      text: `Scaduto ${-daysLeft}g fa`,
    };
  }
  if (daysLeft === 0) {
    return { cls: "niveo-badge niveo-badge--danger", text: "Scade oggi" };
  }
  if (daysLeft <= 2) {
    return { cls: "niveo-badge niveo-badge--warn", text: `${daysLeft}g` };
  }
  if (daysLeft <= 7) {
    return { cls: "niveo-badge niveo-badge--neutral", text: `${daysLeft}g` };
  }
  return null;
}

const STORAGE_BAR_COLOR: Record<string, string> = {
  fresco: "#0ea5e9",
  surgelato: "#6366f1",
  secco: "#f59e0b",
  bevanda: "#10b981",
};

interface Supplier {
  id: string;
  name: string;
  category: string;
  piva: string | null;
  country: string | null;
  email?: string | null;
  phone?: string | null;
  version?: number;
  archived_at?: string | null;
}
interface StockEvent {
  id: string;
  client_ts: string;
  server_rev?: number | null;
  kind?: "stock_in" | "stock_out";
  // T-221: sede del movimento (events.business_id); NULL sugli eventi storici
  // pre-migrazione locale → validi per ogni sede.
  business_id?: string | null;
  payload: {
    business_id?: string | null;
    food_id: string;
    qty: number;
    uom?: string;
    stock_qty?: number;
    stock_uom?: string;
    batch_lot: string | null;
    expiration_date: string | null;
    document_date?: string | null;
    document_number?: string | null;
    supplier_id?: string | null;
    document_id?: string | null;
    storage_unit_id?: string | null;
    batch_lot_source?: "document" | "document_number_fallback";
    lot_closed?: boolean;
    lot_action?: "updated_active" | "recorded_only";
    lot_state_action?: "register" | "close" | "update_metadata";
    command_seq?: number;
    movement_class?: string;
    economic_effect?: string;
    uom_rebase_source?: boolean;
    uom_rebase_snapshot?: boolean;
    uom_rebase_zero?: boolean;
  };
}

interface LotEntry {
  lot: string;
  expiration_date: string | null;
  received_at: string; // client_ts del primo stock_in
  document_id: string | null;
}

interface DraftShipmentReservation {
  id: string;
  business_id?: string | null;
  status?: string | null;
  archived_at?: string | null;
  lines?:
    | Array<{
      food_id?: string | null;
      production_id?: string | null;
      stock_effect_v1?: "dispatch" | "none" | string | null;
      batch_lot?: string | null;
      storage_unit_id?: string | null;
      qty?: number | string | null;
      uom?: string | null;
    }>
    | null;
}

// Convenzione semantica:
//   - foods   = anagrafica alimenti base (master). Tab "Alimenti".
//   - lots    = giacenza reale per-lotto da eventi stock_in/out. Tab "Giacenza".
//   - suppliers = master fornitori. Tab "Fornitori".
type Tab = "foods" | "lots" | "suppliers";
const FOOD_PAGE_SIZE = 50;

type ModalKind = null | "stock_in" | "stock_out" | "quick_load";

function warehouseLotKey(foodId: string, lot: string | null): string {
  return JSON.stringify([foodId, (lot ?? "").trim() || null]);
}

function normalizeFoodSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("it")
    .trim();
}

interface UnitFixRow {
  key: string;
  lot: string | null;
  expires: string | null;
  oldQty: number;
  newQty: string;
  onHandValue: number | null;
  averageUnitCost: number | null;
  currency: string | null;
  reservedQty: number;
  eventCount: number;
  lastEventId: string | null;
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
}

interface UnitFixState {
  foodId: string;
  foodName: string;
  foodVersion: number;
  oldUom: string;
  newUom: string;
  rows: UnitFixRow[];
}

interface StockReassignmentState {
  sourceFoodId: string;
  sourceFoodName: string;
  sourceFoodVersion: number;
  sourceUom: string;
  targetFoodId: string;
  sourceRecipeCount: number;
  rows: StockReconciliationSnapshotRowV1[];
  archiveSource: boolean;
  supplierMatchCount: number | null;
}

interface PhysicalRecountRow {
  key: string;
  lot: string | null;
  expirationDate: string | null;
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
  qty: string;
  value: number | null;
  currency: string | null;
}

interface PhysicalRecountState {
  foodId: string;
  foodName: string;
  foodVersion: number;
  archived: boolean;
  sourceUom: string;
  targetUom: string;
  sourceRecipeCount: number;
  confirmationMode: "verified_projection" | "manual";
  rows: PhysicalRecountRow[];
}

// Sync health changes frequently; only a new push needs these lists refreshed.
function WarehouseReconciliationRefresh({ onRefresh }: {
  onRefresh: () => Promise<void>;
}): null {
  const { lastPushAt } = useSyncState();
  useEffect(() => {
    void onRefresh().catch(() => undefined);
  }, [onRefresh, lastPushAt]);
  return null;
}

function WarehouseClock({ onChange }: { onChange: (now: number) => void }): null {
  const { isActive } = usePageActivity("/warehouse");
  useEffect(() => {
    if (!isActive) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const refresh = (): void => {
      clearInterval(timer);
      if (document.visibilityState !== "visible") return;
      onChange(Date.now());
      timer = setInterval(() => onChange(Date.now()), 60_000);
    };
    refresh();
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [isActive, onChange]);
  return null;
}

export function WarehousePage(): JSX.Element {
  const { t } = useTranslation();
  const { profile, switchBusiness } = useAuth();
  const expirationPreferences = useExpirationPreferences(profile?.active_business_id ?? null);
  const canCreateFoods = canCreateFood(profile);
  const canViewHistoricalLots = hasAppCapability(
    profile,
    "warehouse.historical_lots",
  );
  const history = useHistory();
  const queryClient = useQueryClient();
  // Tab "Giacenza" disponibile quando il flag tenant.warehouse_enabled è ON
  // (piano Pro). Sul piano base parte di default su "Alimenti".
  // T-224: il modulo collettiva implica il magazzino (carico prodotti finiti,
  // scarico ingredienti, DDT che muovono stock) → con collettiva ON la
  // gestione giacenza è sempre disponibile, anche se il flag warehouse non è
  // ancora propagato al client (il trigger DB lo allinea, ma OR-iamo qui per
  // mostrare subito la tab Giacenza al ristoratore che ha appena attivato).
  const collettivaEnabled = useCollettivaEnabled();
  const warehouseEnabled = useWarehouseEnabled() || collettivaEnabled;
  const [tab, setTab] = useState<Tab>(warehouseEnabled ? "lots" : "foods");
  // Se l'utente disattiva il flag mentre è sulla tab "Giacenza", riportalo su "Alimenti".
  useEffect(() => {
    if (!warehouseEnabled && tab === "lots") setTab("foods");
  }, [warehouseEnabled, tab]);
  // La giacenza è una vista di fatti append-only: deve poter rendere visibile
  // anche un alimento archiviato in passato con saldo residuo. Gli altri
  // picker continuano invece a escludere gli archiviati.
  const { data: foodsData, isError: foodsError } = useMasters<Food>("foods", {
    includeArchived: true,
  });
  const foods = useMemo(() => foodsData ?? [], [foodsData]);
  const canonicalUomByFood = useMemo(
    () =>
      Object.fromEntries(
        foods
          // Un archive storico non cancella i fatti stock append-only: continuiamo
          // a proiettarlo per rendere azionabili saldi/reserved pre-guard.
          .filter((food) => food.default_uom?.trim())
          .map((food) => [food.id, food.default_uom]),
      ),
    [foods],
  );
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  const stockByFood = useMemo(() => {
    const result = new Map<string, number>();
    for (const balance of stockProjection.balances) {
      result.set(
        balance.foodId,
        (result.get(balance.foodId) ?? 0) + balance.onHandQty,
      );
    }
    return result;
  }, [stockProjection.balances]);
  const stockRelevantFoodIds = useMemo(() => {
    const result = new Set<string>();
    for (const balance of stockProjection.balances) {
      if (
        Math.abs(balance.onHandQty) > 1e-9 ||
        Math.abs(balance.reservedQty) > 1e-9
      ) result.add(balance.foodId);
    }
    for (const item of stockProjection.quarantine) {
      const foodId = item.event.payload?.food_id;
      if (typeof foodId === "string") result.add(foodId);
    }
    return result;
  }, [stockProjection.balances, stockProjection.quarantine]);
  const { data: suppliers = [] } = useMasters<Supplier>("suppliers");
  const { data: recipes = [] } = useMasters<Recipe>("recipes");
  const { data: storageUnits = [] } = useMasters<
    { id: string; name: string; kind: string; archived_at?: string | null }
  >("storage_units");
  // T-136: documenti esistenti (DDT/fattura) per il collegamento OPZIONALE di
  // un lotto manuale a un documento già caricato (oltre al riferimento testuale).
  const { data: documents = [] } = useMasters<
    {
      id: string;
      document_number?: string | null;
      document_date?: string | null;
      kind?: string | null;
      archived_at?: string | null;
    }
  >("documents");
  // T-224: sedi (business) dell'azienda attiva per lo switcher "magazzino di
  // quale sede" — con collettiva attiva un'azienda ha più magazzini (centrale,
  // impianto, …) e dev'essere agevole passare dall'uno all'altro.
  const {
    data: allBusinessesData,
    isLoading: businessesLoading,
    isError: businessesError,
  } = useMasters<
    {
      id: string;
      tenant_id?: string | null;
      name?: string | null;
      archived_at?: string | null;
    }
  >("businesses");
  const allBusinesses = useMemo(() => allBusinessesData ?? [], [allBusinessesData]);
  const tenantBusinesses = useMemo(
    () =>
      allBusinesses
        .filter((b) =>
          (b.tenant_id == null || b.tenant_id === profile?.active_tenant_id) &&
          !b.archived_at
        )
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "it")),
    [allBusinesses, profile?.active_tenant_id],
  );
  // R3 #4: limit null = nessun cap — la giacenza/netto per lotto (lotMap)
  // dev'essere completa, non troncata a 1000 eventi (allineato a TransferPage;
  // memory feedback_no_app_side_limits_for_user_features).
  const {
    data: stockInData,
    isLoading: stockInLoading,
    isError: stockInError,
  } = useEvents("stock_in", null);
  const stockInAll = useMemo(() => stockInData ?? [], [stockInData]);
  const {
    data: stockOutData,
    isLoading: stockOutLoading,
    isError: stockOutError,
  } = useEvents("stock_out", null);
  const stockOutAll = useMemo(() => stockOutData ?? [], [stockOutData]);
  const {
    data: stockReservedData,
    isLoading: stockReservedLoading,
    isError: stockReservedError,
  } = useEvents("stock_reserved", null);
  const stockReservedAll = stockReservedData ?? [];
  const {
    data: draftShipmentsData,
    isLoading: draftShipmentsLoading,
    isError: draftShipmentsError,
  } = useMasters<DraftShipmentReservation>("shipments");
  const draftShipments = draftShipmentsData ?? [];
  // A first SQLite read is not an empty warehouse. Cached rows remain usable
  // if a later refresh fails; the canonical writers keep their own checks.
  const warehouseDataMissing = [
    foodsData, allBusinessesData, stockInData, stockOutData, stockReservedData, draftShipmentsData,
  ].some((rows) => rows === undefined);
  const warehouseReadError = foodsError || businessesError || stockInError
    || stockOutError || stockReservedError || draftShipmentsError;
  function retryWarehouseRead(): void {
    for (const queryKey of [
      ['masters', 'foods'], ['masters', 'businesses'], ['masters', 'shipments'],
      ['events', 'stock_in'], ['events', 'stock_out'], ['events', 'stock_reserved'],
    ]) {
      void queryClient.refetchQueries({ queryKey: [...queryKey, profile?.active_tenant_id] });
    }
  }
  // T-221: ogni sede ha il suo magazzino — qui si vede SOLO la giacenza della
  // sede attiva. Gli eventi storici senza sede sono utilizzabili soltanto se
  // il catalogo dimostra che il tenant ha una singola sede attiva.
  const activeBusinessId = profile?.active_business_id ?? null;
  const allowLegacyUnscopedBusiness = !businessesLoading &&
    !businessesError &&
    activeBusinessId !== null &&
    tenantBusinesses.length === 1 &&
    tenantBusinesses[0]?.id === activeBusinessId;
  const stockIn = useMemo(
    () =>
      (stockInAll as StockEvent[]).filter((event) =>
        stockEventMatchesBusiness(
          event,
          activeBusinessId,
          allowLegacyUnscopedBusiness,
        )
      ),
    [stockInAll, activeBusinessId, allowLegacyUnscopedBusiness],
  );
  const stockOut = useMemo(
    () =>
      (stockOutAll as StockEvent[]).filter((event) =>
        stockEventMatchesBusiness(
          event,
          activeBusinessId,
          allowLegacyUnscopedBusiness,
        )
      ),
    [stockOutAll, activeBusinessId, allowLegacyUnscopedBusiness],
  );
  const createMaster = useEnqueueMaster();
  const stockCommand = useEnqueueStockCommand();
  const stockReconciliation = useEnqueueStockReconciliation();
  const stockBalanceByLot = useMemo(() =>
    new Map(
      stockProjection.balances.map((balance) => [
        warehouseLotKey(balance.foodId, balance.batchLot),
        balance,
      ]),
    ), [stockProjection.balances]);
  // T-407: CODA carico rapido. emitStockIn emette lo stock_in COMPLETO
  // (qty+lotto+scadenza+foto) quando il worker ha estratto i dati dall'etichetta.
  const emitLabelStockIn = useCallback(
    async (input: EmitStockInInput): Promise<void> => {
      if (!profile?.active_tenant_id || !profile.active_business_id) {
        throw new Error("Nessun locale attivo");
      }
      const food = foods.find((f) => f.id === input.foodId);
      await stockCommand.mutateAsync({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: profile.active_tenant_id,
        movement_class: "receive",
        economic_effect: "stock_only",
        business_id: profile.active_business_id,
        reason: "quick_label_receive",
        lines: [{
          food_id: input.foodId,
          operator_name: profile.full_name?.trim() || null,
          storage_unit_id: input.storageUnitId,
          batch_lot: input.batchLot,
          expiration_date: input.expirationDate,
          notify_expiration: true,
          is_frozen: food?.storage_type === "surgelato" ||
            food?.storage_type === "congelato",
          photo_path: input.photoPath ?? undefined,
          lot_action: "updated_active",
          source_qty: input.qty,
          source_uom: input.uom,
          source_unit_price: null,
          source_line_total: null,
          currency: "EUR",
          stock_uom: input.uom,
          conversion: { factor: 1, source: "identity" },
        }],
      });
    },
    [profile, foods, stockCommand],
  );
  const labelQueue = useLabelLoadQueue({
    tenantId: profile?.active_tenant_id ?? null,
    businessId: profile?.active_business_id ?? null,
    emitStockIn: emitLabelStockIn,
  });
  // Form del "carico rapido" (un articolo alla volta → in coda).
  const [qlFood, setQlFood] = useState("");
  const [qlQty, setQlQty] = useState("");
  const [qlStorage, setQlStorage] = useState("");
  const [qlPhotos, setQlPhotos] = useState<string[]>([]);
  // Input manuali per gli item 'needs_fix' (etichetta non letta): id → {lot, exp}.
  const [qlFix, setQlFix] = useState<
    Record<string, { lot: string; exp: string }>
  >({});

  async function qlCapturePhoto(): Promise<void> {
    try {
      const photo = await getCaptureProvider().takePhoto("camera");
      if (photo) setQlPhotos((p) => [...p, photo.base64]);
    } catch (err) {
      setToast(toUserMessage(err, "Cattura annullata"));
    }
  }
  function qlAddToQueue(): void {
    const qty = Number(qlQty.replace(",", "."));
    const food = foods.find((f) => f.id === qlFood);
    if (!food || !Number.isFinite(qty) || qty <= 0) {
      setToast("Scegli il prodotto e una quantità valida");
      return;
    }
    if (qlPhotos.length === 0) {
      setToast("Scatta almeno una foto dell'etichetta (lotto/scadenza)");
      return;
    }
    labelQueue.addToQueue({
      foodId: food.id,
      foodName: food.name,
      qty,
      uom: food.default_uom || "pz",
      storageUnitId: qlStorage || null,
      photosB64: qlPhotos,
    });
    setToast(`In coda: ${food.name} ×${qty}`);
    // Reset per l'articolo successivo (tengo l'unità di stoccaggio per comodità).
    setQlFood("");
    setQlQty("");
    setQlPhotos([]);
  }
  const suggestions = useAiSuggestions();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [legacyReconciliationOpen, setLegacyReconciliationOpen] = useState(
    false,
  );
  // T-484 rework: il banner di quarantena non deve essere un vicolo cieco.
  // La coda espone soltanto le riconciliazioni UdM che il sistema può eseguire
  // senza dedurre scarichi o quantità fisiche.
  const [uomReconciliationOpen, setUomReconciliationOpen] = useState(false);
  const [legacyReconciliations, setLegacyReconciliations] = useState<
    LegacyStockReconciliation[]
  >([]);
  const [stockCommandReconciliations, setStockCommandReconciliations] =
    useState<StockCommandReconciliation[]>([]);
  const [legacyPermanentRejections, setLegacyPermanentRejections] =
    useState<LegacyPermanentStockCommandRejection[]>([]);
  const [legacyResolveConfirm, setLegacyResolveConfirm] = useState<
    LegacyStockReconciliation | null
  >(null);
  const [stockCommandResolveConfirm, setStockCommandResolveConfirm] = useState<
    StockCommandReconciliation | null
  >(null);
  const [legacyPermanentResolveConfirm, setLegacyPermanentResolveConfirm] =
    useState<LegacyPermanentStockCommandRejection | null>(null);
  const refreshLegacyReconciliations = useCallback(async (): Promise<void> => {
    if (!profile?.active_tenant_id) {
      setLegacyReconciliations([]);
      return;
    }
    try {
      setLegacyReconciliations(
        await listLegacyStockReconciliations({
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
        }),
      );
    } catch {
      // Il ledger è diagnostico locale: un errore di lettura non deve bloccare
      // le normali operazioni di magazzino.
    }
  }, [profile?.active_business_id, profile?.active_tenant_id]);
  useEffect(() => {
    void refreshLegacyReconciliations();
    const refresh = (): void => {
      void refreshLegacyReconciliations();
    };
    window.addEventListener("trackeat:legacy-stock-reconciliation", refresh);
    return () =>
      window.removeEventListener(
        "trackeat:legacy-stock-reconciliation",
        refresh,
      );
  }, [refreshLegacyReconciliations]);
  const refreshStockCommandReconciliations = useCallback(async (): Promise<void> => {
    if (!profile?.active_tenant_id) {
      setStockCommandReconciliations([]);
      setLegacyPermanentRejections([]);
      return;
    }
    const [current, legacy] = await Promise.all([
      listStockCommandReconciliations({
        tenantId: profile.active_tenant_id,
      }),
      listUnarchivedPermanentStockCommandRejections(),
    ]);
    setStockCommandReconciliations(current);
    setLegacyPermanentRejections(legacy);
  }, [profile?.active_tenant_id]);
  // Stato per FoodEditorModal: il form vero vive nel componente, qui teniamo
  // solo apertura e oggetto da editare (assente = modalità "Nuovo").
  const [foodEditorOpen, setFoodEditorOpen] = useState(false);
  const [foodToEdit, setFoodToEdit] = useState<Food | undefined>(undefined);
  // Quando il FoodEditor è aperto da dentro il modal "Carico magazzino"
  // (stock_in) per "+ Crea nuovo prodotto (non in lista)", al salvataggio del
  // food il suo id va automaticamente preselezionato come stockForm.food_id.
  const [stockFoodCreatePending, setStockFoodCreatePending] = useState(false);
  // Stesso pattern per il SupplierEditorModal.
  const [supplierEditorOpen, setSupplierEditorOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState<Supplier | undefined>(
    undefined,
  );
  // Pending: il fornitore appena creato deve essere preselezionato in stockForm.
  const [stockSupplierCreatePending, setStockSupplierCreatePending] = useState(
    false,
  );
  const [supplierSearch, setSupplierSearch] = useState("");
  const [foodSearch, setFoodSearch] = useState("");
  const [lotSearch, setLotSearch] = useState("");
  const [confirmArchive, setConfirmArchive] = useState<
    | {
      table: "foods" | "suppliers";
      id: string;
      name: string;
      version: number;
      supplierMatchCount?: number | null;
    }
    | null
  >(null);
  // Forza re-render al ritorno dall'app in background. La WebView Android
  // ricrea la pagina al resume; i dati arrivano via TanStack Query ma la UI
  // non sempre si rinfresca (Ionic page cache + concurrent mode). Questo
  // counter, incrementato al visibilitychange, è usato come `key` sulla
  // IonContent → re-mount forzato del subtree → UI sempre aggiornata.
  const [resumeKey, setResumeKey] = useState(0);
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        setResumeKey((k) => k + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  const [selectedLot, setSelectedLot] = useState<
    { food_id: string; lot: string | null } | null
  >(null);
  // Azioni sul lotto selezionato (scarico / rettifica)
  type LotActionModal = null | "qty" | "date" | "discharge";
  const [lotActionModal, setLotActionModal] = useState<LotActionModal>(null);
  // Scarico parziale dal Dettaglio lotto: quantità prelevata + motivo. Lo scarico
  // viene SEMPRE emesso col batch_lot del lotto selezionato (vedi handleLotPartial
  // Discharge), così la giacenza del lotto e la conversione UdM lo contano. Il
  // vecchio scarico dal tasto "+" salvava batch_lot=null → orfano per-lotto.
  const [lotDischargeInput, setLotDischargeInput] = useState("");
  const [lotDischargeReason, setLotDischargeReason] = useState<
    "consumed" | "waste"
  >("consumed");
  // La UdM canonica appartiene all'alimento: la correzione converte tutti i
  // saldi fisici aperti in un unico StockCommand atomico.
  const [unitFix, setUnitFix] = useState<UnitFixState | null>(null);
  const [unitFixBusy, setUnitFixBusy] = useState(false);
  const [stockReassignment, setStockReassignment] =
    useState<StockReassignmentState | null>(null);
  const [stockReassignmentQuery, setStockReassignmentQuery] = useState("");
  const [physicalRecount, setPhysicalRecount] =
    useState<PhysicalRecountState | null>(null);
  const [lotConfirmDischarge, setLotConfirmDischarge] = useState<
    { kind: "consumed" | "waste"; qty: number; uom: string } | null
  >(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionCopies, setCorrectionCopies] = useState("0");
  useEffect(() => { setCorrectionReason(""); setCorrectionCopies("0"); }, [lotActionModal]);
  function printCorrectedLot(update: { date?: string; quantity?: number; uom?: string }): void {
    if (!selectedLot || Number(correctionCopies) === 0) return;
    history.push('/haccp/labels', { lotCorrection: {
      id: crypto.randomUUID(), tenantId: profile?.active_tenant_id,
      businessId: profile?.active_business_id, foodId: selectedLot.food_id,
      lot: selectedLot.lot, copies: Number(correctionCopies), reason: correctionReason.trim(), ...update,
    } });
  }
  const [lotQtyInput, setLotQtyInput] = useState("");
  const [lotDateInput, setLotDateInput] = useState("");
  const [stockForm, setStockForm] = useState({
    food_id: "",
    supplier_id: "",
    storage_unit_id: "",
    qty: "",
    batch_lot: "",
    expiration_date: "",
    supplier_sku: "",
    // T-136: riferimento fattura / N° documento, OPZIONALE. Distinto dal
    // numero di lotto: collega il lotto al documento cartaceo di carico così
    // anche la correzione manuale resta tracciabile lotto→fattura.
    document_number: "",
    // T-284 (Food Cost): costo unitario di carico OPZIONALE (€/UdM). Base
    // storica del food cost. Vuoto = costo non noto (mai 0 falso).
    unit_price: "",
    notify_expiration: true,
    is_frozen: false,
  });
  const [receivingConformityConfirmed, setReceivingConformityConfirmed] =
    useState(false);
  const [lotPhotoB64, setLotPhotoB64] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // T-407: true mentre l'AI legge lotto+scadenza dalla foto dell'etichetta (async,
  // non blocca il resto del form).
  const [labelAnalyzing, setLabelAnalyzing] = useState(false);

  // Modal "Aggiungi lotto" (piano base, warehouse OFF). UX semplificata senza
  // quantità: il piano base traccia solo la presenza del lotto, non la giacenza.
  const [addLotOpen, setAddLotOpen] = useState(false);
  const [foodPicker, setFoodPicker] = useState<null | "stock" | "addLot">(null);
  const [addLotForm, setAddLotForm] = useState({
    food_id: "",
    batch_lot: "",
    expiration_date: "",
    document_number: "",
  });
  // T-136: id del documento collegato OPZIONALMENTE (oltre al riferimento testuale
  // document_number). Stato separato per non toccare i tanti reset dei form.
  const [stockDocId, setStockDocId] = useState("");
  const [addLotDocId, setAddLotDocId] = useState("");
  // Documenti ordinati per data desc, etichetta leggibile per il selettore.
  const docOptions = useMemo(
    () =>
      documents
        .filter((d) => !d.archived_at)
        .slice()
        .sort((a, b) =>
          String(b.document_date ?? "").localeCompare(
            String(a.document_date ?? ""),
          )
        )
        .map((d) => ({
          id: d.id,
          label: `${
            d.document_number || (d.kind === "fattura" ? "Fattura" : "DDT")
          }${d.document_date ? ` · ${d.document_date}` : ""}`,
          number: d.document_number ?? null,
        })),
    [documents],
  );
  // Quando l'utente conferma "Aggiungi lotto" e l'alimento HA già un lotto
  // attivo, gli chiediamo se sostituirlo o tenerlo come residuo.
  const [addLotChoice, setAddLotChoice] = useState<
    { food: Food; lot: string; expiration_date: string | null } | null
  >(null);
  // Action sheet per tap su un lotto in lista (set active / close).
  const [lotMenuFor, setLotMenuFor] = useState<
    {
      food: Food;
      lot: string;
      expiration_date: string | null;
      isActive: boolean;
    } | null
  >(null);
  useBackGuard(addLotOpen, () => setAddLotOpen(false));
  useBackGuard(!!addLotChoice, () => setAddLotChoice(null));
  useBackGuard(!!lotMenuFor, () => setLotMenuFor(null));

  // Back guard per ogni layer: popstate intercetta il back gesture prima che
  // raggiunga il router Ionic. Ordine di priorità implicito: il layer più
  // in primo piano pusha per ultimo → viene consumato per primo.
  useBackGuard(sheetOpen, () => setSheetOpen(false));
  useBackGuard(foodEditorOpen, () => setFoodEditorOpen(false));
  useBackGuard(supplierEditorOpen, () => setSupplierEditorOpen(false));
  useBackGuard(!!modal, () => setModal(null));
  useBackGuard(!!selectedLot, () => setSelectedLot(null));
  useBackGuard(!!lotConfirmDischarge, () => setLotConfirmDischarge(null));
  useBackGuard(!!lotActionModal, () => {
    setLotActionModal(null);
    setLotQtyInput("");
    setLotDateInput("");
    setLotDischargeInput("");
  });
  useBackGuard(!!unitFix, () => setUnitFix(null));
  useBackGuard(!!stockReassignment, () => setStockReassignment(null));
  useBackGuard(!!physicalRecount, () => setPhysicalRecount(null));
  useBackGuard(
    legacyReconciliationOpen,
    () => setLegacyReconciliationOpen(false),
  );
  useBackGuard(
    uomReconciliationOpen,
    () => setUomReconciliationOpen(false),
  );
  useBackGuard(!!legacyResolveConfirm, () => setLegacyResolveConfirm(null));
  useBackGuard(
    !!stockCommandResolveConfirm,
    () => setStockCommandResolveConfirm(null),
  );
  useBackGuard(
    !!legacyPermanentResolveConfirm,
    () => setLegacyPermanentResolveConfirm(null),
  );

  function latestLotStockIn(
    foodId: string,
    lot: string | null,
  ): StockEvent | undefined {
    const targetKey = warehouseLotKey(foodId, lot);
    if (!activeBusinessId) return undefined;
    return findExactThenLegacyStockEvent(
      stockInAll as StockEvent[],
      activeBusinessId,
      allowLegacyUnscopedBusiness,
      (event) =>
        warehouseLotKey(
          event.payload.food_id,
          event.payload.batch_lot ?? null,
        ) === targetKey,
    );
  }

  function currentLotOnHandQty(foodId: string, lot: string | null): number {
    return stockBalanceByLot.get(warehouseLotKey(foodId, lot))?.onHandQty ?? 0;
  }

  function currentLotAvailableQty(foodId: string, lot: string | null): number {
    return stockBalanceByLot.get(warehouseLotKey(foodId, lot))?.availableQty ??
      0;
  }

  function eventQtyInUom(
    event: Pick<StockEvent, "payload">,
    targetUom: string,
  ): number | null {
    const qty = event.payload.stock_qty ?? event.payload.qty;
    const uom = event.payload.stock_uom ?? event.payload.uom ?? targetUom;
    if (!Number.isFinite(qty) || qty < 0) return null;
    return convertQty(qty, uom, targetUom);
  }

  function isUomRebaseEvent(event: Pick<StockEvent, "payload">): boolean {
    return event.payload.movement_class === "uom_rebase" ||
      event.payload.uom_rebase_source === true ||
      event.payload.uom_rebase_snapshot === true;
  }

  async function enqueueInventoryCount(input: {
    foodId: string;
    lot: string | null;
    expirationDate: string | null;
    systemQty: number;
    countedQty: number;
    uom: string;
    reason: string;
  }): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      throw new Error("Nessun locale attivo");
    }
    if (hasQuarantinedStockForFood(input.foodId)) {
      throw new Error(
        "Movimenti non coerenti: verifica la giacenza prima della rettifica",
      );
    }
    const source = latestLotStockIn(input.foodId, input.lot);
    const balance = stockBalanceByLot.get(
      warehouseLotKey(input.foodId, input.lot),
    );
    const command = buildInventoryAdjustmentStockCommandV1({
      commandId: crypto.randomUUID(), tenantId: profile.active_tenant_id,
      businessId: profile.active_business_id, foodId: input.foodId, lot: input.lot,
      expirationDate: input.expirationDate, systemQty: input.systemQty,
      countedQty: input.countedQty, uom: input.uom, countedAt: new Date().toISOString(),
      reason: input.reason, operatorName: profile.full_name?.trim() || null,
      // Una conta positiva non è un nuovo acquisto: il delta eredita il valore
      // medio verificabile del lotto; senza costo resta esplicitamente nullo.
      unitCost: balance?.averageUnitCost ?? null, currency: balance?.valueCurrency ?? "EUR",
      provenance: { supplierId: source?.payload.supplier_id ?? null,
        documentId: source?.payload.document_id ?? null,
        storageUnitId: source?.payload.storage_unit_id ?? null },
    });
    await stockCommand.mutateAsync(command);
  }

  async function enqueueConsumption(input: {
    foodId: string;
    lot: string | null;
    expirationDate: string | null;
    qty: number;
    uom: string;
    reason: string;
    closeLot?: boolean;
  }): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      throw new Error("Nessun locale attivo");
    }
    if (hasQuarantinedStockForFood(input.foodId)) {
      throw new Error(
        "Movimenti non coerenti: verifica la giacenza prima dello scarico",
      );
    }
    const source = latestLotStockIn(input.foodId, input.lot);
    const command = buildConsumeStockCommandV1({
      commandId: crypto.randomUUID(), tenantId: profile.active_tenant_id,
      businessId: profile.active_business_id, foodId: input.foodId, lot: input.lot,
      expirationDate: input.expirationDate, qty: input.qty, uom: input.uom,
      reason: input.reason, closeLot: input.closeLot,
      operatorName: profile.full_name?.trim() || null,
      provenance: { supplierId: source?.payload.supplier_id ?? null,
        documentId: source?.payload.document_id ?? null,
        storageUnitId: source?.payload.storage_unit_id ?? null },
    });
    await stockCommand.mutateAsync(command);
  }

  async function handleLotDischarge(
    kind: "consumed" | "waste",
    qty: number,
    uom: string,
  ): Promise<void> {
    if (!selectedLot) return;
    const balance = stockBalanceByLot.get(
      warehouseLotKey(selectedLot.food_id, selectedLot.lot),
    );
    const liveOnHand = balance?.onHandQty ?? 0;
    const liveAvailable = balance?.availableQty ?? 0;
    if (Math.abs(liveOnHand - liveAvailable) > 1e-6) {
      setToast(
        "Il lotto contiene quantità prenotate: libera prima le spedizioni in bozza",
      );
      setLotConfirmDischarge(null);
      return;
    }
    if (Math.abs(liveOnHand - qty) > 1e-6) {
      setToast("La giacenza è cambiata: riapri il lotto e riprova");
      setLotConfirmDischarge(null);
      return;
    }
    const expires = latestLotStockIn(selectedLot.food_id, selectedLot.lot)
      ?.payload.expiration_date ?? null;
    try {
      await enqueueConsumption({
        foodId: selectedLot.food_id,
        lot: selectedLot.lot,
        expirationDate: expires,
        qty: liveOnHand,
        uom,
        reason: kind,
        closeLot: true,
      });
      setToast(
        kind === "consumed"
          ? "Lotto consumato e chiuso"
          : "Lotto scaricato e chiuso come scarto",
      );
      setLotConfirmDischarge(null);
      setSelectedLot(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // Rettifica della giacenza di un lotto (es. inventario fisico): la nuova
  // quantità può essere INFERIORE o SUPERIORE a quella attuale. Append-only
  // (invariante #4): mai UPDATE del lotto. Il command registra insieme quantità
  // di sistema e quantità contata, poi materializza l'unico evento compensativo.
  async function handleLotQtyAdjust(
    currentQty: number,
    uom: string,
  ): Promise<void> {
    if (!selectedLot || !profile?.active_business_id || !validCorrection(correctionReason, correctionCopies)) return;
    if (
      Math.abs(
        currentLotOnHandQty(selectedLot.food_id, selectedLot.lot) - currentQty,
      ) > 1e-6
    ) {
      setToast("La giacenza è cambiata: riapri la rettifica e riprova");
      setLotActionModal(null);
      return;
    }
    const newQty = Number(lotQtyInput.replace(",", "."));
    if (!Number.isFinite(newQty) || newQty < 0) {
      setToast("Quantità non valida");
      return;
    }
    const delta = newQty - currentQty; // >0 = inventario in eccesso, <0 = in difetto
    if (Math.abs(delta) < 1e-9) {
      setToast("La quantità è invariata");
      return;
    }
    const expires = latestLotStockIn(selectedLot.food_id, selectedLot.lot)
      ?.payload.expiration_date ?? null;
    try {
      await enqueueInventoryCount({
        foodId: selectedLot.food_id,
        lot: selectedLot.lot,
        expirationDate: expires,
        systemQty: currentQty,
        countedQty: newQty,
        uom,
        reason: `Rettifica quantità: ${correctionReason.trim()}`,
      });
      printCorrectedLot({ quantity: newQty, uom: uom });
      setToast(`Quantità rettificata a ${newQty} ${uom}`);
      setLotActionModal(null);
      setLotQtyInput("");
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // Scarico parziale di una quantità prelevata DAL lotto selezionato. Sostituisce
  // il vecchio "Scarico magazzino" dal tasto "+" (che salvava batch_lot=null →
  // orfano: non riduceva nessun lotto numerato e non veniva incluso nella
  // conversione UdM). Qui il lotto è noto, quindi lo stock_out è sempre attribuito.
  async function handleLotPartialDischarge(
    remaining: number,
    uom: string,
  ): Promise<void> {
    if (!selectedLot) return;
    if (
      Math.abs(
        currentLotAvailableQty(selectedLot.food_id, selectedLot.lot) -
          remaining,
      ) > 1e-6
    ) {
      setToast("La giacenza è cambiata: riapri lo scarico e riprova");
      setLotActionModal(null);
      return;
    }
    const out = Number(lotDischargeInput.replace(",", "."));
    if (!Number.isFinite(out) || out <= 0) {
      setToast("Quantità non valida");
      return;
    }
    if (out > remaining + 1e-6) {
      setToast(
        `Non puoi scaricare più della giacenza (${
          remaining.toFixed(2)
        } ${uom})`,
      );
      return;
    }
    const expires = latestLotStockIn(selectedLot.food_id, selectedLot.lot)
      ?.payload.expiration_date ?? null;
    const liveOnHand = currentLotOnHandQty(
      selectedLot.food_id,
      selectedLot.lot,
    );
    const closeLot = Math.abs(out - liveOnHand) <= 1e-6 &&
      Math.abs(remaining - liveOnHand) <= 1e-6;
    try {
      await enqueueConsumption({
        foodId: selectedLot.food_id,
        lot: selectedLot.lot,
        expirationDate: expires,
        qty: Math.min(out, remaining),
        uom,
        reason: lotDischargeReason,
        closeLot,
      });
      setToast(
        closeLot
          ? (lotDischargeReason === "waste"
            ? `Scaricati ${out} ${uom} e lotto chiuso come scarto`
            : `Scaricati ${out} ${uom} e lotto chiuso`)
          : (lotDischargeReason === "waste"
            ? `Scaricati ${out} ${uom} (scarto)`
            : `Scaricati ${out} ${uom}`),
      );
      setLotActionModal(null);
      setLotDischargeInput("");
      setLotDischargeReason("consumed");
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function handleLotDateCorrection(
    currentQty: number,
    uom: string,
  ): Promise<void> {
    if (
      !selectedLot ||
      !lotDateInput || !validCorrection(correctionReason, correctionCopies) ||
      currentQty <= 0 ||
      !profile?.active_tenant_id ||
      !profile.active_business_id
    ) return;
    if (!selectedLot.lot) {
      setToast("La correzione data richiede un lotto identificato");
      return;
    }
    const source = latestLotStockIn(selectedLot.food_id, selectedLot.lot);
    try {
      await stockCommand.mutateAsync({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: profile.active_tenant_id,
        movement_class: "lot_state",
        economic_effect: "stock_only",
        business_id: profile.active_business_id,
        reason: `Correzione scadenza: ${correctionReason.trim()}`,
        lines: [{
          food_id: selectedLot.food_id,
          batch_lot: selectedLot.lot,
          expiration_date: lotDateInput,
          supplier_id: source?.payload.supplier_id ?? null,
          document_id: source?.payload.document_id ?? null,
          storage_unit_id: source?.payload.storage_unit_id ?? null,
          operator_name: profile.full_name?.trim() || null,
          action: "update_metadata",
          uom,
        }],
      });
      printCorrectedLot({ date: lotDateInput });
      setToast("Data scadenza corretta");
      setLotActionModal(null);
      setLotDateInput("");
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  function unitFixBalances(foodId: string) {
    return stockProjection.balances
      .filter((balance) => balance.foodId === foodId)
      .slice()
      .sort((left, right) => left.key.localeCompare(right.key));
  }

  function hasQuarantinedStockForFood(foodId: string): boolean {
    return stockProjection.quarantine.some(
      ({ event }) => event.payload?.food_id === foodId,
    ) || (
      tenantBusinesses.length > 1 &&
      ([
        ...stockInAll,
        ...stockOutAll,
        ...stockReservedAll,
      ] as unknown as StockProjectorEvent[]).some((event) => (
        event.business_id == null &&
        event.payload?.food_id === foodId
      ))
    );
  }

  function uomRebaseQuarantineBlock(
    foodId: string,
    targetUom?: string | null,
  ): string | null {
    return rebaseQuarantineBlock({
      events: stockProjection.quarantine,
      foodId,
      targetUom,
    });
  }


  function tenantWideUnitFixBlock(foodId: string): string | null {
    const tenantId = profile?.active_tenant_id;
    const businessId = profile?.active_business_id;
    if (!tenantId || !businessId) return "Nessun locale attivo";
    if (
      businessesLoading ||
      stockInLoading ||
      stockOutLoading ||
      stockReservedLoading ||
      draftShipmentsLoading
    ) {
      return "Attendi il caricamento completo delle giacenze prima di convertire.";
    }
    if (
      businessesError ||
      stockInError ||
      stockOutError ||
      stockReservedError ||
      draftShipmentsError
    ) {
      return "Impossibile verificare tutte le sedi: riprova dopo la sincronizzazione.";
    }

    const events = [
      ...stockInAll,
      ...stockOutAll,
      ...stockReservedAll,
    ] as unknown as StockProjectorEvent[];
    const tenantBusinessIds = tenantBusinesses.map((business) => business.id);

    // foods.default_uom è tenant-wide. Con più sedi, un vecchio movimento senza
    // business_id non può essere attribuito con certezza: convertirne una sola
    // sede renderebbe la stessa storia incompatibile nelle altre.
    if (
      tenantBusinessIds.length > 1 &&
      events.some((event) => (
        event.business_id == null &&
        event.payload?.food_id === foodId
      ))
    ) {
      return "Impossibile convertire: esistono movimenti storici senza sede assegnata.";
    }

    for (const otherBusinessId of tenantBusinessIds) {
      if (otherBusinessId === businessId) continue;
      const externalReservations = draftShipments.flatMap((shipment) => {
        if (
          shipment.status !== "draft" ||
          shipment.archived_at ||
          shipment.business_id !== otherBusinessId
        ) return [];
        return (shipment.lines ?? [])
          .filter((line) => (
            line.food_id === foodId &&
            shipmentLineReservesStockV1(line, collettivaEnabled)
          ))
          .map((line, lineIndex) => ({
            id: `${shipment.id}:${lineIndex}`,
            tenantId,
            businessId: otherBusinessId,
            foodId,
            batchLot: line.batch_lot?.trim() || null,
            storageUnitId: line.storage_unit_id?.trim() || null,
            qty: Number(line.qty),
            uom: line.uom?.trim() || canonicalUomByFood[foodId] || "",
          }));
      });
      const projection = projectStock(events, {
        scope: { tenantId, businessId: otherBusinessId },
        canonicalUomByFood,
        includeLegacyUnscopedBusiness: false,
        externalReservations,
      });
      if (
        projection.quarantine.some(
          ({ event }) => event.payload?.food_id === foodId,
        )
      ) {
        return "Impossibile convertire: un’altra sede contiene movimenti non coerenti per questo prodotto.";
      }
      if (
        projection.balances.some((balance) => (
          balance.foodId === foodId &&
          (
            Math.abs(balance.onHandQty) > 1e-9 ||
            balance.reservedQty > 1e-9
          )
        ))
      ) {
        return "Impossibile convertire: il prodotto ha giacenze o prenotazioni in un’altra sede.";
      }
    }
    return null;
  }

  function openUnitFix(foodId: string, suggestedUom?: string | null): void {
    const food = foods.find((candidate) => candidate.id === foodId);
    if (!food) return;

    const tenantScopeBlock = tenantWideUnitFixBlock(foodId);
    if (tenantScopeBlock) {
      setToast(tenantScopeBlock);
      return;
    }
    const quarantineBlock = uomRebaseQuarantineBlock(foodId);
    if (quarantineBlock) {
      setToast(quarantineBlock);
      return;
    }

    const balances = unitFixBalances(foodId);
    if (balances.some((balance) => balance.onHandQty < -1e-9)) {
      setToast(
        "Impossibile convertire: è presente una giacenza negativa da correggere.",
      );
      return;
    }
    if (
      balances.some((balance) => balance.closed && balance.onHandQty > 1e-9)
    ) {
      setToast(
        "Impossibile convertire: un lotto chiuso conserva ancora giacenza fisica.",
      );
      return;
    }
    if (balances.some((balance) => balance.reservedQty > 1e-9)) {
      setToast(
        "Impossibile convertire mentre esistono quantità prenotate in una spedizione bozza.",
      );
      return;
    }

    const oldUom = food.default_uom?.trim() || "pz";
    const openBalances = balances.filter(
      (balance) => !balance.closed && balance.onHandQty > 1e-9,
    );
    if (
      openBalances.some(
        (balance) => balance.uom.trim().toLowerCase() !== oldUom.toLowerCase(),
      )
    ) {
      setToast(
        "Impossibile convertire: la giacenza non è allineata alla UdM corrente del prodotto.",
      );
      return;
    }

    const rows = openBalances.map<UnitFixRow>((balance) => {
      const source = latestLotStockIn(foodId, balance.batchLot);
      return {
        key: balance.key,
        lot: balance.batchLot,
        expires: balance.expirationDate,
        oldQty: balance.onHandQty,
        newQty: String(balance.onHandQty),
        onHandValue: balance.onHandValue,
        averageUnitCost: balance.averageUnitCost,
        currency: balance.valueCurrency,
        reservedQty: balance.reservedQty,
        eventCount: balance.eventCount,
        lastEventId: balance.lastEventId,
        supplierId: source?.payload.supplier_id ?? null,
        documentId: source?.payload.document_id ?? null,
        storageUnitId: source?.payload.storage_unit_id ?? null,
      };
    });

    setSelectedLot(null);
    const newUom = suggestedUom?.trim().toLowerCase() || oldUom;
    setUnitFix({
      foodId,
      foodName: food.name,
      foodVersion: food.version ?? 0,
      oldUom,
      newUom,
      rows: rows.map((row) => {
        const converted = convertQty(row.oldQty, oldUom, newUom);
        return {
          ...row,
          newQty: converted == null ? "" : String(converted),
        };
      }),
    });
  }

  function changeUnitFixUom(newUom: string): void {
    setUnitFix((current) => {
      if (!current) return current;
      const cleanUom = newUom.trim().toLowerCase();
      return {
        ...current,
        newUom: cleanUom,
        rows: current.rows.map((row) => {
          const converted = convertQty(row.oldQty, current.oldUom, cleanUom);
          return {
            ...row,
            newQty: converted == null ? "" : String(converted),
          };
        }),
      };
    });
  }

  async function submitUnitFix(): Promise<void> {
    if (
      !unitFix ||
      !profile?.active_tenant_id ||
      !profile.active_business_id
    ) return;

    const oldUom = unitFix.oldUom.trim().toLowerCase();
    const newUom = unitFix.newUom.trim().toLowerCase();
    if (!newUom) {
      setToast("Scegli la nuova unità");
      return;
    }
    if (newUom.length > 10) {
      setToast("La nuova unità è troppo lunga");
      return;
    }
    if (oldUom.toLowerCase() === newUom.toLowerCase()) {
      setToast("La nuova unità coincide con quella attuale");
      return;
    }
    const oldNormalized = oldUom.toLowerCase();
    const newNormalized = newUom.toLowerCase();
    const oldIsMass = oldNormalized === "kg" || oldNormalized === "g";
    const newIsMass = newNormalized === "kg" || newNormalized === "g";
    const oldIsVolume = oldNormalized === "l" || oldNormalized === "ml";
    const newIsVolume = newNormalized === "l" || newNormalized === "ml";
    if ((oldIsMass && newIsVolume) || (oldIsVolume && newIsMass)) {
      setToast(
        `Conversione non ammessa: ${oldUom} e ${newUom} misurano grandezze diverse.`,
      );
      return;
    }

    // T-484: un carico storico già nella nuova UdM (es. 72 pz con anagrafica
    // ancora in litri) è quarantinato solo fino al cutover. Lo riabilitiamo
    // quando il rebase chiude la coordinata vecchia; scarichi/prenotazioni o
    // unità diverse rimangono bloccati dalla guardia fail-closed.
    const quarantineBlock = uomRebaseQuarantineBlock(unitFix.foodId, newUom);
    if (quarantineBlock) {
      setToast(quarantineBlock);
      return;
    }

    const parsedRows = unitFix.rows.map((row) => ({
      ...row,
      targetQty: Number(row.newQty.replace(",", ".")),
    }));
    if (
      parsedRows.some(
        (row) => !Number.isFinite(row.targetQty) || row.targetQty <= 0,
      )
    ) {
      setToast("Inserisci una quantità valida (> 0) per ogni lotto");
      return;
    }

    const automaticFactor = convertQty(1, oldUom, newUom);
    if (
      automaticFactor != null &&
      parsedRows.some((row) => (
        Math.abs(row.targetQty - row.oldQty * automaticFactor) >
          1e-9 * Math.max(1, row.targetQty)
      ))
    ) {
      setToast("La conversione metrica non può essere modificata manualmente.");
      return;
    }

    const currentFood = foods.find((food) => food.id === unitFix.foodId);
    if (
      !currentFood ||
      (currentFood.version ?? 0) !== unitFix.foodVersion ||
      currentFood.default_uom.trim().toLowerCase() !== oldUom.toLowerCase()
    ) {
      setToast("Il prodotto è cambiato: riapri “Correggi unità” e riprova.");
      setUnitFix(null);
      return;
    }

    const tenantScopeBlock = tenantWideUnitFixBlock(unitFix.foodId);
    if (tenantScopeBlock) {
      setToast(tenantScopeBlock);
      setUnitFix(null);
      return;
    }

    const liveBalances = unitFixBalances(unitFix.foodId)
      .filter((balance) => !balance.closed && balance.onHandQty > 1e-9);
    const liveByKey = new Map(
      liveBalances.map((balance) => [balance.key, balance]),
    );
    const hasBalanceDrift = liveBalances.length !== parsedRows.length ||
      parsedRows.some((row) => {
        const live = liveByKey.get(row.key);
        return !live ||
          live.closed ||
          live.reservedQty > 1e-9 ||
          Math.abs(live.onHandQty - row.oldQty) > 1e-9 ||
          live.eventCount !== row.eventCount ||
          live.lastEventId !== row.lastEventId ||
          live.uom.trim().toLowerCase() !== oldUom.toLowerCase();
      });
    if (hasBalanceDrift) {
      setToast("La giacenza è cambiata: riapri “Correggi unità” e riprova.");
      setUnitFix(null);
      return;
    }

    const commandLines = parsedRows.length > 0
      ? parsedRows.map((row, index) => ({
        food_id: unitFix.foodId,
        batch_lot: row.lot,
        expiration_date: row.expires,
        supplier_id: row.supplierId,
        document_id: row.documentId,
        storage_unit_id: row.storageUnitId,
        operator_name: profile.full_name?.trim() || null,
        line_ref: `uom-rebase:${index + 1}`,
        lot_action: "recorded_only" as const,
        source_qty: row.oldQty,
        source_uom: oldUom,
        // Deriva dal valore totale, non dal costo medio arrotondato per la UI.
        source_unit_price: uomRebaseUnitPrice(row.onHandValue, row.oldQty),
        source_line_total: row.onHandValue,
        currency: row.currency ?? "EUR",
        stock_uom: newUom,
        conversion: {
          factor: automaticFactor ?? (row.targetQty / row.oldQty),
          source: automaticFactor == null
            ? "user_confirmed" as const
            : "metric" as const,
          reference_id: automaticFactor == null
            ? `uom-rebase:${unitFix.foodId}`
            : null,
        },
      }))
      : [{
        food_id: unitFix.foodId,
        batch_lot: null,
        expiration_date: null,
        supplier_id: null,
        document_id: null,
        storage_unit_id: null,
        operator_name: profile.full_name?.trim() || null,
        line_ref: "uom-cutover:zero",
        source_qty: 0,
        source_uom: oldUom,
        source_unit_price: null,
        source_line_total: null,
        currency: "EUR",
        stock_uom: newUom,
        conversion: {
          factor: automaticFactor ?? 1,
          source: automaticFactor == null
            ? "user_confirmed" as const
            : "metric" as const,
          reference_id: automaticFactor == null
            ? `uom-rebase:${unitFix.foodId}`
            : null,
        },
        zero_balance: true as const,
      }];
    const command: StockCommandV1 = {
      version: 1,
      command_id: crypto.randomUUID(),
      tenant_id: profile.active_tenant_id,
      movement_class: "uom_rebase",
      economic_effect: "stock_only",
      business_id: profile.active_business_id,
      reason: `uom_rebase:${oldUom}->${newUom}`,
      lines: commandLines,
    };

    setUnitFixBusy(true);
    try {
      const handle = await stockCommand.mutateAsync({
        command,
        master_effects: [{
          table: "foods",
          id: unitFix.foodId,
          op: "update",
          base_version: unitFix.foodVersion,
          patch: {
            id: unitFix.foodId,
            tenant_id: profile.active_tenant_id,
            default_uom: newUom,
          },
        }],
      });
      const confirmation = await getSyncEngine().confirmStockCommandPushed(
        handle,
      );
      if (!confirmation.confirmed) {
        if (confirmation.status === "permanently_rejected") {
          setToast(
            `Riallineamento non registrato: ${
              confirmation.reason ===
                  "stock_command_v1_uom_rebase_incompatible_history"
                ? "esistono ancora movimenti in una UdM diversa da quella scelta."
                : confirmation.reason ??
                  "il server ha rifiutato la correzione."
            }`,
          );
          return;
        }
        setToast(
          "Riallineamento salvato sul dispositivo ma non ancora confermato dal server. Resta in coda e verrà ritentato con la connessione attiva.",
        );
        setUnitFix(null);
        return;
      }
      // Il writeback del comando ha già aggiornato SQLite. Invalida però
      // esplicitamente le query React: senza questo una scheda import aperta
      // nella stessa sessione continuava a vedere la vecchia UdM fino al
      // reload e bloccava erroneamente il commit.
      await queryClient.invalidateQueries({ queryKey: ["masters"] });
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      setToast(
        parsedRows.length > 0
          ? `Unità corretta: ${oldUom} → ${newUom}. Valore e giacenza sono stati riproporzionati.`
          : `Unità corretta: ${oldUom} → ${newUom}. I prossimi movimenti useranno la nuova unità.`,
      );
      setUnitFix(null);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setUnitFixBusy(false);
    }
  }

  function openStockReassignment(
    foodId: string,
    allowEmpty = false,
    supplierMatchCount: number | null = 0,
    targetFoodId = "",
  ): boolean {
    const food = foods.find((candidate) => candidate.id === foodId);
    if (!food) return false;
    if (hasQuarantinedStockForFood(foodId)) {
      setToast(
        "Prima esegui il riconteggio fisico: la giacenza di questo prodotto non è verificabile.",
      );
      return false;
    }
    const balances = unitFixBalances(foodId).filter(
      (balance) => balance.onHandQty > 1e-9,
    );
    if (balances.length === 0 && !allowEmpty) {
      setToast("Questo prodotto non ha giacenza da spostare.");
      return false;
    }
    if (
      balances.some(
        (balance) =>
          balance.reservedQty > 1e-9 ||
          balance.onHandQty < -1e-9 ||
          balance.uom.trim().toLowerCase() !==
            food.default_uom.trim().toLowerCase(),
      )
    ) {
      setToast(
        "La giacenza contiene prenotazioni o coordinate non coerenti e non può essere riassegnata.",
      );
      return false;
    }
    const rows = balances.map<StockReconciliationSnapshotRowV1>((balance) => {
      const source = latestLotStockIn(foodId, balance.batchLot);
      return {
        batch_lot: balance.batchLot,
        expiration_date: balance.expirationDate,
        supplier_id: source?.payload.supplier_id ?? null,
        document_id: source?.payload.document_id ?? null,
        storage_unit_id: balance.storageUnitId ??
          source?.payload.storage_unit_id ?? null,
        qty: balance.onHandQty,
        uom: food.default_uom,
        value: balance.onHandValue,
        currency: balance.onHandValue == null
          ? null
          : balance.valueCurrency ?? "EUR",
      };
    });
    setSelectedLot(null);
    setStockReassignmentQuery("");
    setStockReassignment({
      sourceFoodId: food.id,
      sourceFoodName: food.name,
      sourceFoodVersion: food.version ?? 0,
      sourceUom: food.default_uom,
      targetFoodId,
      sourceRecipeCount: recipesPerFood.get(food.id) ?? 0,
      rows,
      archiveSource: true,
      supplierMatchCount,
    });
    return true;
  }

  async function submitStockReassignment(): Promise<void> {
    if (
      !stockReassignment ||
      !profile?.active_tenant_id ||
      !profile.active_business_id
    ) return;
    const source = foods.find(
      (food) => food.id === stockReassignment.sourceFoodId,
    );
    const target = foods.find(
      (food) => food.id === stockReassignment.targetFoodId,
    );
    if (!source || !target || target.archived_at) {
      setToast("Scegli il prodotto corretto di destinazione.");
      return;
    }
    if (
      target.default_uom.trim().toLowerCase() !==
        stockReassignment.sourceUom.trim().toLowerCase()
    ) {
      setToast(
        "La destinazione deve usare la stessa unità. Correggi prima l’unità del prodotto corretto.",
      );
      return;
    }
    if (
      (source.version ?? 0) !== stockReassignment.sourceFoodVersion ||
      hasQuarantinedStockForFood(source.id)
    ) {
      setToast(
        "Il prodotto o la giacenza sono cambiati: riapri la procedura e riprova.",
      );
      setStockReassignment(null);
      return;
    }

    try {
      await stockReconciliation.mutateAsync({
        mode: "product_reassignment",
        requestId: crypto.randomUUID(),
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        sourceFoodId: source.id,
        targetFoodId: target.id,
        sourceFoodVersion: source.version ?? 0,
        targetFoodVersion: target.version ?? 0,
        sourceUom: source.default_uom,
        targetUom: target.default_uom,
        requestedAt: new Date().toISOString(),
        operatorName: profile.full_name?.trim() || "Operatore",
        archiveSource: true,
        sourceRecipeCount: stockReassignment.sourceRecipeCount,
        rows: stockReassignment.rows,
      });
      setStockReassignment(null);
      setToast(
        `Fusione registrata: giacenza, match fornitore e ricette passeranno da “${source.name}” a “${target.name}” in un’unica operazione verificabile.`,
      );
    } catch (err) {
      setToast(toUserMessage(err, "Riassegnazione non riuscita"));
    }
  }

  function openPhysicalRecount(
    foodId: string,
    confirmationMode: "verified_projection" | "manual" = "manual",
  ): void {
    const food = foods.find((candidate) => candidate.id === foodId);
    if (!food) return;
    const tenantScopeBlock = tenantWideUnitFixBlock(foodId);
    if (tenantScopeBlock) {
      setToast(tenantScopeBlock);
      return;
    }
    if (
      unitFixBalances(foodId).some(
        (balance) => balance.reservedQty > 1e-9,
      )
    ) {
      setToast(
        "Impossibile riconteggiare mentre esistono quantità prenotate. Completa o annulla prima la prenotazione.",
      );
      return;
    }
    const balances = unitFixBalances(foodId);
    if (
      confirmationMode === "verified_projection" &&
      balances.some((balance) => balance.onHandQty < -1e-9)
    ) {
      setToast(
        "Il saldo verificabile contiene una quantità negativa: serve un riconteggio fisico.",
      );
      return;
    }
    const byKey = new Map<string, PhysicalRecountRow>();
    const addRow = (
      input: Omit<PhysicalRecountRow, "key" | "qty" | "value" | "currency">,
      verified?: {
        qty: number;
        value: number | null;
        currency: string | null;
      },
    ): void => {
      const key = warehouseLotKey(foodId, input.lot);
      if (byKey.has(key)) return;
      byKey.set(key, {
        ...input,
        key,
        qty: confirmationMode === "verified_projection"
          ? String(Math.max(0, verified?.qty ?? 0))
          : "",
        value: confirmationMode === "verified_projection" &&
            (verified?.qty ?? 0) > 1e-9
          ? verified?.value ?? null
          : null,
        currency: confirmationMode === "verified_projection" &&
            (verified?.qty ?? 0) > 1e-9 &&
            verified?.value != null
          ? verified.currency
          : null,
      });
    };
    for (const balance of balances) {
      const source = latestLotStockIn(foodId, balance.batchLot);
      addRow({
        lot: balance.batchLot,
        expirationDate: balance.expirationDate,
        supplierId: source?.payload.supplier_id ?? null,
        documentId: source?.payload.document_id ?? null,
        storageUnitId: balance.storageUnitId ??
          source?.payload.storage_unit_id ?? null,
      }, {
        qty: balance.onHandQty,
        value: balance.onHandValue,
        currency: balance.valueCurrency,
      });
    }
    for (const { event } of stockProjection.quarantine) {
      if (event.payload?.food_id !== foodId) continue;
      const lot = typeof event.payload.batch_lot === "string"
        ? event.payload.batch_lot.trim() || null
        : null;
      const source = latestLotStockIn(foodId, lot);
      addRow({
        lot,
        expirationDate:
          typeof event.payload.expiration_date === "string"
            ? event.payload.expiration_date
            : null,
        supplierId: source?.payload.supplier_id ?? null,
        documentId: source?.payload.document_id ?? null,
        storageUnitId: source?.payload.storage_unit_id ?? null,
      });
    }
    if (byKey.size === 0) {
      addRow({
        lot: null,
        expirationDate: null,
        supplierId: null,
        documentId: null,
        storageUnitId: null,
      });
    }
    setSelectedLot(null);
    setPhysicalRecount({
      foodId: food.id,
      foodName: food.name,
      foodVersion: food.version ?? 0,
      archived: !!food.archived_at,
      sourceUom: food.default_uom,
      targetUom: food.default_uom,
      sourceRecipeCount: recipesPerFood.get(food.id) ?? 0,
      confirmationMode,
      rows: [...byKey.values()],
    });
  }

  async function submitPhysicalRecount(): Promise<void> {
    if (
      !physicalRecount ||
      !profile?.active_tenant_id ||
      !profile.active_business_id
    ) return;
    const food = foods.find((candidate) => candidate.id === physicalRecount.foodId);
    if (
      !food ||
      (food.version ?? 0) !== physicalRecount.foodVersion ||
      food.default_uom.trim().toLowerCase() !==
        physicalRecount.sourceUom.trim().toLowerCase()
    ) {
      setToast("Il prodotto è cambiato: riapri il riconteggio e riprova.");
      setPhysicalRecount(null);
      return;
    }
    const targetUom = physicalRecount.targetUom.trim().toLowerCase();
    const rows = physicalRecount.rows.map<StockReconciliationSnapshotRowV1>(
      (row) => ({
        batch_lot: row.lot,
        expiration_date: row.expirationDate,
        supplier_id: row.supplierId,
        document_id: row.documentId,
        storage_unit_id: row.storageUnitId,
        qty: Number(row.qty.replace(",", ".")),
        uom: targetUom,
        // Conserva il valore soltanto quando deriva dal saldo già proiettato e
        // l'operatore non ha modificato la quantità proposta.
        value: row.value,
        currency: row.value == null ? null : row.currency,
      }),
    );
    if (
      !targetUom ||
      rows.some((row) => !Number.isFinite(row.qty) || row.qty < 0)
    ) {
      setToast(
        "Inserisci la quantità fisica di ogni lotto (usa 0 se non è più presente).",
      );
      return;
    }
    if (
      physicalRecount.archived &&
      (
        targetUom !== physicalRecount.sourceUom.trim().toLowerCase() ||
        rows.some((row) => row.qty > 1e-9)
      )
    ) {
      setToast(
        "Un prodotto archiviato può essere riconciliato soltanto confermando quantità zero nella sua unità attuale.",
      );
      return;
    }

    try {
      await stockReconciliation.mutateAsync({
        mode: "physical_recount",
        requestId: crypto.randomUUID(),
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        sourceFoodId: food.id,
        targetFoodId: food.id,
        sourceFoodVersion: food.version ?? 0,
        targetFoodVersion: food.version ?? 0,
        sourceUom: physicalRecount.sourceUom,
        targetUom,
        requestedAt: new Date().toISOString(),
        operatorName: profile.full_name?.trim() || "Operatore",
        archiveSource: false,
        sourceRecipeCount: physicalRecount.sourceRecipeCount,
        rows,
      });
      setPhysicalRecount(null);
      setToast(
        `Riconteggio di “${food.name}” registrato. Lo storico resta consultabile; la nuova giacenza partirà dalla fotografia confermata.`,
      );
    } catch (err) {
      setToast(toUserMessage(err, "Riconteggio non riuscito"));
    }
  }

  // Apre il modal FoodEditor: con `f` → modalità edit, senza → modalità nuovo.
  function openFoodEditor(f?: Food): void {
    if (!f && !canCreateFoods) {
      history.push('/documents/capture');
      return;
    }
    setFoodToEdit(f);
    setFoodEditorOpen(true);
  }

  // Counter ricette in cui ciascun food è ingrediente (cross-reference badge B10).
  const recipesPerFood = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of recipes) {
      if (r.archived_at) continue;
      // T-441: non contare le varianti dieta (ricette figlie) — stessa base.
      if (r.variant_of_recipe_id != null) continue;
      const seen = new Set<string>();
      for (const ing of r.ingredients ?? []) {
        if (ing?.food_id && !seen.has(ing.food_id)) {
          seen.add(ing.food_id);
          m.set(ing.food_id, (m.get(ing.food_id) ?? 0) + 1);
        }
      }
    }
    return m;
  }, [recipes]);

  function openSupplierEditor(s?: Supplier): void {
    setSupplierToEdit(s);
    setSupplierEditorOpen(true);
  }

  async function archiveItem(
    table: "foods" | "suppliers",
    id: string,
    baseVersion: number,
  ): Promise<void> {
    if (table === "foods" && stockRelevantFoodIds.has(id)) {
      setToast(
        "Non puoi archiviare il prodotto: scarica prima il saldo e libera le quantità prenotate.",
      );
      return;
    }
    try {
      await createMaster.mutateAsync({
        table,
        id,
        op: "archive",
        base_version: baseVersion,
        patch: { id },
      });
      setToast(
        table === "foods"
          ? "Archiviazione richiesta: sarà confermata dopo la verifica del saldo."
          : "Fornitore archiviato",
      );
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  async function requestFoodArchive(food: Pick<Food, "id" | "name" | "version">): Promise<void> {
    let supplierMatchCount: number | null = null;
    const tenantId = profile?.active_tenant_id;
    if (tenantId) {
      const { count, error } = await getSupabase()
        .from("supplier_item_aliases")
        .select("raw_description_norm", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("food_id", food.id);
      if (!error) supplierMatchCount = count ?? 0;
    }
    setConfirmArchive({
      table: "foods",
      id: food.id,
      name: food.name,
      version: food.version ?? 0,
      supplierMatchCount,
    });
  }

  const filteredSuppliers = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    const visible = suppliers.filter((s) => !s.archived_at);
    if (!q) return visible;
    return visible.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.piva ?? "").toLowerCase().includes(q) ||
        (s.category ?? "").toLowerCase().includes(q),
    );
  }, [suppliers, supplierSearch]);

  // T-629: unica selezione per Prodotti e Giacenze; nessuno stato per-tab.
  const [warehouseStorageFilter, setWarehouseStorageFilter] = useState<
    "all" | "fresco" | "surgelato" | "secco" | "bevanda"
  >("all");
  const [warehouseStatusFilter, setWarehouseStatusFilter] = useState<
    "all" | "open" | "thawed" | "expiring"
  >("all");
  const [warehouseCategoryFilter, setWarehouseCategoryFilter] = useState<string>("all");
  const [warehouseSort, setWarehouseSort] = useState<"name" | "category" | "expiry">(
    "name",
  );
  const [warehouseFiltersOpen, setWarehouseFiltersOpen] = useState(false);
  // T-132: sezione collassabile "Alimenti mai usati né caricati" (pulizia orfani).
  const [orphansOpen, setOrphansOpen] = useState(false);

  // Conteggi per chip storage type (basati sui food NON filtrati per storage,
  // così i numeri non vanno a zero quando l'utente filtra)
  const foodStorageCounts = useMemo(() => {
    const visible = foods.filter((f) => !f.archived_at);
    const all = visible.length;
    const fresco = visible.filter((f) => f.storage_type === "fresco").length;
    const surgelato = visible.filter((f) =>
      f.storage_type === "surgelato"
    ).length;
    const secco = visible.filter((f) => f.storage_type === "secco").length;
    const bevanda = visible.filter((f) => f.storage_type === "bevanda").length;
    return { all, fresco, surgelato, secco, bevanda };
  }, [foods]);

  // T-132: alimenti "orfani" = mai usati in una ricetta E mai caricati (nessun
  // stock_in) E senza un lotto attivo reale. Il placeholder "-" su
  // current_batch_lot NON è un orfano: è la convenzione del ristoratore per
  // stampare un'etichetta quando il lotto reale non è noto (vedi memoria).
  const orphanFoods = useMemo(() => {
    // Set dei food_id che hanno almeno un carico (stock_in), indipendentemente
    // dalla giacenza residua: se è mai entrato, non è "mai caricato".
    const everStockedIn = new Set<string>();
    for (const e of stockIn as StockEvent[]) {
      everStockedIn.add(e.payload.food_id);
    }
    return foods
      .filter((f) => !f.archived_at)
      .filter((f) => (recipesPerFood.get(f.id) ?? 0) === 0)
      .filter((f) => !everStockedIn.has(f.id))
      // Il placeholder "-" è una scelta esplicita del ristoratore (etichetta
      // senza lotto reale noto): NON è un orfano, va escluso.
      .filter((f) => (f.current_batch_lot ?? "").trim() !== "-")
      .sort((a, b) => a.name.localeCompare(b.name, "it"));
  }, [foods, recipesPerFood, stockIn]);

  async function captureLotPhoto(source: CaptureSource): Promise<void> {
    try {
      const photo = await getCaptureProvider().takePhoto(source);
      if (!photo) return;
      // La foto è SEMPRE salvata subito (prova verificabile del lotto), poi
      // l'analisi parte in background senza bloccare il form.
      setLotPhotoB64(photo.base64);
      void extractAndFillLotExpiry(photo.base64);
    } catch (err) {
      setToast(toUserMessage(err, "Cattura annullata"));
    }
  }

  // T-407: legge lotto+scadenza dalla foto dell'etichetta via edge
  // `ai-vision-proxy` (kind='lot_expiry', no-thinking → veloce) e auto-compila i
  // due campi del form. Async e non-bloccante: l'operatore può continuare a
  // compilare mentre l'AI lavora "con calma". La foto resta comunque allegata
  // al carico anche se l'estrazione fallisce.
  async function extractAndFillLotExpiry(base64: string): Promise<void> {
    setLabelAnalyzing(true);
    try {
      const { data: sess } = await getSupabase().auth.getSession();
      const tok = sess.session?.access_token;
      if (!tok) throw new Error("Sessione scaduta. Esci e rientra.");
      const res = await fetch(
        `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/ai-vision-proxy`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tok}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            kind: "lot_expiry",
            image_b64: base64,
            business_id: profile?.active_business_id ?? undefined,
          }),
        },
      );
      const json = (await res.json()) as Record<string, unknown>;
      if (!res.ok) {
        const code = (json.error as string) ?? `HTTP ${res.status}`;
        if (code === "quota_exceeded") {
          throw new Error("Quota OCR esaurita. Riprova più tardi.");
        }
        throw new Error(`Analisi etichetta non riuscita (codice: ${code}).`);
      }
      const result = (json.result ?? json) as {
        batch_lot?: string | null;
        expiration_date?: string | null;
      };
      const lot = result.batch_lot ?? null;
      const exp = result.expiration_date ?? null;
      if (lot || exp) {
        setStockForm((f) => ({
          ...f,
          ...(lot ? { batch_lot: lot } : {}),
          ...(exp ? { expiration_date: exp } : {}),
        }));
        const parts: string[] = [];
        if (lot) parts.push(`lotto ${lot}`);
        if (exp) {
          parts.push(`scad. ${new Date(exp).toLocaleDateString("it-IT")}`);
        }
        setToast(`📷 Letto dall'etichetta: ${parts.join(" · ")}`);
      } else {
        setToast(
          "Etichetta: lotto/scadenza non riconosciuti. Inseriscili a mano (la foto resta allegata).",
        );
      }
    } catch (err) {
      setToast(toUserMessage(err, "Analisi etichetta non riuscita"));
    } finally {
      setLabelAnalyzing(false);
    }
  }

  async function uploadLotPhoto(tenantId: string): Promise<string | null> {
    if (!lotPhotoB64) return null;
    setUploadingPhoto(true);
    try {
      const bytes = Uint8Array.from(atob(lotPhotoB64), (c) => c.charCodeAt(0));
      const path = `${tenantId}/${crypto.randomUUID()}.jpg`;
      const { error } = await getSupabase().storage
        .from("lot-photos")
        .upload(path, bytes, { contentType: "image/jpeg", upsert: false });
      if (error) throw error;
      return path;
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function saveStockMovement(
    kind: "stock_in" | "stock_out",
  ): Promise<void> {
    if (!profile?.active_business_id || !profile?.active_tenant_id) {
      setToast("Nessun locale attivo");
      return;
    }
    const qty = Number(stockForm.qty.replace(",", "."));
    const food = foods.find((item) => item.id === stockForm.food_id);
    if (!food || !Number.isFinite(qty) || qty <= 0) {
      setToast("Dati non validi");
      return;
    }
    if (kind === "stock_in" && !receivingConformityConfirmed) {
      setToast(
        "Conferma il controllo di conformità prima di registrare il carico",
      );
      return;
    }
    // T-284 (Food Cost): costo unitario di carico opzionale. Vuoto → non scritto
    // (mai 0 falso); valore >= 0 valido (0 = omaggio).
    const rawStockCost = Number(stockForm.unit_price.replace(",", "."));
    const stockCost = roundMoney(rawStockCost);
    const stockCostValid = stockForm.unit_price.trim() !== "" &&
      Number.isFinite(rawStockCost) && rawStockCost >= 0;
    try {
      let photoPath: string | null = null;
      let photoUploadFailed = false;
      if (kind === "stock_in" && lotPhotoB64) {
        try {
          photoPath = await uploadLotPhoto(profile.active_tenant_id);
        } catch {
          // Lo stock command è offline-first, mentre Storage non lo è ancora:
          // un errore foto non deve impedire il carico fisico (né indurre
          // l'utente a ritentare creando un doppio carico).
          photoUploadFailed = true;
          photoPath = null;
        }
      }
      const batchLot = stockForm.batch_lot.trim() ||
        (kind === "stock_out" ? (activeLotFor(food) ?? "") : "") ||
        null;
      if (kind === "stock_in") {
        const command: StockCommandV1 = {
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: "receive",
          economic_effect: "purchase",
          business_id: profile.active_business_id,
          reason: "manual_receive",
          lines: [{
            food_id: food.id,
            operator_name: profile.full_name?.trim() || null,
            supplier_id: stockForm.supplier_id || null,
            storage_unit_id: stockForm.storage_unit_id || null,
            batch_lot: batchLot,
            expiration_date: stockForm.expiration_date || null,
            supplier_sku: stockForm.supplier_sku.trim() || null,
            document_number: stockForm.document_number.trim() || null,
            document_date: documents.find((doc) =>
              doc.id === stockDocId
            )?.document_date ?? null,
            is_frozen: stockForm.is_frozen,
            notify_expiration: stockForm.notify_expiration,
            photo_path: photoPath,
            document_id: stockDocId || null,
            manual_entry: true,
            receiving_conformity_confirmed: true,
            lot_action: "updated_active",
            purchase_cost_source: "manual",
            source_qty: qty,
            source_uom: food.default_uom || "pz",
            source_unit_price: stockCostValid ? stockCost : null,
            // Conserva anche il valore totale della coordinata sorgente: il
            // proiettore può così riproporzionare il costo quando la UdM stock
            // cambia senza dover ricostruire il totale da un alias legacy.
            source_line_total: stockCostValid ? stockCost * qty : null,
            currency: "EUR",
            stock_uom: food.default_uom || "pz",
            conversion: { factor: 1, source: "identity" },
          }],
        };
        await stockCommand.mutateAsync(command);
      } else {
        if (!batchLot) {
          throw new Error(
            "Seleziona un lotto dalla giacenza prima di registrare lo scarico",
          );
        }
        const availableQty = currentLotAvailableQty(food.id, batchLot);
        if (qty > availableQty + 1e-6) {
          throw new Error(
            `Non puoi scaricare più della giacenza disponibile (${
              availableQty.toFixed(2)
            } ${food.default_uom})`,
          );
        }
        const onHandQty = currentLotOnHandQty(food.id, batchLot);
        const source = latestLotStockIn(food.id, batchLot);
        await enqueueConsumption({
          foodId: food.id,
          lot: batchLot,
          expirationDate: source?.payload.expiration_date ??
            food.current_expiration_date ?? null,
          qty: Math.min(qty, availableQty),
          uom: food.default_uom || "pz",
          reason: "manual_stock_out",
          closeLot: Math.abs(qty - onHandQty) <= 1e-6 &&
            Math.abs(availableQty - onHandQty) <= 1e-6,
        });
      }
      // Schedula notifica solo se l'utente l'ha richiesta esplicitamente.
      if (
        kind === "stock_in" && stockForm.expiration_date &&
        stockForm.notify_expiration
      ) {
        void scheduleExpirationNotifications([{
          id: `${stockForm.food_id}|${stockForm.batch_lot ?? ""}`,
          product: food.name,
          date: stockForm.expiration_date,
        }], expirationPreferences, profile.active_business_id);
      }
      setToast(
        kind === "stock_in"
          ? photoUploadFailed
            ? "Carico registrato senza foto: il caricamento dell’allegato non è riuscito."
            : "Carico registrato"
          : "Scarico registrato",
      );
      setStockForm({
        food_id: "",
        supplier_id: "",
        storage_unit_id: "",
        qty: "",
        batch_lot: "",
        expiration_date: "",
        supplier_sku: "",
        document_number: "",
        unit_price: "",
        notify_expiration: true,
        is_frozen: false,
      });
      setStockDocId("");
      setReceivingConformityConfirmed(false);
      setLotPhotoB64(null);
      setModal(null);
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  // Aggregate per-food stock from the canonical additive projector. Legacy
  // qty/uom coordinates are converted only when compatible; malformed events
  // remain quarantined instead of silently corrupting the displayed balance.
  const stockInCount = stockIn.length;
  const stockOutCount = stockOut.length;
  const foodsWithStockCount = stockByFood.size;
  // Log diagnostico: cosa sta per essere renderizzato? Permette di capire se
  // un "magazzino vuoto" lamentato dall'utente è davvero un dato mancante o
  // un render di UI con dati presenti.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    void (async () => {
      const { dbg } = await import("../../lib/debug/debugLog.js");
      dbg.info("WarehousePage", "render_state", {
        foods_count: foods.length,
        suppliers_count: suppliers.length,
        stockIn_count: stockInCount,
        stockOut_count: stockOutCount,
        foods_with_stock: foodsWithStockCount,
        quarantined_stock_events: stockProjection.quarantine.length,
        active_tab: tab,
      });
    })();
    // Solo quando le quantità cambiano davvero, non a ogni render.
  }, [
    foods.length,
    suppliers.length,
    stockInCount,
    stockOutCount,
    foodsWithStockCount,
    stockProjection.quarantine.length,
    tab,
  ]);

  // Per-lot detail. `qty` is the canonical available quantity: the shared
  // projector already includes immutable reservations and draft shipments.
  const lotMap = useMemo(() => {
    const receipts = indexWarehouseLotReceipts(stockIn as StockEvent[]);
    const lots = new Map<
      string,
      { food_id: string; lot: string | null; qty: number; expires: string | null; received_at: string | null }
    >();
    for (const balance of stockProjection.balances) {
      const key = warehouseLotKey(balance.foodId, balance.batchLot);
      lots.set(key, {
        food_id: balance.foodId,
        lot: balance.batchLot,
        qty: balance.availableQty,
        expires: balance.expirationDate,
        received_at: receipts.get(balance.foodId)?.get(balance.batchLot) ?? null,
      });
    }
    return lots;
  }, [stockIn, stockProjection.balances]);
  const [warehouseNow, setWarehouseNow] = useState(() => Date.now());
  const warehouseFilterDefaults = {
    storage: "all",
    status: "all",
    category: "all",
    sort: "name",
    expirationLeadHours: expirationPreferences.alertLeadHours,
    now: warehouseNow,
  } as const;
  const warehouseCandidateFoods = foods.filter((food) =>
    !food.archived_at || stockRelevantFoodIds.has(food.id)
  );
  const warehouseLots = [...lotMap.values()];
  const warehouseStatusCounts = {
    open: selectWarehouseFoods(warehouseCandidateFoods, warehouseLots, {
      ...warehouseFilterDefaults,
      status: "open",
    }).length,
    thawed: selectWarehouseFoods(warehouseCandidateFoods, warehouseLots, {
      ...warehouseFilterDefaults,
      status: "thawed",
    }).length,
    expiring: selectWarehouseFoods(warehouseCandidateFoods, warehouseLots, {
      ...warehouseFilterDefaults,
      status: "expiring",
    }).length,
  };
  const warehouseFoods = selectWarehouseFoods(
    warehouseCandidateFoods,
    warehouseLots,
    {
      storage: warehouseStorageFilter,
      status: warehouseStatusFilter,
      category: warehouseCategoryFilter,
      sort: warehouseSort,
      expirationLeadHours: expirationPreferences.alertLeadHours,
      now: warehouseNow,
    },
  );
  const warehouseFoodRanks = new Map(warehouseFoods.map((food, index) => [food.id, index]));
  const foodQuery = normalizeFoodSearch(foodSearch);
  const filteredFoods = warehouseFoods.filter((food) => !foodQuery
    || normalizeFoodSearch(food.name).includes(foodQuery)
    || normalizeFoodSearch(food.category ?? "").includes(foodQuery));
  // Limit only the rendered cards: search, filters and stock use every product.
  const foodPageKey = JSON.stringify([
    profile?.id, profile?.active_tenant_id, activeBusinessId,
    foodSearch, warehouseStorageFilter, warehouseStatusFilter,
    warehouseCategoryFilter, warehouseSort,
  ]);
  const [foodPage, setFoodPage] = useState<{ key: string; limit: number } | null>(null);
  useEffect(() => { setFoodPage(null); }, [foodPageKey]);
  const foodLimit = foodPage?.key === foodPageKey ? foodPage.limit : FOOD_PAGE_SIZE;
  const visibleFoods = filteredFoods.slice(0, foodLimit);

  const inventoryLotCount = [...lotMap.values()].filter((lot) => lot.qty > 0)
    .length;

  function foodName(id: string): string {
    return foods.find((f) => f.id === id)?.name ?? id.slice(0, 8);
  }
  function foodUom(id: string): string {
    return foods.find((f) => f.id === id)?.default_uom ?? "";
  }
  function foodIsArchived(id: string): boolean {
    return !!foods.find((f) => f.id === id)?.archived_at;
  }

  // Aggregazione "lotti distinti" per alimento (piano base): usa lo stato
  // finale del projector, non la presenza storica di un qualunque close. Così
  // un lotto chiuso e poi registrato di nuovo torna correttamente disponibile.
  const lotsByFood = useMemo(() => {
    const map = new Map<string, LotEntry[]>();
    for (const balance of stockProjection.balances) {
      const lot = balance.batchLot?.trim();
      if (!lot || balance.closed) continue;
      const source = (stockIn as StockEvent[]).find(
        (event) =>
          event.payload.food_id === balance.foodId &&
          (event.payload.batch_lot ?? "").trim() === lot,
      );
      const list = map.get(balance.foodId) ?? [];
      list.push({
        lot,
        expiration_date: balance.expirationDate,
        received_at: source?.client_ts ?? "",
        document_id: source?.payload.document_id ?? null,
      });
      map.set(balance.foodId, list);
    }
    // Sort cronologicamente più recenti prima.
    for (const list of map.values()) {
      list.sort((a, b) => b.received_at.localeCompare(a.received_at));
    }
    return map;
  }, [stockIn, stockProjection.balances]);

  // Stato lotto attivo comprensivo degli eventi ottimistici ancora in outbox.
  // Il master foods resta il fallback per storici/sync parziali; le azioni nuove
  // non lo patchano più separatamente dal comando stock.
  const activeLotByFood = useMemo(() => {
    const active = new Map<string, string | null>();
    const ordered = [
      ...(stockIn as StockEvent[]).map((event, index) => ({
        event,
        kind: "stock_in" as const,
        index,
      })),
      ...(stockOut as StockEvent[]).map((event, index) => ({
        event,
        kind: "stock_out" as const,
        index: index + stockIn.length,
      })),
    ].sort((left, right) => {
      const leftRev = left.event.server_rev;
      const rightRev = right.event.server_rev;
      if (leftRev != null && rightRev != null && leftRev !== rightRev) {
        return leftRev - rightRev;
      }
      if (leftRev != null && rightRev == null) return -1;
      if (leftRev == null && rightRev != null) return 1;
      const leftTs = Date.parse(left.event.client_ts) || 0;
      const rightTs = Date.parse(right.event.client_ts) || 0;
      if (leftTs !== rightTs) return leftTs - rightTs;
      const leftSeq = left.event.payload.command_seq ?? 0;
      const rightSeq = right.event.payload.command_seq ?? 0;
      if (leftSeq !== rightSeq) return leftSeq - rightSeq;
      return left.index - right.index;
    });
    for (const { event, kind } of ordered) {
      const lot = event.payload.batch_lot?.trim() || null;
      if (
        kind === "stock_in" &&
        lot &&
        event.payload.lot_action !== "recorded_only"
      ) {
        active.set(event.payload.food_id, lot);
      } else if (
        kind === "stock_out" &&
        event.payload.lot_state_action === "close" &&
        active.get(event.payload.food_id) === lot
      ) {
        active.set(event.payload.food_id, null);
      }
    }
    return active;
  }, [stockIn, stockOut]);

  function activeLotFor(food: Food): string | null {
    return activeLotByFood.has(food.id)
      ? activeLotByFood.get(food.id) ?? null
      : food.current_batch_lot?.trim() || null;
  }

  async function handleSetActiveLot(food: Food, lot: LotEntry): Promise<void> {
    try {
      if (hasQuarantinedStockForFood(food.id)) {
        throw new Error(
          "Movimenti non coerenti: verifica la giacenza prima di cambiare il lotto attivo",
        );
      }
      if (!profile?.active_tenant_id || !profile.active_business_id) {
        throw new Error("Nessun locale attivo");
      }
      const source = latestLotStockIn(food.id, lot.lot);
      await stockCommand.mutateAsync({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: profile.active_tenant_id,
        movement_class: "lot_state",
        economic_effect: "stock_only",
        business_id: profile.active_business_id,
        reason: "lot_set_active",
        lines: [{
          food_id: food.id,
          batch_lot: lot.lot,
          expiration_date: lot.expiration_date,
          supplier_id: source?.payload.supplier_id ?? null,
          document_id: lot.document_id,
          storage_unit_id: source?.payload.storage_unit_id ?? null,
          operator_name: profile.full_name?.trim() || null,
          // `register` è il comando esplicito che rende il lotto attivo.
          // `update_metadata` resta volutamente recorded_only e non modifica
          // foods.current_*.
          action: "register",
          uom: food.default_uom || "pz",
        }],
      });
      setToast(`Lotto ${lot.lot} impostato come attivo`);
      setLotMenuFor(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-074: "Vedi documento originale" da magazzino. Naviga al DocumentDetail
  // del DDT (`public.documents`) o della fattura (`fiscal_archive.documents`)
  // associato al lotto. Risolve il `source` via 2 query rapide, perché lato
  // food abbiamo solo `document_id` senza l'info "DDT vs fattura".
  async function openLotDocument(documentId: string): Promise<void> {
    if (!documentId) {
      setToast("Lotto senza documento collegato");
      return;
    }
    try {
      const sb = getSupabase();
      const { data: ddt } = await sb
        .from("documents")
        .select("id")
        .eq("id", documentId)
        .maybeSingle();
      if (ddt?.id) {
        setLotMenuFor(null);
        history.push(`/documents/public/${documentId}`);
        return;
      }
      const { data: fa } = await sb
        .from("fiscal_documents")
        .select("id")
        .eq("id", documentId)
        .maybeSingle();
      if (fa?.id) {
        setLotMenuFor(null);
        history.push(`/documents/fiscal/${documentId}`);
        return;
      }
      setToast("Documento non più disponibile (forse eliminato)");
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function handleCloseLot(food: Food, lot: LotEntry): Promise<void> {
    try {
      if (hasQuarantinedStockForFood(food.id)) {
        throw new Error(
          "Movimenti non coerenti: verifica la giacenza prima di chiudere il lotto",
        );
      }
      if (!profile?.active_tenant_id || !profile.active_business_id) {
        throw new Error("Nessun locale attivo");
      }
      const balance = stockBalanceByLot.get(warehouseLotKey(food.id, lot.lot));
      const onHand = balance?.onHandQty ?? 0;
      const available = balance?.availableQty ?? 0;
      if (onHand > 1e-9) {
        if (Math.abs(onHand - available) > 1e-6) {
          throw new Error(
            "Il lotto contiene quantità prenotate: libera prima le spedizioni in bozza.",
          );
        }
        const source = latestLotStockIn(food.id, lot.lot);
        await enqueueConsumption({
          foodId: food.id,
          lot: lot.lot,
          expirationDate: source?.payload.expiration_date ??
            lot.expiration_date,
          qty: onHand,
          uom: food.default_uom || "pz",
          reason: "consumed",
          closeLot: true,
        });
        setToast(`Lotto ${lot.lot} consumato e chiuso`);
        setLotMenuFor(null);
        return;
      }
      const lines: Extract<
        StockCommandV1,
        { movement_class: "lot_state" }
      >["lines"] = [{
        food_id: food.id,
        batch_lot: lot.lot,
        expiration_date: lot.expiration_date,
        document_id: lot.document_id,
        supplier_id: null,
        storage_unit_id: null,
        operator_name: profile.full_name?.trim() || null,
        action: "close",
        uom: food.default_uom || "pz",
      }];
      // Se si chiude il lotto attivo, la selezione del successivo viaggia nello
      // stesso command. Senza successivo, il trigger close azzera current_*.
      if (activeLotFor(food) === lot.lot) {
        const next = (lotsByFood.get(food.id) ?? []).find((entry) =>
          entry.lot !== lot.lot
        );
        if (next) {
          const source = latestLotStockIn(food.id, next.lot);
          lines.push({
            food_id: food.id,
            batch_lot: next.lot,
            expiration_date: next.expiration_date,
            document_id: next.document_id,
            supplier_id: source?.payload.supplier_id ?? null,
            storage_unit_id: source?.payload.storage_unit_id ?? null,
            operator_name: profile.full_name?.trim() || null,
            action: "register",
            uom: food.default_uom || "pz",
          });
        }
      }
      await stockCommand.mutateAsync({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: profile.active_tenant_id,
        movement_class: "lot_state",
        economic_effect: "stock_only",
        business_id: profile.active_business_id,
        reason: "lot_closed",
        lines,
      });
      setToast(`Lotto ${lot.lot} cancellato`);
      setLotMenuFor(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function commitManualLot(
    mode: "replace" | "add",
    choice?: { food: Food; lot: string; expiration_date: string | null },
  ): Promise<void> {
    const eff = choice ?? addLotChoice;
    if (!eff) return;
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast("Nessun locale attivo");
      return;
    }
    const { food, lot, expiration_date } = eff;
    try {
      const lines: Extract<
        StockCommandV1,
        { movement_class: "lot_state" }
      >["lines"] = [];
      // Se "replace", la chiusura e la registrazione viaggiano nello stesso
      // command e non possono restare applicate a metà.
      const activeLot = activeLotFor(food);
      if (mode === "replace" && activeLot) {
        const activeOnHand = currentLotOnHandQty(food.id, activeLot);
        if (activeOnHand > 1e-9) {
          throw new Error(
            `Il lotto attivo conserva ${
              activeOnHand.toFixed(2)
            } ${food.default_uom}. ` +
              "Registralo prima come finito o scartato: sostituirlo non può nascondere una giacenza reale.",
          );
        }
        const old = (lotsByFood.get(food.id) ?? []).find((l) =>
          l.lot === activeLot
        );
        lines.push({
          food_id: food.id,
          batch_lot: activeLot,
          expiration_date: old?.expiration_date ??
            food.current_expiration_date ?? null,
          supplier_id: null,
          storage_unit_id: null,
          document_id: old?.document_id ?? food.current_document_id ?? null,
          operator_name: profile.full_name?.trim() || null,
          action: "close",
          uom: food.default_uom || "pz",
        });
      }
      lines.push({
        food_id: food.id,
        operator_name: profile.full_name?.trim() || null,
        batch_lot: lot,
        expiration_date,
        manual_entry: true,
        document_number: addLotForm.document_number.trim() || null,
        document_date: documents.find((doc) =>
          doc.id === addLotDocId
        )?.document_date ?? null,
        supplier_id: null,
        storage_unit_id: null,
        document_id: addLotDocId || null,
        notify_expiration: true,
        action: "register",
        uom: food.default_uom || "pz",
      });
      await stockCommand.mutateAsync({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: profile.active_tenant_id,
        movement_class: "lot_state",
        economic_effect: "stock_only",
        business_id: profile.active_business_id,
        reason: mode === "replace"
          ? "manual_lot_replace"
          : "manual_lot_register",
        lines,
      });
      // Notifica scadenza locale.
      if (expiration_date) {
        void scheduleExpirationNotifications([{
          id: `${food.id}|${lot}`,
          product: food.name,
          date: expiration_date,
        }], expirationPreferences, profile.active_business_id);
      }
      setToast(mode === "replace" ? "Lotto sostituito" : "Lotto aggiunto");
      setAddLotChoice(null);
      setAddLotForm({
        food_id: "",
        batch_lot: "",
        expiration_date: "",
        document_number: "",
      });
      setAddLotDocId("");
      setAddLotOpen(false);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  const activeFilterCount =
    Number(warehouseStorageFilter !== "all") +
    Number(warehouseStatusFilter !== "all") +
    Number(warehouseCategoryFilter !== "all") +
    Number(warehouseSort !== "name");
  const warehouseFilterToggle = (
    <IonButton
      expand="block"
      fill="outline"
      aria-expanded={warehouseFiltersOpen}
      aria-controls="warehouse-shared-filters"
      onClick={() => setWarehouseFiltersOpen((open) => !open)}
      style={{ marginTop: 8, minHeight: 44 }}
    >
      {warehouseFiltersOpen ? "Nascondi filtri" : "Filtri e ordinamento"}
      {activeFilterCount > 0 &&
        ` · ${activeFilterCount} ${activeFilterCount === 1 ? "attivo" : "attivi"}`}
    </IonButton>
  );
  const warehouseFilterPanel = warehouseFiltersOpen && (
    <div id="warehouse-shared-filters" className="te-filter-panel" style={{ marginInline: 12 }}>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          paddingBottom: 4,
        }}
      >
        {(
          [
            {
              v: "all",
              label: "Tutti",
              count: foodStorageCounts.all,
              color: "#475569",
            },
            {
              v: "fresco",
              label: "Fresco",
              count: foodStorageCounts.fresco,
              color: "#0ea5e9",
            },
            {
              v: "surgelato",
              label: "Surgelato",
              count: foodStorageCounts.surgelato,
              color: "#6366f1",
            },
            {
              v: "secco",
              label: "Secco",
              count: foodStorageCounts.secco,
              color: "#f59e0b",
            },
            {
              v: "bevanda",
              label: "Bevanda",
              count: foodStorageCounts.bevanda,
              color: "#10b981",
            },
            {
              v: "open",
              label: "Prodotti aperti",
              count: warehouseStatusCounts.open,
              color: "#8b5cf6",
            },
            {
              v: "thawed",
              label: "Prodotti scongelati",
              count: warehouseStatusCounts.thawed,
              color: "#2563eb",
            },
            {
              v: "expiring",
              label: "Prodotti in scadenza",
              count: warehouseStatusCounts.expiring,
              color: "#d97706",
            },
          ] as const
        ).map((c) => {
          const isStatus = c.v === "open" || c.v === "thawed" || c.v === "expiring";
          const active = isStatus
            ? warehouseStatusFilter === c.v
            : warehouseStatusFilter === "all" && warehouseStorageFilter === c.v;
          return (
            <button
              key={c.v}
              type="button"
              aria-pressed={active}
              onClick={() => {
                if (isStatus) {
                  setWarehouseStorageFilter("all");
                  setWarehouseStatusFilter(c.v);
                } else {
                  setWarehouseStatusFilter("all");
                  setWarehouseStorageFilter(c.v);
                }
              }}
              style={{
                minHeight: 44,
                flexShrink: 0,
                background: active ? c.color : "#fff",
                color: active ? "#fff" : "#475569",
                border: `1px solid ${active ? c.color : "#e2e8f0"}`,
                borderRadius: 999,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: active ? `0 1px 3px ${c.color}33` : "none",
              }}
            >
              {c.label}
              <span
                style={{
                  background: active ? "rgba(255,255,255,.25)" : "#f1f5f9",
                  color: active ? "#fff" : "#94a3b8",
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 999,
                  minWidth: 18,
                  textAlign: "center",
                }}
              >
                {c.count}
              </span>
            </button>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 8,
          alignItems: "center",
        }}
      >
        <IonSelect
          interface="popover"
          aria-label="Categoria"
          value={warehouseCategoryFilter}
          onIonChange={(ev) => setWarehouseCategoryFilter(String(ev.detail.value))}
          style={
            {
              flex: 1,
            minWidth: 140,
            minHeight: 44,
              fontSize: 12,
              "--padding-start": "12px",
              "--padding-end": "12px",
              background: "#fff",
              borderRadius: 10,
              border: warehouseCategoryFilter === "all" ? "1px solid #e2e8f0" : "1px solid #0066cc",
            } as React.CSSProperties
          }
        >
          <IonSelectOption value="all">📁 Tutte le categorie</IonSelectOption>
          {FOOD_CATEGORIES.map((c) => (
            <IonSelectOption key={c.value} value={c.value}>
              {c.label}
            </IonSelectOption>
          ))}
        </IonSelect>
        <IonSelect
          interface="popover"
          aria-label="Ordinamento"
          value={warehouseSort}
          onIonChange={(ev) => setWarehouseSort(ev.detail.value as typeof warehouseSort)}
          style={
            {
              flex: 1,
            minWidth: 140,
            minHeight: 44,
              fontSize: 12,
              "--padding-start": "12px",
              "--padding-end": "12px",
              background: "#fff",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
            } as React.CSSProperties
          }
        >
          <IonSelectOption value="name">↕ Nome A-Z</IonSelectOption>
          <IonSelectOption value="category">↕ Categoria</IonSelectOption>
          <IonSelectOption value="expiry">↕ Scadenza</IonSelectOption>
        </IonSelect>
      </div>
    </div>
  );

  return (
    <IonPage>
      {NATIVE_PERF_TRACE_ENABLED && <WarehousePerfTrace
        scopeKey={`${profile?.id}:${profile?.active_tenant_id}:${profile?.active_business_id}`}
        readyMask={[foodsData, allBusinessesData, stockInData, stockOutData, stockReservedData, draftShipmentsData]
          .reduce((mask, rows, index) => mask | (rows === undefined ? 0 : 1 << index), 0)}
        errorMask={[foodsError, businessesError, stockInError, stockOutError, stockReservedError, draftShipmentsError]
          .reduce((mask, error, index) => mask | (error ? 1 << index : 0), 0)}
      />}
      <WarehouseReconciliationRefresh onRefresh={refreshStockCommandReconciliations} />
      <WarehouseClock onChange={setWarehouseNow} />
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("warehouse.title")}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment
            value={tab}
            onIonChange={(ev) => setTab(ev.detail.value as Tab)}
          >
            <IonSegmentButton value="foods">
              <IonLabel>{t("warehouse.foods", "Prodotti")}</IonLabel>
            </IonSegmentButton>
            {warehouseEnabled && (
              <IonSegmentButton value="lots">
                <IonLabel>{t("warehouse.inventory", "Giacenza")}</IonLabel>
              </IonSegmentButton>
            )}
            <IonSegmentButton value="suppliers">
              <IonLabel>{t("warehouse.suppliers")}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent
        key={resumeKey}
        className="te-page"
      >
        {tab !== "suppliers" && (warehouseDataMissing || warehouseReadError) && (
          <IonItem lines="none">
            {!warehouseReadError && <IonSpinner slot="start" name="dots" />}
            <IonLabel className="ion-text-wrap" role={warehouseReadError ? "alert" : "status"}>
              {warehouseReadError
                ? warehouseDataMissing
                  ? "Impossibile leggere i dati locali del magazzino. Riprova."
                  : "Aggiornamento dei dati locali non riuscito. Mostro gli ultimi dati caricati."
                : "Caricamento dei dati locali del magazzino…"}
            </IonLabel>
            {warehouseReadError && <IonButton slot="end" onClick={retryWarehouseRead}>Riprova</IonButton>}
          </IonItem>
        )}
        {
          /* T-224: switcher "magazzino di quale sede". Con collettiva attiva e
            più di una sede, permette di passare agilmente da un magazzino
            all'altro (centrale / impianto / …). Cambia la sede attiva globale
            (active_business_id), su cui sono già filtrate giacenza, etichette
            e trasferimenti. */
        }
        {collettivaEnabled && tenantBusinesses.length > 1 && (
          <div
            style={{
              background: "#fff",
              margin: "12px 12px 0",
              borderRadius: 12,
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid #e2e8f0",
            }}
          >
            <span style={{ fontSize: 16 }}>🏭</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#3b82f6",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Magazzino della sede
              </div>
              <IonSelect
                interface="action-sheet"
                value={profile?.active_business_id ?? ""}
                onIonChange={(ev) => {
                  const id = String(ev.detail.value ?? "");
                  if (id && id !== profile?.active_business_id) {
                    void switchBusiness(id);
                  }
                }}
                style={{
                  "--padding-start": 0,
                  "--padding-end": 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0f172a",
                } as React.CSSProperties}
              >
                {tenantBusinesses.map((b) => (
                  <IonSelectOption key={b.id} value={b.id}>
                    {b.name || `Sede ${b.id.slice(0, 8)}`}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </div>
          </div>
        )}
        {/* T-215: accesso rapido collettiva (render nullo se flag off) */}
        <CollettivaQuickNav />
        {legacyReconciliations.length > 0 && (
          <IonItem
            button
            detail
            color="warning"
            onClick={() => setLegacyReconciliationOpen(true)}
          >
            <IonIcon icon={documentTextOutline} slot="start" />
            <IonLabel className="ion-text-wrap">
              <h3>Verifica richiesta su movimenti precedenti</h3>
              <p>
                {legacyReconciliations.length} moviment
                {legacyReconciliations.length === 1
                  ? "o locale non è stato accettato"
                  : "i locali non sono stati accettati"}{" "}
                dal nuovo controllo di giacenza e non vengono conteggiati.
              </p>
            </IonLabel>
          </IonItem>
        )}
        {(stockCommandReconciliations.length + legacyPermanentRejections.length) > 0 && (
          <IonItem
            button
            detail
            color="warning"
            onClick={() => setLegacyReconciliationOpen(true)}
          >
            <IonIcon icon={documentTextOutline} slot="start" />
            <IonLabel className="ion-text-wrap">
              <h3>Operazioni magazzino da verificare</h3>
              <p>
                {stockCommandReconciliations.length + legacyPermanentRejections.length} operazion
                {stockCommandReconciliations.length + legacyPermanentRejections.length === 1 ? "e è stata isolata" : "i sono state isolate"}.
                Le altre continuano a sincronizzarsi.
              </p>
            </IonLabel>
          </IonItem>
        )}
        {tab === "foods" && !warehouseDataMissing && (
          <>
            <div style={{ padding: "14px 12px 0" }}>
              <div className="te-screen-intro">
                <div>
                  <h1>Anagrafica prodotti</h1>
                  <p>{visibleFoods.length} di {filteredFoods.length} prodotti visualizzati. Cerca subito oppure apri i filtri solo quando servono.</p>
                </div>
                <IonButton onClick={() => openFoodEditor()}>
                  Nuovo prodotto
                </IonButton>
              </div>
              <IonSearchbar
                placeholder="Cerca prodotto"
                aria-label="Cerca prodotto in magazzino"
                debounce={0}
                value={foodSearch}
                onIonInput={(ev) =>
                  setFoodSearch(String(ev.detail.value ?? ""))}
                style={{ padding: 0 }}
              />
              {warehouseFilterToggle}
            </div>
            {/* ── FILTRI: chip storage_type + select categoria + sort ── */}
            {warehouseFilterPanel}
            {
              /* T-132: strumento di pulizia "Alimenti mai usati né caricati".
                Sezione collassabile, mostrata solo se ci sono orfani. Azione
                per riga: archivia (soft-delete append-only), mai cancella. */
            }
            {orphanFoods.length > 0 && (
              <div style={{ padding: "4px 12px 0" }}>
                <button
                  type="button"
                  onClick={() => setOrphansOpen((v) => !v)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                    border: "1px solid #fde68a",
                    borderRadius: 12,
                    padding: "10px 14px",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 16 }}>🧹</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#92400e",
                      }}
                    >
                      {t(
                        "warehouse.orphans.title",
                        "Prodotti mai usati né caricati",
                      )}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "#b45309",
                        marginTop: 2,
                      }}
                    >
                      {t(
                        "warehouse.orphans.subtitle",
                        "Anagrafiche senza ricetta e senza carico: candidati all’archiviazione",
                      )}
                    </span>
                  </span>
                  <span
                    style={{
                      background: "#f59e0b",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 999,
                      minWidth: 20,
                      textAlign: "center",
                    }}
                  >
                    {orphanFoods.length}
                  </span>
                  <span style={{ fontSize: 12, color: "#b45309" }}>
                    {orphansOpen ? "▴" : "▾"}
                  </span>
                </button>
                {orphansOpen && (
                  <div
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderTop: "none",
                      borderRadius: "0 0 12px 12px",
                      marginTop: -2,
                      padding: "4px 6px 8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11,
                        color: "#92400e",
                        padding: "6px 10px 4px",
                        margin: 0,
                      }}
                    >
                      {t(
                        "warehouse.orphans.hint",
                        "Archiviare li nasconde dalle liste; non vengono cancellati e restano recuperabili dal backup.",
                      )}
                    </p>
                    {orphanFoods.map((f) => (
                      <div
                        key={f.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 10px",
                          margin: "4px 4px",
                          boxShadow: "0 1px 2px rgba(15,23,42,.05)",
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            onClick={() => openFoodEditor(f)}
                            role="button"
                            style={{
                              display: "block",
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#0f172a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {f.name}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: 11,
                              color: "#94a3b8",
                              marginTop: 1,
                            }}
                          >
                            {FOOD_CATEGORIES.find((c) => c.value === f.category)
                              ?.label ?? f.category ?? "—"}
                          </span>
                        </span>
                        <IonButton
                          size="small"
                          fill="outline"
                          color="warning"
                          onClick={() =>
                            void requestFoodArchive({
                              id: f.id,
                              name: f.name,
                              version: f.version ?? 0,
                            })}
                          style={{
                            "--padding-start": "10px",
                            "--padding-end": "10px",
                            textTransform: "none",
                            fontSize: 12,
                          } as React.CSSProperties}
                        >
                          {t("warehouse.orphans.archive", "Archivia")}
                        </IonButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div style={{ padding: "8px 12px 96px" }}>
              {foods.length === 0 &&
                  !foodSearch &&
                  !isIntroDismissed(
                    "warehouse_foods",
                    profile?.id ?? null,
                    profile?.active_business_id ?? null,
                  )
                ? (
                  <FeatureIntroBlock
                    featureKey="warehouse_foods"
                    icon="🥫"
                    title="Anagrafica prodotti"
                    description={
                      <>
                        L'<b>anagrafica prodotti</b>{" "}
                        è il punto di partenza del magazzino: ogni ingrediente
                        che entra nel locale (da carne e pesce a farine, vini,
                        prodotti puliti) è registrato qui con nome, categoria,
                        conservazione e allergeni.
                        <br />
                        <br />
                        {canCreateFoods
                          ? <>Quando carichi un DDT o una fattura, l&apos;AI propone gli alimenti mancanti e collega i lotti. Puoi anche crearli a mano.</>
                          : <>Dal tuo profilo puoi associare gli alimenti esistenti. Se nel DDT ne manca uno, l&apos;intero carico passa prima alla revisione di un responsabile.</>}
                      </>
                    }
                    whatsappQuestion="Ciao, ho dubbi sull'anagrafica prodotti in TrackEAT, mi puoi aiutare?"
                    startLabel={canCreateFoods ? "Aggiungi il primo prodotto" : "Carica un DDT"}
                    onStart={canCreateFoods
                      ? () => setFoodEditorOpen(true)
                      : () => history.push("/documents/capture")}
                    userId={profile?.id ?? null}
                    businessId={profile?.active_business_id ?? null}
                    flush
                  />
                )
                : filteredFoods.length === 0
                ? (
                  <div
                    style={{
                      background: "#fff",
                      padding: 16,
                      borderRadius: 14,
                      color: "#64748b",
                      fontStyle: "italic",
                      fontSize: 13,
                    }}
                  >
                    {t("warehouse.empty")}
                  </div>
                )
                : (
                  <IonList style={{ background: "transparent" }} lines="none">
                    {visibleFoods.map((f) => {
                      const qty = stockByFood.get(f.id) ?? 0;
                      const usedIn = recipesPerFood.get(f.id) ?? 0;
                      const barColor =
                        STORAGE_BAR_COLOR[f.storage_type ?? ""] ?? "#94a3b8";
                      const qtyClass = qty > 0
                        ? "niveo-badge niveo-badge--ok"
                        : "niveo-badge niveo-badge--danger";
                      // Piano base (warehouse OFF): mostriamo TUTTI i lotti attivi
                      // di ogni alimento (lista verticale) con il badge "attivo"
                      // sul lotto corrente. Piano Pro: indicazione giacenza classica.
                      const foodLots = lotsByFood.get(f.id) ?? [];
                      // T-181: nel piano base (warehouse OFF) il lotto attivo vive sui
                      // campi current_* del food master, che è SEMPRE sincronizzato.
                      // L'elenco lotti deriva però dagli eventi stock_in locali: se
                      // questi non sono ancora sul dispositivo (es. import fatto su un
                      // altro device, eventi non ancora sincronizzati) il food mostrava
                      // "Nessun lotto registrato" pur avendo current_batch_lot valorizzato.
                      // Qui surfacciamo il lotto attivo dal master così resta visibile.
                      const activeLot = activeLotFor(f);
                      const baseLots = (() => {
                        const cur = activeLot;
                        if (
                          !cur || cur === "-" ||
                          foodLots.some((l) => l.lot === cur)
                        ) return foodLots;
                        return [
                          {
                            lot: cur,
                            expiration_date: f.current_expiration_date ?? null,
                            received_at: f.current_lot_received_at ?? "",
                            document_id: f.current_document_id ?? null,
                          },
                          ...foodLots,
                        ];
                      })();
                      const activeExpiration = foodLots.find((lot) =>
                        lot.lot === activeLot
                      )
                        ?.expiration_date ??
                        (activeLot === f.current_batch_lot
                          ? f.current_expiration_date
                          : null);
                      const expDays = activeExpiration
                        ? Math.floor(
                          (new Date(activeExpiration).getTime() - warehouseNow) /
                            86_400_000,
                        )
                        : null;
                      const expBadge = expiryBadge(expDays);
                      return (
                        <div
                          key={f.id}
                          style={{
                            display: "flex",
                            background: "#ffffff",
                            borderRadius: 14,
                            boxShadow:
                              "0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)",
                            marginBottom: 10,
                            overflow: "hidden",
                          }}
                        >
                          {
                            /* Stripe colorata storage_type: colonna flex 4px a tutta
                          altezza che unifica visivamente food + lotti. Soluzione
                          flex (non absolute) per evitare lo stacking context di
                          IonItemSliding che nascondeva il bordo. */
                          }
                          <div
                            style={{
                              width: 4,
                              background: barColor,
                              flexShrink: 0,
                              alignSelf: "stretch",
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <IonItemSliding>
                              <IonItem
                                button={!f.archived_at}
                                detail={false}
                                onClick={() => {
                                  if (f.archived_at) {
                                    setToast(
                                      "Prodotto archiviato con saldo storico: serve una bonifica controllata prima di modificarlo.",
                                    );
                                    return;
                                  }
                                  openFoodEditor(f);
                                }}
                                style={{
                                  "--background": "#ffffff",
                                  "--padding-start": "14px",
                                  "--inner-padding-end": "14px",
                                  "--min-height": "68px",
                                } as React.CSSProperties}
                              >
                                <IonLabel style={{ marginInlineEnd: 8 }}>
                                  <h3
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 16,
                                      color: "#0f172a",
                                      letterSpacing: "-0.005em",
                                    }}
                                  >
                                    {f.name}
                                  </h3>
                                  <p
                                    style={{
                                      fontSize: 12,
                                      color: "#64748b",
                                      marginTop: 2,
                                    }}
                                  >
                                    {FOOD_CATEGORIES.find((c) =>
                                      c.value === f.category
                                    )?.label ?? f.category ?? "—"}
                                    {f.storage_type
                                      ? ` · ${f.storage_type}`
                                      : ""}
                                  </p>
                                  {usedIn > 0 && (
                                    <p
                                      style={{
                                        color: "#0066cc",
                                        fontSize: 11,
                                        marginTop: 2,
                                      }}
                                    >
                                      In {usedIn}{" "}
                                      ricett{usedIn === 1 ? "a" : "e"}
                                    </p>
                                  )}
                                  {f.archived_at && (
                                    <p
                                      style={{
                                        color: "#b45309",
                                        fontSize: 11,
                                        marginTop: 2,
                                        fontWeight: 700,
                                      }}
                                    >
                                      Archiviato · saldo storico ancora attivo
                                    </p>
                                  )}
                                </IonLabel>
                                {f.data_sheet_file_id
                                  ? (
                                    <IonIcon
                                      slot="end"
                                      icon={documentTextOutline}
                                      title="Scheda tecnica"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        void openTechSheet(
                                          f.data_sheet_file_id!,
                                        );
                                      }}
                                      style={{
                                        fontSize: 20,
                                        color: "#0066cc",
                                        marginInlineEnd: 6,
                                      }}
                                    />
                                  )
                                  : null}
                                {warehouseEnabled
                                  ? (
                                    <span
                                      slot="end"
                                      className={qtyClass}
                                      style={{ fontSize: 12 }}
                                    >
                                      {qty.toFixed(2)} {f.default_uom}
                                    </span>
                                  )
                                  : expBadge
                                  ? (
                                    <span
                                      slot="end"
                                      className={expBadge.cls}
                                      style={{ fontSize: 12 }}
                                    >
                                      {expBadge.text}
                                    </span>
                                  )
                                  : null}
                              </IonItem>
                              {!f.archived_at && (
                                <IonItemOptions side="end">
                                  <IonItemOption
                                    color="primary"
                                    onClick={() => openFoodEditor(f)}
                                  >
                                    <IonIcon
                                      slot="icon-only"
                                      icon={createOutline}
                                    />
                                  </IonItemOption>
                                  <IonItemOption
                                    color="danger"
                                    onClick={() =>
                                      void requestFoodArchive({
                                        id: f.id,
                                        name: f.name,
                                        version: f.version ?? 0,
                                      })}
                                  >
                                    <IonIcon
                                      slot="icon-only"
                                      icon={trashOutline}
                                    />
                                  </IonItemOption>
                                </IonItemOptions>
                              )}
                            </IonItemSliding>
                            {!warehouseEnabled && (
                              <div
                                style={{
                                  background: "#fafbfc",
                                  borderTop: "1px solid #f1f5f9",
                                  padding: "4px 12px 8px 14px",
                                }}
                              >
                                {baseLots.length === 0
                                  ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        padding: "6px 4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#94a3b8",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        Nessun lotto registrato
                                      </span>
                                      {!f.archived_at && (
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          onClick={() => {
                                            setAddLotForm({
                                              food_id: f.id,
                                              batch_lot: "",
                                              expiration_date: "",
                                              document_number: "",
                                            });
                                            setAddLotOpen(true);
                                          }}
                                          style={{
                                            "--padding-start": "10px",
                                            "--padding-end": "10px",
                                            textTransform: "none",
                                          } as React.CSSProperties}
                                        >
                                          <IonIcon slot="start" icon={add} />
                                          Aggiungi lotto
                                        </IonButton>
                                      )}
                                    </div>
                                  )
                                  : (
                                    <>
                                      {baseLots.map((l, lotIdx) => {
                                        const isActive = activeLot === l.lot;
                                        const days = l.expiration_date
                                          ? Math.floor(
                                            (new Date(l.expiration_date)
                                              .getTime() - warehouseNow) /
                                              86_400_000,
                                          )
                                          : null;
                                        const lotBadge = expiryBadge(days);
                                        return (
                                          <div
                                            key={l.lot}
                                            onClick={() => {
                                              setLotMenuFor({
                                                food: f,
                                                lot: l.lot,
                                                expiration_date:
                                                  l.expiration_date,
                                                isActive,
                                              });
                                            }}
                                            role="button"
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              paddingBlock: 8,
                                              paddingInline: 4,
                                              borderTop: lotIdx > 0
                                                ? "1px solid #f1f5f9"
                                                : "none",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {/* Indicatore "attivo": pallino blu pieno se attivo, vuoto altrimenti */}
                                            <div
                                              style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: 4,
                                                background: isActive
                                                  ? "#0066cc"
                                                  : "transparent",
                                                border: isActive
                                                  ? "none"
                                                  : "1px solid #cbd5e1",
                                                flexShrink: 0,
                                              }}
                                            />
                                            <div
                                              style={{
                                                flex: 1,
                                                minWidth: 0,
                                                display: "flex",
                                                alignItems: "baseline",
                                                gap: 8,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: 13,
                                                  fontWeight: isActive
                                                    ? 600
                                                    : 500,
                                                  color: isActive
                                                    ? "#0066cc"
                                                    : "#475569",
                                                  fontVariantNumeric:
                                                    "tabular-nums",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                {l.lot}
                                              </span>
                                              {l.expiration_date && (
                                                <span
                                                  style={{
                                                    fontSize: 11,
                                                    color: "#94a3b8",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {new Date(l.expiration_date)
                                                    .toLocaleDateString(
                                                      "it-IT",
                                                    )}
                                                </span>
                                              )}
                                            </div>
                                            {lotBadge && (
                                              <span
                                                className={lotBadge.cls}
                                                style={{
                                                  fontSize: 10,
                                                  padding: "2px 8px",
                                                }}
                                              >
                                                {lotBadge.text}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                      <div
                                        style={{
                                          paddingTop: 4,
                                          paddingBottom: 2,
                                          textAlign: "right",
                                          borderTop: "1px solid #f1f5f9",
                                        }}
                                      >
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          onClick={() => {
                                            setAddLotForm({
                                              food_id: f.id,
                                              batch_lot: "",
                                              expiration_date: "",
                                              document_number: "",
                                            });
                                            setAddLotOpen(true);
                                          }}
                                          style={{
                                            "--padding-start": "10px",
                                            "--padding-end": "10px",
                                            textTransform: "none",
                                            fontSize: 12,
                                          } as React.CSSProperties}
                                        >
                                          <IonIcon slot="start" icon={add} />
                                          Aggiungi lotto
                                        </IonButton>
                                      </div>
                                    </>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </IonList>
                )}
              {visibleFoods.length < filteredFoods.length && (
                <IonButton expand="block" fill="outline" onClick={() => setFoodPage((current) => ({
                  key: foodPageKey,
                  limit: (current?.key === foodPageKey ? current.limit : FOOD_PAGE_SIZE) + FOOD_PAGE_SIZE,
                }))}>
                  Mostra di più
                </IonButton>
              )}
            </div>
          </>
        )}

        {tab === "lots" && !warehouseDataMissing && (
          <>
            <div style={{ padding: "14px 12px 0" }}>
              <div className="te-screen-intro">
                <div>
                  <h1>Giacenze</h1>
                  <p>
                    {inventoryLotCount} {inventoryLotCount === 1 ? "lotto disponibile" : "lotti disponibili"}.
                    Cerca subito per nome prodotto o numero di lotto.
                  </p>
                </div>
              </div>
              <IonSearchbar
                placeholder="Cerca per prodotto o lotto"
                aria-label="Cerca nelle giacenze per prodotto o numero di lotto"
                inputmode="search"
                value={lotSearch}
                onIonInput={(ev) =>
                  setLotSearch(String(ev.detail.value ?? ""))}
                style={{ padding: 0 }}
              />
              {warehouseFilterToggle}
            </div>
            {warehouseFilterPanel}
            <div style={{ marginInline: 12 }}>
                {canViewHistoricalLots && (
                  <IonButton
                    fill="outline"
                    color="medium"
                    style={{ minHeight: 44, textTransform: "none" }}
                    onClick={() => {
                      // The route repeats this client guard and the server
                      // repeats it again: a hidden button is never the only
                      // protection for historical physical recounts.
                      if (!hasAppCapability(profile, "warehouse.historical_lots")) {
                        return;
                      }
                      history.push("/warehouse/historical-lots");
                    }}
                  >
                    <IonIcon icon={timeOutline} slot="start" />
                    Lotti storici
                  </IonButton>
                )}
            </div>
            {stockProjection.quarantine.length > 0 && (
              <IonItem color="warning" lines="none" className="ion-text-wrap">
                <IonIcon icon={documentTextOutline} slot="start" />
                <IonLabel>
                  <h3>Giacenza parzialmente verificabile</h3>
                  <p>
                    {stockProjection.quarantine.length} moviment
                    {stockProjection.quarantine.length === 1
                      ? "o è stato escluso"
                      : "i sono stati esclusi"}{" "}
                    perché quantità o UdM non sono coerenti. Le azioni sui
                    prodotti coinvolti restano bloccate per evitare nuovi
                    errori.
                  </p>
                  <IonButton
                    expand="block"
                    fill="solid"
                    color="light"
                    aria-label="Apri l’elenco dei problemi di giacenza da correggere"
                    style={{
                      marginTop: 8,
                      minHeight: 44,
                      textTransform: "none",
                    }}
                    onClick={() => setUomReconciliationOpen(true)}
                  >
                    Vedi e correggi problemi
                  </IonButton>
                </IonLabel>
              </IonItem>
            )}
            {(() => {
              const q = lotSearch.trim().toLowerCase();
              const now = warehouseNow;
              const allBeforeQtyFilter = [...lotMap.values()];
              const all = allBeforeQtyFilter.filter((l) => l.qty > 0);
              // Diagnostica: se la lista finale è vuota mentre lotMap aveva
              // entries, il filter qty>0 sta tagliando tutto (qty NaN/string?).
              if (all.length === 0 && allBeforeQtyFilter.length > 0) {
                void import("../../lib/debug/debugLog.js").then(({ dbg }) => {
                  dbg.warn("WarehousePage", "lots_filter_kills_all", {
                    lotMap_size: allBeforeQtyFilter.length,
                    filter_survived: all.length,
                    sample_qty_values: allBeforeQtyFilter.slice(0, 5).map((
                      l,
                    ) => ({
                      qty: l.qty,
                      qty_type: typeof l.qty,
                      food_id: l.food_id?.slice(0, 8),
                      lot: l.lot,
                    })),
                  });
                });
              }
              const matchingLots = all.filter((lot) => warehouseFoodRanks.has(lot.food_id)
                || (warehouseStorageFilter === "all" && warehouseStatusFilter === "all"
                  && warehouseCategoryFilter === "all"
                  && !foods.some((food) => food.id === lot.food_id)));
              const filtered = q
                ? matchingLots.filter((l) =>
                  foodName(l.food_id).toLowerCase().includes(q) ||
                  (l.lot ?? "").toLowerCase().includes(q)
                )
                : matchingLots;
              if (filtered.length === 0) {
                return (
                  <div
                    style={{
                      padding: 16,
                      margin: 16,
                      background: "#fff",
                      borderRadius: 14,
                      color: "#64748b",
                      fontStyle: "italic",
                      fontSize: 13,
                    }}
                  >
                    {q
                      ? "Nessun lotto trovato. Prova con il nome del prodotto o con una parte del numero di lotto."
                      : activeFilterCount > 0
                        ? "Nessuna giacenza corrisponde ai filtri selezionati."
                        : "Nessuna giacenza. Registra un carico per iniziare."}
                  </div>
                );
              }
              const byFood = new Map<string, typeof filtered>();
              for (const l of filtered) {
                const list = byFood.get(l.food_id) ?? [];
                list.push(l);
                byFood.set(l.food_id, list);
              }
              const sortedGroups = [...byFood.entries()].sort((a, b) =>
                (warehouseFoodRanks.get(a[0]) ?? Infinity) - (warehouseFoodRanks.get(b[0]) ?? Infinity)
                || foodName(a[0]).localeCompare(foodName(b[0]), "it"));
              return (
                <div style={{ padding: "8px 12px 96px" }}>
                  {sortedGroups.map(([fid, lots]) => {
                    const totalQty = lots.reduce((s, l) => s + l.qty, 0);
                    const sortedLots = [...lots].sort((a, b) => {
                      const ax = a.expires ? Date.parse(a.expires) : Infinity;
                      const bx = b.expires ? Date.parse(b.expires) : Infinity;
                      return ax - bx;
                    });
                    return (
                      <div
                        key={fid}
                        style={{
                          marginBottom: 14,
                          background: "#fff",
                          borderRadius: 14,
                          boxShadow:
                            "0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)",
                          overflow: "hidden",
                        }}
                      >
                        {/* Intestazione gruppo alimento */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 14px",
                            borderBottom: "1px solid #e2e8f0",
                            background: "#eef2f7",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: "#0f172a",
                              letterSpacing: "0.01em",
                            }}
                          >
                            {foodName(fid)}
                            {foodIsArchived(fid) && (
                              <span
                                style={{
                                  display: "block",
                                  color: "#b45309",
                                  fontSize: 11,
                                  marginTop: 2,
                                }}
                              >
                                Archiviato · disponibile solo per lo scarico
                              </span>
                            )}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#475569",
                              background: "#fff",
                              padding: "4px 10px",
                              borderRadius: 999,
                              border: "1px solid #cbd5e1",
                            }}
                          >
                            Tot {totalQty.toFixed(2)} {foodUom(fid)}
                          </span>
                        </div>
                        <div>
                          {sortedLots.map((l) => {
                            const lotFoodName = foodName(l.food_id);
                            const isThawedLot = /^SC-/i.test(l.lot ?? "") || /\(scongelato\)$/i.test(lotFoodName);
                            const isOpenedLot = /^AP-/i.test(l.lot ?? "") || /\(aperto\)$/i.test(lotFoodName);
                            const expMs = l.expires
                              ? Date.parse(l.expires)
                              : null;
                            const daysLeft = expMs != null
                              ? Math.floor((expMs - now) / 86_400_000)
                              : null;
                            const badge = expiryBadge(daysLeft);
                            const accent = daysLeft != null && daysLeft < 0
                              ? "#dc2626"
                              : daysLeft != null && daysLeft <= 2
                              ? "#b45309"
                              : daysLeft != null && daysLeft <= 7
                              ? "#0d9488"
                              : "#cbd5e1";
                            return (
                              <div
                                key={`${l.food_id}|${l.lot ?? ""}`}
                                onClick={() => {
                                  setSelectedLot({
                                    food_id: l.food_id,
                                    lot: l.lot,
                                  });
                                }}
                                role="button"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "12px 14px",
                                  borderLeft: `3px solid ${accent}`,
                                  borderBottom: "1px solid #f1f5f9",
                                  cursor: "pointer",
                                  background: foodIsArchived(l.food_id)
                                    ? "#fffbeb"
                                    : "#fff",
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 14,
                                      color: "#0f172a",
                                    }}
                                  >
                                    {l.lot
                                      ? `Lotto ${l.lot}`
                                      : "Senza numero lotto"}
                                  </div>
                                  {l.expires && (
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: "#64748b",
                                        marginTop: 2,
                                      }}
                                    >
                                      Scade{" "}
                                      {new Date(l.expires).toLocaleDateString(
                                        "it-IT",
                                      )}
                                    </div>
                                  )}
                                  {(isThawedLot || isOpenedLot) && l.received_at && (
                                    <div style={{ fontSize: 12, color: "#475569", marginTop: 2, fontWeight: 600 }}>
                                      {isThawedLot ? "Scongelato" : "Aperto"}{" "}
                                      il {new Date(l.received_at).toLocaleString("it-IT", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                    gap: 4,
                                  }}
                                >
                                  <span
                                    className="niveo-badge niveo-badge--ok"
                                    style={{ fontSize: 11 }}
                                  >
                                    {l.qty.toFixed(2)} {foodUom(l.food_id)}
                                  </span>
                                  {badge && (
                                    <span
                                      className={badge.cls}
                                      style={{ fontSize: 11 }}
                                    >
                                      {badge.text}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}

        {tab === "suppliers" && (
          <>
            <IonToolbar>
              <IonSearchbar
                placeholder="Cerca fornitore (nome, P.IVA, categoria)"
                value={supplierSearch}
                onIonInput={(ev) =>
                  setSupplierSearch(String(ev.detail.value ?? ""))}
              />
            </IonToolbar>
            <div style={{ padding: "8px 12px 96px" }}>
              {filteredSuppliers.length === 0
                ? (
                  <div
                    style={{
                      background: "#fff",
                      padding: 16,
                      borderRadius: 14,
                      color: "#64748b",
                      fontStyle: "italic",
                      fontSize: 13,
                    }}
                  >
                    {supplierSearch
                      ? "Nessun fornitore trovato"
                      : t("warehouse.empty")}
                  </div>
                )
                : (
                  <IonList style={{ background: "transparent" }} lines="none">
                    {filteredSuppliers.map((s) => {
                      const initials = s.name.trim().split(/\s+/).slice(0, 2)
                        .map((w) => w[0]?.toUpperCase() ?? "").join("");
                      // Hash il nome per scegliere un colore stabile
                      const colors = [
                        "#0066cc",
                        "#0d9488",
                        "#b45309",
                        "#7c3aed",
                        "#db2777",
                        "#15803d",
                      ];
                      const colorIdx = s.name.split("").reduce((a, c) =>
                        a + c.charCodeAt(0), 0) % colors.length;
                      const avatarBg = colors[colorIdx]!;
                      return (
                        <IonItemSliding key={s.id} style={{ marginBottom: 10 }}>
                          <IonItem
                            button
                            detail={false}
                            onClick={() =>
                              openSupplierEditor(s)}
                            style={{
                              "--background": "#ffffff",
                              "--padding-start": "12px",
                              "--inner-padding-end": "14px",
                              "--min-height": "64px",
                              borderRadius: 14,
                              boxShadow:
                                "0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)",
                              overflow: "hidden",
                            } as React.CSSProperties}
                          >
                            <div
                              slot="start"
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 12,
                                flexShrink: 0,
                                background: avatarBg,
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                                fontWeight: 700,
                              }}
                            >
                              {initials}
                            </div>
                            <IonLabel>
                              <h3
                                style={{
                                  fontWeight: 600,
                                  fontSize: 15,
                                  color: "#0f172a",
                                }}
                              >
                                {s.name}
                              </h3>
                              <p style={{ fontSize: 12, color: "#64748b" }}>
                                {s.category}
                                {s.piva ? ` · P.IVA ${s.piva}` : ""}
                                {s.country ? ` · ${s.country}` : ""}
                              </p>
                              {(s.email || s.phone) && (
                                <p
                                  style={{
                                    fontSize: 11,
                                    color: "#94a3b8",
                                    marginTop: 2,
                                  }}
                                >
                                  {[s.email, s.phone].filter(Boolean).join(
                                    " · ",
                                  )}
                                </p>
                              )}
                            </IonLabel>
                          </IonItem>
                          <IonItemOptions side="end">
                            <IonItemOption
                              color="primary"
                              onClick={() =>
                                openSupplierEditor(s)}
                            >
                              <IonIcon slot="icon-only" icon={createOutline} />
                            </IonItemOption>
                            <IonItemOption
                              color="danger"
                              onClick={() =>
                                setConfirmArchive({
                                  table: "suppliers",
                                  id: s.id,
                                  name: s.name,
                                  version: s.version ?? 0,
                                })}
                            >
                              <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonItemOption>
                          </IonItemOptions>
                        </IonItemSliding>
                      );
                    })}
                  </IonList>
                )}
            </div>
          </>
        )}

        {tab !== "foods" && <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton
            aria-label="Aggiungi"
            onClick={() => setSheetOpen(true)}
          >
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>}

        <IonActionSheet
          isOpen={sheetOpen}
          onDidDismiss={() => setSheetOpen(false)}
          header="Magazzino"
          buttons={[
            {
              text: "Importa fattura/DDT (foto AI)",
              icon: scanOutline,
              handler: () => {
                history.push("/documents/capture");
              },
            },
            {
              text: "Nuova lavorazione (QR tracciabilità)",
              handler: () => {
                history.push("/warehouse/productions");
              },
            },
            {
              text: "Nuovo prodotto",
              handler: () => {
                openFoodEditor();
              },
            },
            {
              text: "Nuovo fornitore",
              handler: () => {
                openSupplierEditor();
              },
            },
            // Carico/Scarico con quantità: solo piano Pro (warehouse_enabled).
            // Sul piano base offriamo "Aggiungi lotto" senza giacenza.
            // Lo scarico NON è più qui: dal "+" non si conosce il lotto, quindi
            // produceva stock_out con batch_lot=null (orfani — non aggiornavano la
            // giacenza del lotto né la conversione UdM). Lo scarico di una quantità
            // si fa dal "Dettaglio lotto" (tap su un lotto → "Scarica"), dove il
            // lotto è noto e lo stock_out è sempre attribuito correttamente.
            ...(warehouseEnabled
              ? [
                {
                  text: "⚡ Carico rapido (foto etichetta → coda)",
                  handler: () => {
                    setModal("quick_load");
                  },
                },
                {
                  text: "Carico magazzino",
                  handler: () => {
                    setModal("stock_in");
                  },
                },
              ]
              : [
                {
                  text: "Aggiungi lotto",
                  handler: () => {
                    setAddLotForm({
                      food_id: "",
                      batch_lot: "",
                      expiration_date: "",
                      document_number: "",
                    });
                    setAddLotOpen(true);
                  },
                },
              ]),
            { text: "Annulla", role: "cancel" as const },
          ]}
        />

        {/* T-407: Carico rapido a coda — foto etichetta → estrazione in background. */}
        <IonModal
          isOpen={modal === "quick_load"}
          onDidDismiss={() => setModal(null)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>⚡ Carico rapido</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setModal(null)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>
              Scegli prodotto e quantità, scatta 1+ foto dell'etichetta e premi
              {" "}
              <b>Aggiungi in coda</b>. Lotto e scadenza vengono letti in
              background:{" "}
              <b>non aspetti</b>, passi subito al prodotto successivo.
            </p>

            <IonItem>
              <IonLabel position="stacked">Prodotto</IonLabel>
              <IonSelect
                value={qlFood}
                placeholder="Scegli…"
                interface="action-sheet"
                onIonChange={(ev) => setQlFood(String(ev.detail.value ?? ""))}
              >
                {foods.filter((f) => !f.archived_at).map((f) => (
                  <IonSelectOption key={f.id} value={f.id}>
                    {f.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Quantità</IonLabel>
              <IonInput
                type="number"
                inputmode="decimal"
                value={qlQty}
                placeholder="es. 5"
                onIonInput={(ev) => setQlQty(String(ev.detail.value ?? ""))}
              />
            </IonItem>
            {storageUnits.filter((u) => !u.archived_at).length > 0 && (
              <IonItem>
                <IonLabel position="stacked">Posizione (opzionale)</IonLabel>
                <IonSelect
                  value={qlStorage}
                  placeholder="Nessuna"
                  interface="action-sheet"
                  onIonChange={(ev) =>
                    setQlStorage(String(ev.detail.value ?? ""))}
                >
                  <IonSelectOption value="">Nessuna</IonSelectOption>
                  {storageUnits.filter((u) => !u.archived_at).map((u) => (
                    <IonSelectOption key={u.id} value={u.id}>
                      {u.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            )}
            <IonItem>
              <IonLabel>
                Foto etichetta{" "}
                {qlPhotos.length > 0 ? `(${qlPhotos.length})` : ""}
              </IonLabel>
              <IonButton
                slot="end"
                fill="outline"
                size="small"
                onClick={() => {
                  void qlCapturePhoto();
                }}
              >
                <IonIcon icon={cameraOutline} slot="start" />Scatta
              </IonButton>
            </IonItem>
            {qlPhotos.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  margin: "8px 0",
                }}
              >
                {qlPhotos.map((p, i) => (
                  <div key={i} style={{ position: "relative" }}>
                    <img
                      src={`data:image/jpeg;base64,${p}`}
                      alt={`foto ${i + 1}`}
                      style={{
                        width: 72,
                        height: 72,
                        objectFit: "cover",
                        borderRadius: 6,
                      }}
                    />
                    <IonButton
                      fill="clear"
                      size="small"
                      color="medium"
                      style={{ position: "absolute", top: -10, right: -10 }}
                      onClick={() =>
                        setQlPhotos((ps) =>
                          ps.filter((_, j) =>
                            j !== i
                          )
                        )}
                      aria-label="Rimuovi foto"
                    >
                      <IonIcon icon={closeOutline} />
                    </IonButton>
                  </div>
                ))}
              </div>
            )}
            <IonButton
              expand="block"
              onClick={qlAddToQueue}
              style={{ marginTop: 12 }}
            >
              <IonIcon icon={add} slot="start" />Aggiungi in coda
            </IonButton>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 24,
              }}
            >
              <h4 style={{ margin: 0 }}>
                Coda{labelQueue.pendingCount > 0
                  ? ` · ${labelQueue.pendingCount} in lavorazione`
                  : ""}
              </h4>
              {labelQueue.queue.some((i) => i.status === "done") && (
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={labelQueue.clearDone}
                >
                  Svuota completati
                </IonButton>
              )}
            </div>
            {labelQueue.queue.length === 0 && (
              <p style={{ color: "#999", fontSize: 13 }}>
                Nessun prodotto in coda.
              </p>
            )}
            <IonList>
              {labelQueue.queue.map((it) => (
                <IonItem key={it.id} lines="full">
                  <IonLabel className="ion-text-wrap">
                    <strong>{it.foodName}</strong> · {it.qty} {it.uom}
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      {it.status === "queued" && (
                        <IonChip color="medium">⏳ in coda</IonChip>
                      )}
                      {it.status === "processing" && (
                        <span>
                          <IonSpinner
                            name="dots"
                            style={{
                              width: 18,
                              height: 18,
                              verticalAlign: "middle",
                            }}
                          />{" "}
                          analisi etichetta…
                        </span>
                      )}
                      {it.status === "done" && (
                        <IonChip color="success">
                          ✓ lotto {it.batchLot ?? "—"}
                          {it.expirationDate
                            ? ` · scad ${
                              new Date(it.expirationDate).toLocaleDateString(
                                "it-IT",
                              )
                            }`
                            : ""}
                        </IonChip>
                      )}
                      {it.status === "error" && (
                        <IonChip color="danger">
                          ⚠️ {it.error ?? "errore"}
                        </IonChip>
                      )}
                      {it.status === "needs_fix" && (
                        <div style={{ marginTop: 6 }}>
                          <IonChip color="warning">
                            Etichetta non letta — inserisci a mano
                          </IonChip>
                          <IonItem lines="none">
                            <IonInput
                              label="Lotto"
                              labelPlacement="stacked"
                              value={qlFix[it.id]?.lot ?? ""}
                              onIonInput={(ev) =>
                                setQlFix((m) => ({
                                  ...m,
                                  [it.id]: {
                                    lot: String(ev.detail.value ?? ""),
                                    exp: m[it.id]?.exp ?? "",
                                  },
                                }))}
                            />
                          </IonItem>
                          <IonItem lines="none">
                            <IonInput
                              label="Scadenza"
                              labelPlacement="stacked"
                              type="date"
                              value={qlFix[it.id]?.exp ?? ""}
                              onIonInput={(ev) =>
                                setQlFix((m) => ({
                                  ...m,
                                  [it.id]: {
                                    lot: m[it.id]?.lot ?? "",
                                    exp: String(ev.detail.value ?? ""),
                                  },
                                }))}
                            />
                          </IonItem>
                          <IonButton
                            size="small"
                            onClick={() => {
                              void labelQueue.commitManual(
                                it.id,
                                qlFix[it.id]?.lot || null,
                                qlFix[it.id]?.exp || null,
                              );
                            }}
                          >
                            Salva carico
                          </IonButton>
                        </div>
                      )}
                    </div>
                  </IonLabel>
                  {(it.status === "error" || it.status === "needs_fix") && (
                    <IonButtons slot="end">
                      {it.status === "error" && (
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => labelQueue.retry(it.id)}
                        >
                          Riprova
                        </IonButton>
                      )}
                      <IonButton
                        fill="clear"
                        size="small"
                        color="medium"
                        onClick={() => labelQueue.remove(it.id)}
                        aria-label="Scarta"
                      >
                        <IonIcon icon={trashOutline} />
                      </IonButton>
                    </IonButtons>
                  )}
                </IonItem>
              ))}
            </IonList>
          </IonContent>
        </IonModal>

        <FoodEditorModal
          isOpen={foodEditorOpen && (!!foodToEdit || canCreateFoods)}
          initial={foodToEdit as FoodEditorValue | undefined}
          lockDefaultUom={!!foodToEdit && (
            stockProjection.balances.some(
              (balance) =>
                balance.foodId === foodToEdit.id &&
                balance.eventCount > 0,
            ) ||
            hasQuarantinedStockForFood(foodToEdit.id) ||
            (stockInAll as StockEvent[]).some(
              (event) => event.payload.food_id === foodToEdit.id,
            ) ||
            (stockOutAll as StockEvent[]).some(
              (event) => event.payload.food_id === foodToEdit.id,
            )
          )}
          onDismiss={() => {
            setFoodEditorOpen(false);
            setStockFoodCreatePending(false);
          }}
          onSaved={(food) => {
            // Se il FoodEditor era stato aperto da dentro lo Stock-In modal
            // ("+ Crea nuovo alimento"), preseleziona l'id appena creato
            // così l'utente trova subito il food selezionato e può proseguire
            // a digitare quantità / lotto. Senza, la perdita di contesto
            // costringerebbe a riaprire la select e cercarlo a mano.
            if (stockFoodCreatePending && food.id) {
              setStockForm((s) => ({ ...s, food_id: food.id }));
              setStockFoodCreatePending(false);
            }
          }}
          onRequestUomRebase={(foodId) => {
            setFoodEditorOpen(false);
            setFoodToEdit(undefined);
            openUnitFix(foodId);
          }}
          onRequestArchive={(food) => void requestFoodArchive(food)}
        />

        {/* Selettore ricerca alimento (carico stock-in e "aggiungi lotto"). */}
        <SearchPickerModal
          isOpen={foodPicker !== null}
          title="Scegli un prodotto"
          wrapItems
          focusSearchOnOpen
          searchPlaceholder="Cerca prodotto…"
          selectedId={foodPicker === "stock"
            ? stockForm.food_id
            : addLotForm.food_id}
          emptyHint="Nessun prodotto. Creane uno con «Nuovo prodotto»."
          items={foods
            .filter((f) =>
              !f.archived_at ||
              (
                foodPicker === "stock" &&
                modal === "stock_out" &&
                stockRelevantFoodIds.has(f.id)
              )
            )
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((f) => ({
              id: f.id,
              label: f.name,
              sublabel: f.archived_at
                ? "Archiviato · disponibile solo per scaricare il saldo residuo"
                : FOOD_CATEGORIES.find((c) =>
                  c.value === f.category
                )?.label ?? undefined,
            }))}
          onSelect={(id) => {
            if (foodPicker === "stock") {setStockForm((s) => ({
                ...s,
                food_id: id,
              })
              );} else if (foodPicker === "addLot") {setAddLotForm((s) => ({
                ...s,
                food_id: id,
              }));}
            setFoodPicker(null);
          }}
          onClose={() => setFoodPicker(null)}
        />

        <SupplierEditorModal
          isOpen={supplierEditorOpen}
          initial={supplierToEdit as SupplierEditorValue | undefined}
          onDismiss={() => {
            setSupplierEditorOpen(false);
            setStockSupplierCreatePending(false);
          }}
          onSaved={(s) => {
            if (stockSupplierCreatePending && s.id) {
              setStockForm((sf) => ({ ...sf, supplier_id: s.id }));
              setStockSupplierCreatePending(false);
            }
          }}
          onRequestArchive={(s) =>
            setConfirmArchive({ table: "suppliers", ...s })}
          onRequestCanonicalMerge={(sourceFoodId, targetFoodId) => {
            if (openStockReassignment(
              sourceFoodId,
              true,
              null,
              targetFoodId,
            )) {
              setSupplierEditorOpen(false);
            }
          }}
        />

        <IonModal
          data-testid="stock-modal"
          isOpen={modal === "stock_in" || modal === "stock_out"}
          onDidDismiss={() => {
            setModal(null);
            setReceivingConformityConfirmed(false);
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {modal === "stock_in"
                  ? "Carico magazzino"
                  : "Scarico magazzino"}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setModal(null)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {foods.length === 0
              ? (
                <>
                  <IonItem lines="none">
                    <IonLabel
                      color="medium"
                      className="ion-text-wrap"
                      style={{ fontSize: 13 }}
                    >
                      {canCreateFoods
                        ? 'Nessun alimento ancora. Creane uno qui sotto: tornerai automaticamente a questo carico.'
                        : 'Nessun alimento disponibile. Carica un DDT: le nuove schede saranno create soltanto dopo la revisione di un responsabile.'}
                    </IonLabel>
                  </IonItem>
                  {canCreateFoods && <IonButton
                    expand="block"
                    color="primary"
                    onClick={() => {
                      setStockFoodCreatePending(true);
                      setFoodEditorOpen(true);
                    }}
                    style={{ marginTop: 8 }}
                  >
                    <IonIcon icon={add} slot="start" />
                    Crea il primo alimento
                  </IonButton>}
                </>
              )
              : (
                <>
                  {modal === "stock_in" && (
                    <IonButton
                      expand="block"
                      fill="outline"
                      color="primary"
                      onClick={() => {
                        setModal(null);
                        history.push("/documents/capture");
                      }}
                      style={{ marginBottom: 12 }}
                    >
                      <IonIcon icon={scanOutline} slot="start" />
                      Importa da fattura/DDT (foto AI)
                    </IonButton>
                  )}
                  <IonItem>
                    <PickerTriggerField
                      label="Prodotto *"
                      placeholder="Tocca per cercare un prodotto"
                      value={foods.find((f) => f.id === stockForm.food_id)
                        ?.name}
                      onClick={() => setFoodPicker("stock")}
                    />
                  </IonItem>
                  {canCreateFoods && <IonItem
                    button
                    detail={false}
                    onClick={() => {
                      setStockFoodCreatePending(true);
                      setFoodEditorOpen(true);
                    }}
                    lines="none"
                  >
                    <IonIcon icon={add} slot="start" color="primary" />
                    <IonLabel color="primary" style={{ fontSize: 13 }}>
                      + Crea nuovo prodotto (non in lista)
                    </IonLabel>
                  </IonItem>}
                  {modal === "stock_in" && (
                    <>
                      <IonItem>
                        <IonLabel position="stacked">Fornitore</IonLabel>
                        <IonSelect
                          value={stockForm.supplier_id}
                          placeholder="(nessuno)"
                          onIonChange={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              supplier_id: String(ev.detail.value ?? ""),
                            }))}
                        >
                          <IonSelectOption value="">(nessuno)</IonSelectOption>
                          {suppliers.filter((s) => !s.archived_at).map((s) => (
                            <IonSelectOption key={s.id} value={s.id}>
                              {s.name}
                              {s.piva ? ` · ${s.piva}` : ""}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                      <IonItem
                        button
                        detail={false}
                        onClick={() => {
                          setStockSupplierCreatePending(true);
                          setSupplierEditorOpen(true);
                        }}
                        lines="none"
                      >
                        <IonIcon icon={add} slot="start" color="primary" />
                        <IonLabel color="primary" style={{ fontSize: 13 }}>
                          + Crea nuovo fornitore (non in lista)
                        </IonLabel>
                      </IonItem>
                    </>
                  )}
                  <IonItem>
                    <IonLabel position="stacked">
                      Quantità * ({foods.find((f) => f.id === stockForm.food_id)
                        ?.default_uom ?? "—"})
                    </IonLabel>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      value={stockForm.qty}
                      onIonInput={(ev) =>
                        setStockForm((f) => ({
                          ...f,
                          qty: String(ev.detail.value ?? ""),
                        }))}
                    />
                  </IonItem>
                  {modal === "stock_in" && (
                    <>
                      <IonItem>
                        {
                          /* T-090: label esplicita "Lotto / N° Fattura" — il cliente
                          nel carico manuale usa spesso il numero della fattura
                          cartacea come riferimento di tracciabilità (Reg. CE
                          178/2002 Art. 18 — basta che sia un identificativo
                          univoco riconducibile al lotto fisico). Esempi nel
                          placeholder lo rendono evidente. */
                        }
                        <IonLabel position="stacked">
                          Lotto / N° Fattura
                        </IonLabel>
                        <IonInput
                          value={stockForm.batch_lot}
                          placeholder="es. LT-2024-089 oppure FATT 2024/0156"
                          onIonInput={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              batch_lot: String(ev.detail.value ?? ""),
                            }))}
                        />
                      </IonItem>
                      <IonItem>
                        {
                          /* T-136: riferimento fattura/documento OPZIONALE,
                          distinto dal numero di lotto. Stesso collegamento al
                          documento di carico usato dall'import AI, così la
                          correzione manuale resta tracciabile lotto→fattura. */
                        }
                        <IonLabel position="stacked">
                          {t(
                            "warehouse.docRef.label",
                            "Riferimento fattura / N° documento",
                          )}
                          <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                            ({t("common.optional", "opzionale")})
                          </span>
                        </IonLabel>
                        <IonInput
                          value={stockForm.document_number}
                          placeholder={t(
                            "warehouse.docRef.placeholder",
                            "es. FATT 2024/0156, DDT 887",
                          )}
                          onIonInput={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              document_number: String(ev.detail.value ?? ""),
                            }))}
                        />
                      </IonItem>
                      {
                        /* T-136: collegamento OPZIONALE a un documento già caricato
                        (FK reale lotto→documento). Selezionando un documento si
                        compila anche il riferimento testuale. */
                      }
                      {docOptions.length > 0 && (
                        <IonItem>
                          <IonLabel position="stacked">
                            {t("warehouse.docRef.link", "Collega documento")}
                            <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                              ({t("common.optional", "opzionale")})
                            </span>
                          </IonLabel>
                          <IonSelect
                            value={stockDocId}
                            interface="action-sheet"
                            placeholder={t(
                              "warehouse.docRef.linkPlaceholder",
                              "Nessun documento collegato",
                            )}
                            onIonChange={(ev) => {
                              const id = String(ev.detail.value ?? "");
                              setStockDocId(id);
                              const doc = docOptions.find((o) => o.id === id);
                              if (doc?.number) {
                                setStockForm((f) => ({
                                  ...f,
                                  document_number: doc.number as string,
                                }));
                              }
                            }}
                          >
                            <IonSelectOption value="">
                              {t("warehouse.docRef.linkNone", "Nessuno")}
                            </IonSelectOption>
                            {docOptions.map((o) => (
                              <IonSelectOption key={o.id} value={o.id}>
                                {o.label}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                        </IonItem>
                      )}
                      <IonItem>
                        <IonLabel position="stacked">Scadenza</IonLabel>
                        <IonInput
                          type="date"
                          value={stockForm.expiration_date}
                          onIonInput={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              expiration_date: String(ev.detail.value ?? ""),
                            }))}
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">
                          Codice fornitore (SKU)
                        </IonLabel>
                        <IonInput
                          value={stockForm.supplier_sku}
                          placeholder="es. AV-UO-456"
                          onIonInput={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              supplier_sku: String(ev.detail.value ?? ""),
                            }))}
                        />
                      </IonItem>
                      {
                        /* T-284 (Food Cost): costo unitario di carico OPZIONALE.
                        Base storica del food cost; vuoto = non noto. */
                      }
                      <IonItem>
                        <IonLabel position="stacked">
                          Costo unitario (€/{foods.find((f) =>
                            f.id === stockForm.food_id
                          )?.default_uom ?? "UdM"})
                          <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                            ({t("common.optional", "opzionale")})
                          </span>
                        </IonLabel>
                        <IonInput
                          type="number"
                          inputmode="decimal"
                          value={stockForm.unit_price}
                          placeholder="es. 2.50"
                          step="0.01"
                          onIonInput={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              unit_price: String(ev.detail.value ?? ""),
                            }))}
                          onIonBlur={() =>
                            setStockForm((f) => ({
                              ...f,
                              unit_price: f.unit_price.trim() === ""
                                ? ""
                                : moneyInputValue(Number(
                                  f.unit_price.replace(",", "."),
                                )),
                            }))}
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">
                          Frigo / Cella destinazione
                        </IonLabel>
                        <IonSelect
                          value={stockForm.storage_unit_id}
                          placeholder="(opzionale)"
                          onIonChange={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              storage_unit_id: String(ev.detail.value ?? ""),
                            }))}
                        >
                          <IonSelectOption value="">
                            (opzionale)
                          </IonSelectOption>
                          {storageUnits.filter((u) => !u.archived_at).map((
                            u,
                          ) => (
                            <IonSelectOption key={u.id} value={u.id}>
                              {u.name} · {u.kind}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                      <IonItem>
                        <IonLabel>Surgelato</IonLabel>
                        <IonToggle
                          slot="end"
                          checked={stockForm.is_frozen}
                          onIonChange={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              is_frozen: ev.detail.checked,
                            }))}
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel className="ion-text-wrap">
                          <strong>Controllo conformità al ricevimento *</strong>
                          <p style={{ color: "#64748b", fontSize: 12 }}>
                            Confermo integrità, temperatura, scadenza ed
                            etichettatura. Se la merce non è conforme non
                            registrare il carico: apri prima la non conformità e
                            registralo solo dopo correzione/autorizzazione.
                          </p>
                        </IonLabel>
                        <IonToggle
                          slot="end"
                          checked={receivingConformityConfirmed}
                          onIonChange={(ev) =>
                            setReceivingConformityConfirmed(ev.detail.checked)}
                          aria-label="Conferma controllo di conformità al ricevimento"
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel>
                          Notifica alla scadenza
                          <p style={{ color: "#888", fontSize: 11 }}>
                            Push 24h prima + giorno scadenza
                          </p>
                        </IonLabel>
                        <IonToggle
                          slot="end"
                          checked={stockForm.notify_expiration}
                          onIonChange={(ev) =>
                            setStockForm((f) => ({
                              ...f,
                              notify_expiration: ev.detail.checked,
                            }))}
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel>
                          Foto etichetta
                          <p style={{ color: "#888", fontSize: 11 }}>
                            {labelAnalyzing
                              ? "🔎 Leggo lotto e scadenza…"
                              : "Compila lotto + scadenza dalla foto"}
                          </p>
                        </IonLabel>
                        <IonButton
                          slot="end"
                          fill="outline"
                          size="small"
                          disabled={labelAnalyzing}
                          onClick={() => {
                            void captureLotPhoto("camera");
                          }}
                        >
                          <IonIcon icon={cameraOutline} slot="start" />
                          {labelAnalyzing
                            ? "Analisi…"
                            : (lotPhotoB64 ? "Sostituisci" : "Scatta")}
                        </IonButton>
                        {lotPhotoB64 && !labelAnalyzing && (
                          <IonButton
                            slot="end"
                            fill="clear"
                            size="small"
                            color="medium"
                            onClick={() => setLotPhotoB64(null)}
                            aria-label="Rimuovi foto"
                          >
                            <IonIcon icon={closeOutline} />
                          </IonButton>
                        )}
                      </IonItem>
                      {lotPhotoB64 && (
                        <div style={{ textAlign: "center", margin: "8px 0" }}>
                          <img
                            src={`data:image/jpeg;base64,${lotPhotoB64}`}
                            alt="Foto lotto"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 160,
                              borderRadius: 6,
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
                    <IonButton
                      expand="block"
                      disabled={uploadingPhoto || stockCommand.isPending}
                      onClick={() => void saveStockMovement(
                        modal === "stock_in" ? "stock_in" : "stock_out",
                      )}
                      style={{ flex: 1 }}
                    >
                      {uploadingPhoto ? "Caricamento…" : "Salva"}
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => setModal(null)}
                      style={{ flex: 1 }}
                    >
                      Annulla
                    </IonButton>
                  </div>
                </>
              )}
          </IonContent>
        </IonModal>

        {/* Dettaglio lotto: storico carichi + scarichi del lotto selezionato */}
        <IonModal
          isOpen={!!selectedLot}
          onDidDismiss={() => setSelectedLot(null)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Dettaglio lotto</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedLot(null)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedLot && (() => {
              const lotEntries = (stockIn as StockEvent[]).filter(
                (e) =>
                  warehouseLotKey(
                    e.payload.food_id,
                    e.payload.batch_lot ?? null,
                  ) ===
                    warehouseLotKey(selectedLot.food_id, selectedLot.lot),
              );
              const lotExits = (stockOut as StockEvent[]).filter(
                (e) =>
                  warehouseLotKey(
                    e.payload.food_id,
                    e.payload.batch_lot ?? null,
                  ) ===
                    warehouseLotKey(selectedLot.food_id, selectedLot.lot),
              );
              const food = foods.find((f) => f.id === selectedLot.food_id);
              const archived = !!food?.archived_at;
              const stockReliable = !hasQuarantinedStockForFood(
                selectedLot.food_id,
              );
              const canOpenUnitFix = stockReliable || canOpenUomRebase({
                events: stockProjection.quarantine,
                foodId: selectedLot.food_id,
              });
              const uom = food?.default_uom ?? "pz";
              // Le due gambe del rebase sono snapshot tecnici: restano visibili
              // nell'audit, ma non sono un nuovo carico/scarico fisico e non
              // devono gonfiare i totali storici.
              const convertedEntries = lotEntries
                .filter((event) => !isUomRebaseEvent(event))
                .map((event) => eventQtyInUom(event, uom));
              const convertedExits = lotExits
                .filter((event) => !isUomRebaseEvent(event))
                .map((event) => eventQtyInUom(event, uom));
              const totalIn = convertedEntries.every((qty) => qty != null)
                ? convertedEntries.reduce<number>(
                  (sum, qty) => sum + (qty ?? 0),
                  0,
                )
                : null;
              const totalOut = convertedExits.every((qty) => qty != null)
                ? convertedExits.reduce<number>(
                  (sum, qty) => sum + (qty ?? 0),
                  0,
                )
                : null;
              const onHand = currentLotOnHandQty(
                selectedLot.food_id,
                selectedLot.lot,
              );
              const available = currentLotAvailableQty(
                selectedLot.food_id,
                selectedLot.lot,
              );
              const firstIn = lotEntries[0]?.payload;
              const expires = stockBalanceByLot.get(
                warehouseLotKey(selectedLot.food_id, selectedLot.lot),
              )?.expirationDate ?? firstIn?.expiration_date;
              const docDate = firstIn?.document_date ?? null;
              const docNumber = firstIn?.document_number ?? null;
              const lotFromDocNumber =
                firstIn?.batch_lot_source === "document_number_fallback";
              // T-241: fornitore del carico (dal supplier_id dell'evento stock_in
              // di questo lotto) → risale al nome master per il double-check.
              const supplierName = firstIn?.supplier_id
                ? (suppliers.find((s) => s.id === firstIn.supplier_id)?.name ??
                  null)
                : null;
              return (
                <>
                  <IonList inset>
                    <IonItem>
                      <IonLabel>
                        <h2>{food?.name ?? "—"}</h2>
                        <p>Lotto {selectedLot.lot ?? "senza numero"}</p>
                      </IonLabel>
                    </IonItem>
                    {!stockReliable && (
                      <IonItem color="warning" lines="none">
                        <IonLabel
                          className="ion-text-wrap"
                          style={{ fontSize: 12 }}
                        >
                          {canOpenUnitFix
                            ? "Le azioni ordinarie restano bloccate, ma questo prodotto può essere riallineato in sicurezza."
                            : "Azioni bloccate: esistono movimenti con quantità o UdM da verificare che non sono riconciliabili automaticamente."}
                          {canOpenUnitFix && (
                            <IonButton
                              size="small"
                              color="dark"
                              fill="outline"
                              aria-label={`Riconcilia unità e giacenza per ${
                                food?.name ?? "prodotto"
                              }`}
                              style={{
                                display: "block",
                                marginTop: 8,
                                textTransform: "none",
                              }}
                              disabled={archived || stockCommand.isPending}
                              onClick={() => openUnitFix(selectedLot.food_id)}
                            >
                              Riconcilia unità e giacenza
                            </IonButton>
                          )}
                          {!canOpenUnitFix && (
                            <IonButton
                              size="small"
                              color="dark"
                              fill="outline"
                              style={{
                                display: "block",
                                marginTop: 8,
                                textTransform: "none",
                              }}
                              disabled={archived ||
                                stockReconciliation.isPending}
                              onClick={() =>
                                openPhysicalRecount(selectedLot.food_id)}
                            >
                              Riconteggio fisico guidato
                            </IonButton>
                          )}
                        </IonLabel>
                      </IonItem>
                    )}
                    {lotFromDocNumber && (
                      <IonItem color="warning" lines="none">
                        <IonLabel
                          className="ion-text-wrap"
                          style={{ fontSize: 12 }}
                        >
                          ⚠️ Lotto generato dal numero documento (la fattura non
                          riportava i lotti reali del fornitore).
                        </IonLabel>
                      </IonItem>
                    )}
                    <IonItem>
                      <IonLabel>Caricato</IonLabel>
                      <IonNote slot="end">
                        {totalIn == null
                          ? "storico in più UdM"
                          : `${totalIn.toFixed(2)} ${uom}`}
                      </IonNote>
                    </IonItem>
                    <IonItem>
                      <IonLabel>Scaricato</IonLabel>
                      <IonNote slot="end">
                        {totalOut == null
                          ? "storico in più UdM"
                          : `${totalOut.toFixed(2)} ${uom}`}
                      </IonNote>
                    </IonItem>
                    <IonItem>
                      <IonLabel>
                        <b>Giacenza fisica</b>
                      </IonLabel>
                      <IonNote
                        slot="end"
                        color={onHand > 0 ? "success" : "medium"}
                      >
                        <b>{onHand.toFixed(2)} {uom}</b>
                      </IonNote>
                    </IonItem>
                    {Math.abs(available - onHand) > 1e-9 && (
                      <IonItem>
                        <IonLabel>
                          <b>Disponibile</b>
                        </IonLabel>
                        <IonNote
                          slot="end"
                          color={available > 0 ? "success" : "warning"}
                        >
                          <b>{available.toFixed(2)} {uom}</b>
                        </IonNote>
                      </IonItem>
                    )}
                    {expires && (
                      <IonItem>
                        <IonLabel>Scadenza</IonLabel>
                        <IonNote slot="end">
                          {new Date(expires).toLocaleDateString("it-IT")}
                        </IonNote>
                      </IonItem>
                    )}
                    {supplierName && (
                      <IonItem>
                        <IonLabel>Fornitore</IonLabel>
                        <IonNote
                          slot="end"
                          className="ion-text-wrap"
                          style={{ textAlign: "right" }}
                        >
                          {supplierName}
                        </IonNote>
                      </IonItem>
                    )}
                    {docNumber && (
                      <IonItem>
                        <IonLabel>Documento</IonLabel>
                        <IonNote slot="end">{docNumber}</IonNote>
                      </IonItem>
                    )}
                    {docDate && (
                      <IonItem>
                        <IonLabel>Data documento</IonLabel>
                        <IonNote slot="end">
                          {new Date(docDate).toLocaleDateString("it-IT")}
                        </IonNote>
                      </IonItem>
                    )}
                  </IonList>

                  <IonList inset>
                    <IonItem lines="none" color="light">
                      <IonLabel>
                        <h3>
                          Storico movimenti ({lotEntries.length +
                            lotExits.length})
                        </h3>
                      </IonLabel>
                    </IonItem>
                    {[
                      ...lotEntries.map((e) => ({ ...e, kind: "in" as const })),
                      ...lotExits.map((e) => ({ ...e, kind: "out" as const })),
                    ]
                      .sort((a, b) =>
                        Date.parse(b.client_ts) - Date.parse(a.client_ts)
                      )
                      .map((e) => {
                        const rebase = isUomRebaseEvent(e);
                        const convertedQty = eventQtyInUom(e, uom);
                        const originalQty = e.payload.stock_qty ??
                          e.payload.qty;
                        const originalUom = e.payload.stock_uom ??
                          e.payload.uom ?? uom;
                        return (
                          <IonItem key={e.id}>
                            <IonIcon
                              icon={e.kind === "in" ? add : trashOutline}
                              slot="start"
                              color={e.kind === "in" ? "success" : "danger"}
                            />
                            <IonLabel>
                              <h3>
                                {rebase
                                  ? (e.kind === "in"
                                    ? "Saldo convertito (tecnico)"
                                    : "Chiusura vecchia UdM (tecnica)")
                                  : (e.kind === "in" ? "Carico" : "Scarico")}
                              </h3>
                              <p>
                                {new Date(e.client_ts).toLocaleString("it-IT")}
                              </p>
                            </IonLabel>
                            <IonNote slot="end">
                              {rebase ? "" : (e.kind === "in" ? "+" : "-")}
                              {convertedQty == null
                                ? `${originalQty.toFixed(2)} ${originalUom}`
                                : `${convertedQty.toFixed(2)} ${uom}`}
                            </IonNote>
                          </IonItem>
                        );
                      })}
                  </IonList>

                  {onHand > 0 && (
                    <div style={{ padding: "8px 16px 24px" }}>
                      {archived && (
                        <p
                          style={{
                            color: "#b45309",
                            fontSize: "0.85em",
                            marginBottom: 12,
                            fontWeight: 600,
                          }}
                        >
                          Prodotto archiviato: puoi soltanto scaricare il saldo
                          residuo. Rettifiche, cambio unità e modifica lotto
                          restano bloccati.
                        </p>
                      )}
                      <p
                        style={{
                          color: "var(--ion-color-medium)",
                          fontSize: "0.85em",
                          marginBottom: 12,
                        }}
                      >
                        Azioni sul lotto
                      </p>
                      <div
                        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                      >
                        {
                          /* Scarico di una quantità prelevata da questo lotto. Il
                            lotto è già nel contesto (selectedLot) → lo stock_out
                            riporta il batch_lot e la giacenza si aggiorna subito. */
                        }
                        <IonButton
                          size="small"
                          color="primary"
                          disabled={!stockReliable || stockCommand.isPending ||
                            available <= 0}
                          onClick={() => {
                            setLotDischargeInput("");
                            setLotDischargeReason("consumed");
                            setLotActionModal("discharge");
                          }}
                        >
                          <IonIcon slot="start" icon={removeCircleOutline} />
                          Scarica
                        </IonButton>
                        <IonButton
                          size="small"
                          color="success"
                          disabled={!stockReliable || stockCommand.isPending ||
                            available <= 0 ||
                            Math.abs(available - onHand) > 1e-9}
                          onClick={() =>
                            setLotConfirmDischarge({
                              kind: "consumed",
                              qty: onHand,
                              uom,
                            })}
                        >
                          <IonIcon slot="start" icon={checkmarkCircleOutline} />
                          Finito
                        </IonButton>
                        <IonButton
                          size="small"
                          color="danger"
                          disabled={!stockReliable || stockCommand.isPending ||
                            available <= 0 ||
                            Math.abs(available - onHand) > 1e-9}
                          onClick={() =>
                            setLotConfirmDischarge({
                              kind: "waste",
                              qty: onHand,
                              uom,
                            })}
                        >
                          <IonIcon slot="start" icon={trashOutline} />
                          Scartato
                        </IonButton>
                        <IonButton
                          size="small"
                          color="primary"
                          fill="outline"
                          disabled={archived || !stockReliable ||
                            stockCommand.isPending}
                          onClick={() => {
                            setLotQtyInput(String(onHand));
                            setLotActionModal("qty");
                          }}
                        >
                          <IonIcon slot="start" icon={createOutline} />
                          Rettifica qtà
                        </IonButton>
                        <IonButton
                          size="small"
                          color="tertiary"
                          fill="outline"
                          disabled={archived || !stockReliable ||
                            onHand <= 0 || stockCommand.isPending}
                          onClick={() => {
                            setLotDateInput(expires ?? "");
                            setLotActionModal("date");
                          }}
                        >
                          <IonIcon slot="start" icon={calendarOutline} />
                          {expires ? "Correggi data" : "Aggiungi scadenza"}
                        </IonButton>
                        <IonButton
                          size="small"
                          color="warning"
                          fill="outline"
                          aria-label={stockReliable
                            ? `Correggi unità per ${food?.name ?? "prodotto"}`
                            : `Riconcilia unità e giacenza per ${
                              food?.name ?? "prodotto"
                            }`}
                          disabled={archived || !canOpenUnitFix ||
                            stockCommand.isPending}
                          onClick={() => openUnitFix(selectedLot.food_id)}
                        >
                          <IonIcon slot="start" icon={createOutline} />
                          {stockReliable
                            ? "Correggi unità"
                            : "Riconcilia unità e giacenza"}
                        </IonButton>
                        {stockReliable && (
                          <IonButton
                            size="small"
                            color="secondary"
                            fill="outline"
                            disabled={archived ||
                              stockReconciliation.isPending}
                            onClick={() =>
                              openStockReassignment(selectedLot.food_id)}
                          >
                            Sposta sul prodotto corretto
                          </IonButton>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        {/* Alert conferma scarico totale lotto */}
        <IonAlert
          isOpen={!!lotConfirmDischarge}
          header={lotConfirmDischarge?.kind === "consumed"
            ? "Lotto consumato"
            : "Lotto scartato"}
          message={lotConfirmDischarge
            ? `Registra ${
              lotConfirmDischarge.qty.toFixed(2)
            } ${lotConfirmDischarge.uom} come ${
              lotConfirmDischarge.kind === "consumed" ? "consumato" : "scarto"
            }?`
            : ""}
          buttons={[
            {
              text: "Annulla",
              role: "cancel",
              handler: () => setLotConfirmDischarge(null),
            },
            {
              text: "Conferma",
              handler: () => {
                if (lotConfirmDischarge) {void handleLotDischarge(
                    lotConfirmDischarge.kind,
                    lotConfirmDischarge.qty,
                    lotConfirmDischarge.uom,
                  );}
              },
            },
          ]}
          onDidDismiss={() => setLotConfirmDischarge(null)}
        />

        {/* Modale scarico parziale lotto (merce prelevata dal magazzino) */}
        <IonModal
          isOpen={lotActionModal === "discharge"}
          onDidDismiss={() => {
            setLotActionModal(null);
            setLotDischargeInput("");
            setLotDischargeReason("consumed");
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Scarica dal lotto</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setLotActionModal(null)}>
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedLot && (() => {
              const f = foods.find((x) => x.id === selectedLot.food_id);
              const uom = f?.default_uom ?? "pz";
              const remainingD = currentLotAvailableQty(
                selectedLot.food_id,
                selectedLot.lot,
              );
              return (
                <>
                  <p style={{ marginBottom: 8 }}>
                    <strong>{f?.name}</strong>
                    {selectedLot.lot ? ` — lotto ${selectedLot.lot}` : ""}
                  </p>
                  <p
                    style={{
                      color: "var(--ion-color-medium)",
                      marginBottom: 16,
                    }}
                  >
                    Giacenza attuale: {remainingD.toFixed(2)} {uom}
                  </p>
                  <IonItem>
                    <IonLabel position="stacked">
                      Quantità da scaricare ({uom})
                    </IonLabel>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      value={lotDischargeInput}
                      onIonInput={(e) =>
                        setLotDischargeInput(e.detail.value ?? "")}
                      placeholder="0"
                    />
                  </IonItem>
                  <IonSegment
                    value={lotDischargeReason}
                    onIonChange={(e) =>
                      setLotDischargeReason(
                        (e.detail.value as "consumed" | "waste") ?? "consumed",
                      )}
                    style={{ marginTop: 16 }}
                  >
                    <IonSegmentButton value="consumed">
                      Utilizzato
                    </IonSegmentButton>
                    <IonSegmentButton value="waste">Scarto</IonSegmentButton>
                  </IonSegment>
                  <IonButton
                    expand="block"
                    style={{ marginTop: 24 }}
                    color={lotDischargeReason === "waste"
                      ? "danger"
                      : "primary"}
                    onClick={() =>
                      void handleLotPartialDischarge(remainingD, uom)}
                    disabled={stockCommand.isPending}
                  >
                    Scarica {lotDischargeInput || "0"} {uom}
                  </IonButton>
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        {/* Modale rettifica quantità lotto */}
        <IonModal
          isOpen={lotActionModal === "qty"}
          onDidDismiss={() => {
            setLotActionModal(null);
            setLotQtyInput("");
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Rettifica quantità</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setLotActionModal(null)}>
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedLot && (() => {
              const f = foods.find((x) => x.id === selectedLot.food_id);
              const uom = f?.default_uom ?? "pz";
              const remaining2 = currentLotOnHandQty(
                selectedLot.food_id,
                selectedLot.lot,
              );
              return (
                <>
                  <p style={{ marginBottom: 8 }}>
                    <strong>{f?.name}</strong>
                    {selectedLot.lot ? ` — lotto ${selectedLot.lot}` : ""}
                  </p>
                  <p
                    style={{
                      color: "var(--ion-color-medium)",
                      marginBottom: 16,
                    }}
                  >
                    Giacenza attuale: {remaining2.toFixed(2)} {uom}
                  </p>
                  <IonItem>
                    <IonLabel position="stacked">
                      Nuova quantità ({uom})
                    </IonLabel>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      value={lotQtyInput}
                      onIonInput={(e) => setLotQtyInput(e.detail.value ?? "")}
                      placeholder="0"
                    />
                  </IonItem>
                  <p
                    style={{
                      color: "var(--ion-color-medium)",
                      fontSize: 13,
                      marginTop: 8,
                    }}
                  >
                    Puoi indicare una quantità inferiore o superiore: in
                    inventario il sistema registra la differenza (calo o
                    eccedenza) sul lotto.
                  </p>
                  <LotCorrectionFields reason={correctionReason} copies={correctionCopies}
                    onReason={setCorrectionReason} onCopies={setCorrectionCopies} />
                  <IonButton
                    expand="block"
                    style={{ marginTop: 24 }}
                    onClick={() => void handleLotQtyAdjust(remaining2, uom)}
                    disabled={stockCommand.isPending || !validCorrection(correctionReason, correctionCopies)}
                  >
                    {Number(correctionCopies) > 0 ? `Salva e ristampa ${correctionCopies} etichette` : "Salva rettifica"}
                  </IonButton>
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        {/* Modale correzione data scadenza lotto */}
        <IonModal
          isOpen={lotActionModal === "date"}
          onDidDismiss={() => {
            setLotActionModal(null);
            setLotDateInput("");
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Correggi data scadenza</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setLotActionModal(null)}>
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedLot && (() => {
              const f = foods.find((x) => x.id === selectedLot.food_id);
              const uom = f?.default_uom ?? "pz";
              const remaining3 = currentLotOnHandQty(
                selectedLot.food_id,
                selectedLot.lot,
              );
              const currentExpires = stockBalanceByLot.get(
                warehouseLotKey(selectedLot.food_id, selectedLot.lot),
              )?.expirationDate ?? "";
              return (
                <>
                  <p style={{ marginBottom: 8 }}>
                    <strong>{f?.name}</strong>
                    {selectedLot.lot ? ` — lotto ${selectedLot.lot}` : ""}
                  </p>
                  <p
                    style={{
                      color: "var(--ion-color-medium)",
                      marginBottom: 16,
                    }}
                  >
                    Data attuale: {currentExpires
                      ? new Date(currentExpires).toLocaleDateString("it-IT")
                      : "—"}
                  </p>
                  <IonItem>
                    <IonLabel position="stacked">Nuova data scadenza</IonLabel>
                    <IonInput
                      type="date"
                      value={lotDateInput}
                      onIonInput={(e) => setLotDateInput(e.detail.value ?? "")}
                    />
                  </IonItem>
                  <p
                    style={{
                      color: "var(--ion-color-medium)",
                      fontSize: "0.85em",
                      marginTop: 12,
                    }}
                  >
                    Registra la nuova data come correzione append-only del
                    lotto.
                  </p>
                  <LotCorrectionFields reason={correctionReason} copies={correctionCopies}
                    onReason={setCorrectionReason} onCopies={setCorrectionCopies} />
                  <IonButton
                    expand="block"
                    style={{ marginTop: 24 }}
                    onClick={() =>
                      void handleLotDateCorrection(remaining3, uom)}
                    disabled={stockCommand.isPending || !lotDateInput || !validCorrection(correctionReason, correctionCopies)}
                  >
                    {Number(correctionCopies) > 0 ? `Correggi e ristampa ${correctionCopies} etichette` : "Conferma correzione"}
                  </IonButton>
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={uomReconciliationOpen}
          onDidDismiss={() => setUomReconciliationOpen(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Problemi da correggere</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setUomReconciliationOpen(false)}>
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {(() => {
              const issues = new Map<string, {
                foodId: string;
                name: string;
                count: number;
                uoms: Set<string>;
                lots: Set<string>;
              }>();
              for (const { event } of stockProjection.quarantine) {
                const foodId = event.payload?.food_id;
                if (typeof foodId !== "string" || !foodId) continue;
                const current = issues.get(foodId) ?? {
                  foodId,
                  name: foods.find((food) => food.id === foodId)?.name ?? "Prodotto non disponibile",
                  count: 0,
                  uoms: new Set<string>(),
                  lots: new Set<string>(),
                };
                current.count += 1;
                current.uoms.add(
                  String(event.payload?.stock_uom ?? event.payload?.uom ?? "UdM mancante"),
                );
                const lot = event.payload?.batch_lot;
                if (typeof lot === "string" && lot.trim()) current.lots.add(lot.trim());
                issues.set(foodId, current);
              }
              const rows = [...issues.values()].sort((left, right) => left.name.localeCompare(right.name));
              return rows.length === 0 ? (
                <p>Nessun problema di UdM da correggere.</p>
              ) : (
                <>
                  <p style={{ color: "var(--ion-color-medium)", fontSize: "0.9em" }}>
                    Le azioni ordinarie restano bloccate finché la giacenza non è coerente. Qui trovi il percorso di correzione: non modifica mai gli eventi esistenti, ma registra una riconciliazione verificabile.
                  </p>
                  <IonList inset>
                    {rows.map((issue) => {
                      const canRebase = uomRebaseQuarantineBlock(issue.foodId) == null;
                      const food = foods.find((candidate) => candidate.id === issue.foodId);
                      const currentUom = food?.default_uom?.trim().toLowerCase() ?? "";
                      const quarantinedUoms = [...issue.uoms]
                        .map((uom) => uom.trim().toLowerCase())
                        .filter((uom) => uom && uom !== "udm mancante");
                      const suggestedUom = quarantinedUoms.length === 1
                        ? quarantinedUoms[0]
                        : null;
                      const verifiedBalances = unitFixBalances(issue.foodId);
                      const canConfirmVerified = !food?.archived_at &&
                        verifiedBalances.length > 0 &&
                        verifiedBalances.every(
                          (balance) =>
                            balance.onHandQty >= -1e-9 &&
                            balance.reservedQty <= 1e-9,
                        );
                      return (
                        <IonItem key={issue.foodId} className="ion-text-wrap">
                          <IonLabel>
                            <h3>{issue.name}</h3>
                            <p>
                              {issue.count} moviment{issue.count === 1 ? "o" : "i"} da verificare · UdM: {[...issue.uoms].join(", ")}
                            </p>
                            {issue.lots.size > 0 && (
                              <p>Lotto: {[...issue.lots].join(" · ")}</p>
                            )}
                            {!canRebase && (
                              <p style={{ color: "var(--ion-color-warning-shade)" }}>
                                Non riconciliabile automaticamente: contiene scarichi, prenotazioni o più UdM incompatibili.
                              </p>
                            )}
                            {canConfirmVerified && (
                              <IonButton
                                size="small"
                                color="warning"
                                fill="outline"
                                style={{ marginTop: 8, textTransform: "none" }}
                                disabled={stockReconciliation.isPending}
                                onClick={() => {
                                  setUomReconciliationOpen(false);
                                  openPhysicalRecount(
                                    issue.foodId,
                                    "verified_projection",
                                  );
                                }}
                              >
                                Conferma saldo attuale in {currentUom}
                              </IonButton>
                            )}
                            {canRebase && suggestedUom &&
                              suggestedUom !== currentUom && (
                              <IonButton
                                size="small"
                                color="warning"
                                fill="outline"
                                style={{ marginTop: 8, textTransform: "none" }}
                                onClick={() => {
                                  setUomReconciliationOpen(false);
                                  openUnitFix(issue.foodId, suggestedUom);
                                }}
                              >
                                Correggi anagrafica in {suggestedUom}
                              </IonButton>
                            )}
                            {(!canRebase || !canConfirmVerified) && (
                              <IonButton
                                size="small"
                                color="warning"
                                fill="outline"
                                style={{ marginTop: 8, textTransform: "none" }}
                                disabled={stockReconciliation.isPending}
                                onClick={() => {
                                  setUomReconciliationOpen(false);
                                  openPhysicalRecount(issue.foodId, "manual");
                                }}
                              >
                                Riconteggio fisico guidato
                              </IonButton>
                            )}
                          </IonLabel>
                        </IonItem>
                      );
                    })}
                  </IonList>
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={!!stockReassignment}
          onDidDismiss={() => {
            setStockReassignment(null);
            setStockReassignmentQuery("");
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {stockReassignment?.rows.length === 0
                  ? "Unisci articoli"
                  : "Unisci articoli e giacenza"}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  disabled={stockReconciliation.isPending}
                  aria-label="Chiudi"
                  onClick={() => setStockReassignment(null)}
                >
                  <IonIcon icon={closeOutline} slot="icon-only" />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent
            style={{ "--background": "#eef2f7" } as React.CSSProperties}
          >
            {stockReassignment && (() => {
              const totalQty = stockReassignment.rows.reduce(
                (sum, row) => sum + row.qty,
                0,
              );
              const valued = stockReassignment.rows.every(
                (row) => row.value !== null && row.currency === "EUR",
              );
              const totalValue = valued
                ? stockReassignment.rows.reduce(
                  (sum, row) => sum + (row.value ?? 0),
                  0,
                )
                : null;
              const target = foods.find(
                (food) => food.id === stockReassignment.targetFoodId,
              );
              const query = normalizeFoodSearch(stockReassignmentQuery);
              const availableTargets = foods
                .filter((food) =>
                  !food.archived_at &&
                  food.id !== stockReassignment.sourceFoodId &&
                  food.default_uom.trim().toLowerCase() ===
                    stockReassignment.sourceUom.trim().toLowerCase()
                )
                .slice()
                .sort((left, right) => left.name.localeCompare(right.name, "it"));
              const matchingTargets = query
                ? availableTargets.filter((food) =>
                  normalizeFoodSearch([
                    food.name,
                    food.category ?? "",
                    food.storage_type ?? "",
                    food.default_uom,
                  ].join(" ")).includes(query)
                )
                : availableTargets;
              const visibleTargets = matchingTargets.slice(0, 80);
              return (
                <div style={{ padding: "12px 12px 28px" }}>
                  <section
                    aria-label="Giacenza da spostare"
                    style={{
                      background: "#fff",
                      borderRadius: 14,
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(15,23,42,.08)",
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".03em" }}>
                      {stockReassignment.rows.length === 0
                        ? "Articolo da archiviare"
                        : "Articolo da correggere"}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 750, color: "#0f172a", marginTop: 4 }}>
                      {stockReassignment.sourceFoodName}
                    </div>
                    {stockReassignment.rows.length === 0 ? (
                      <div style={{ color: "#475569", fontSize: 13, lineHeight: 1.45, marginTop: 6 }}>
                        <strong>
                          {stockReassignment.supplierMatchCount === null
                            ? "Collegamenti fornitore da verificare"
                            : `${stockReassignment.supplierMatchCount} collegament${stockReassignment.supplierMatchCount === 1 ? "o" : "i"} fornitore`}
                        </strong>
                        {" · "}nessuna giacenza da spostare
                      </div>
                    ) : (
                      <div style={{ color: "#475569", fontSize: 13, lineHeight: 1.45, marginTop: 6 }}>
                        {stockReassignment.rows.length} lott{stockReassignment.rows.length === 1 ? "o" : "i"} ·{" "}
                        <strong>{totalQty.toLocaleString("it-IT", { maximumFractionDigits: 3 })} {stockReassignment.sourceUom}</strong>
                        {totalValue !== null
                          ? ` · € ${totalValue.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : " · valore non verificato"}
                      </div>
                    )}
                    <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.4, marginTop: 5 }}>
                      {stockReassignment.rows.length === 0
                        ? "Le descrizioni e i codici del fornitore verranno associati all’alimento scelto."
                        : "Lotti, scadenze, documenti e fornitore resteranno collegati."}
                    </div>
                  </section>

                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      background: "#eef2f7",
                      padding: "2px 0 8px",
                    }}
                  >
                    <IonSearchbar
                      value={stockReassignmentQuery}
                      debounce={80}
                      showClearButton="focus"
                      placeholder="Cerca il prodotto corretto…"
                      aria-label="Cerca il prodotto corretto"
                      onIonInput={(event) =>
                        setStockReassignmentQuery(String(event.detail.value ?? ""))}
                      style={{
                        "--background": "#fff",
                        "--border-radius": "12px",
                        "--box-shadow": "0 1px 3px rgba(15,23,42,.08)",
                        paddingTop: 0,
                        paddingBottom: 0,
                      } as React.CSSProperties}
                    />
                    <div style={{ padding: "5px 6px 0", fontSize: 12, color: "#64748b" }}>
                      {matchingTargets.length} articol{matchingTargets.length === 1 ? "o compatibile" : "i compatibili"} · stessa unità {stockReassignment.sourceUom}
                    </div>
                  </div>

                  {availableTargets.length === 0 ? (
                    <div style={{ background: "#fff", borderRadius: 14, padding: "26px 18px", color: "#64748b", textAlign: "center", fontSize: 14 }}>
                      Nessun altro articolo attivo usa l’unità {stockReassignment.sourceUom}.
                    </div>
                  ) : visibleTargets.length === 0 ? (
                    <div style={{ background: "#fff", borderRadius: 14, padding: "26px 18px", color: "#64748b", textAlign: "center", fontSize: 14 }}>
                      Nessun articolo trovato per “{stockReassignmentQuery.trim()}”.
                    </div>
                  ) : (
                    <div style={{ background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,.08)" }}>
                      {visibleTargets.map((food, index) => {
                        const isSelected = food.id === stockReassignment.targetFoodId;
                        const recipesCount = recipesPerFood.get(food.id) ?? 0;
                        const targetQty = stockByFood.get(food.id) ?? 0;
                        const storage = food.storage_type?.trim();
                        return (
                          <button
                            key={food.id}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() =>
                              setStockReassignment((current) =>
                                current ? { ...current, targetFoodId: food.id } : current)}
                            style={{
                              width: "100%",
                              minHeight: 64,
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 14px",
                              textAlign: "left",
                              background: isSelected ? "#eff6ff" : "#fff",
                              border: "none",
                              borderTop: index === 0 ? "none" : "1px solid #e2e8f0",
                              borderLeft: `4px solid ${isSelected ? "#2563eb" : STORAGE_BAR_COLOR[storage ?? ""] ?? "#cbd5e1"}`,
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: "#0f172a", fontSize: 15, fontWeight: 700, lineHeight: 1.3, overflowWrap: "anywhere" }}>
                                {food.name}
                              </div>
                              <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>
                                {[food.category, storage].filter(Boolean).join(" · ") || "Categoria e conservazione non indicate"}
                                {` · ${recipesCount} ${recipesCount === 1 ? "ricetta" : "ricette"}`}
                              </div>
                            </div>
                            <div style={{ flexShrink: 0, textAlign: "right" }}>
                              <div style={{ color: targetQty > 0 ? "#15803d" : "#64748b", fontSize: 12, fontWeight: 700 }}>
                                {targetQty.toLocaleString("it-IT", { maximumFractionDigits: 3 })} {food.default_uom}
                              </div>
                              <div style={{ color: "#94a3b8", fontSize: 10, marginTop: 2 }}>in giacenza</div>
                            </div>
                            <IonIcon
                              icon={isSelected ? checkmarkCircleOutline : chevronForward}
                              aria-hidden="true"
                              style={{ fontSize: isSelected ? 24 : 18, color: isSelected ? "#2563eb" : "#cbd5e1", flexShrink: 0 }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {matchingTargets.length > visibleTargets.length && (
                    <div style={{ textAlign: "center", color: "#64748b", fontSize: 12, marginTop: 10 }}>
                      +{matchingTargets.length - visibleTargets.length} altri · affina la ricerca
                    </div>
                  )}

                  <section style={{ background: "#fff", borderRadius: 14, marginTop: 12, padding: "4px 12px 14px", boxShadow: "0 1px 3px rgba(15,23,42,.08)" }}>
                    {target && (
                      <div style={{ color: "#166534", fontSize: 13, lineHeight: 1.4, padding: "12px 4px 4px" }}>
                        Selezionato: <strong>{target.name}</strong>
                      </div>
                    )}
                    <div style={{ color: "#166534", fontSize: 12, lineHeight: 1.45, padding: "10px 4px" }}>
                      La conferma rende “{target?.name ?? "l’articolo scelto"}” l’articolo unico: sposta i match fornitore, aggiorna {stockReassignment.sourceRecipeCount} ricett{stockReassignment.sourceRecipeCount === 1 ? "a" : "e"}, trasferisce l’eventuale giacenza e archivia il duplicato.
                    </div>
                    <div style={{ color: "#92400e", fontSize: 12, lineHeight: 1.45, padding: "2px 4px 10px" }}>
                      {stockReassignment.rows.length === 0
                        ? "Il server ricontrolla saldo e versione. Se qualcosa è cambiato, non sposta né archivia nulla."
                        : "Prima della conferma il server ricontrolla saldo, valore e versioni. Se qualcosa è cambiato, non sposta nulla."}
                    </div>
                    <IonButton
                      expand="block"
                      disabled={!stockReassignment.targetFoodId || stockReconciliation.isPending}
                      onClick={() => void submitStockReassignment()}
                    >
                      {stockReconciliation.isPending
                        ? <><IonSpinner name="crescent" /> Verifica e salvataggio…</>
                        : target
                        ? stockReassignment.rows.length === 0
                          ? `Sposta e archivia su ${target.name}`
                          : `Sposta su ${target.name}`
                        : "Seleziona un articolo"}
                    </IonButton>
                  </section>
                </div>
              );
            })()}
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={!!physicalRecount}
          onDidDismiss={() => setPhysicalRecount(null)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {physicalRecount?.confirmationMode === "verified_projection"
                  ? "Conferma giacenza verificata"
                  : "Riconteggio fisico guidato"}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  disabled={stockReconciliation.isPending}
                  onClick={() => setPhysicalRecount(null)}
                >
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {physicalRecount && (
              <>
                <p><strong>{physicalRecount.foodName}</strong></p>
                <p
                  style={{
                    color: "var(--ion-color-medium)",
                    fontSize: "0.88em",
                  }}
                >
                  {physicalRecount.confirmationMode === "verified_projection"
                    ? (
                      <>
                        Le quantità proposte sono il saldo attualmente
                        verificabile. Confermandole invii una nuova fotografia
                        append-only al server: gli eventi incoerenti restano
                        nell’audit, ma non concorrono più alla giacenza.
                      </>
                    )
                    : (
                      <>
                        Inserisci ciò che è realmente presente adesso. Questa
                        fotografia diventa il nuovo saldo verificabile; gli
                        eventi precedenti restano nell’audit ma non concorrono
                        più alla giacenza.
                      </>
                    )}
                </p>
                {physicalRecount.archived && (
                  <IonItem color="warning" lines="none">
                    <IonLabel className="ion-text-wrap">
                      Prodotto archiviato: conferma 0 per ogni lotto. Il server
                      registrerà il cutover e manterrà l’anagrafica archiviata.
                    </IonLabel>
                  </IonItem>
                )}
                <IonItem>
                  <UomSelect
                    label="Unità corretta"
                    value={physicalRecount.targetUom}
                    disabled={physicalRecount.archived}
                    onChange={(targetUom) =>
                      setPhysicalRecount((current) =>
                        current
                          ? {
                            ...current,
                            targetUom,
                            confirmationMode:
                              targetUom.trim().toLowerCase() ===
                                  current.targetUom.trim().toLowerCase()
                                ? current.confirmationMode
                                : "manual",
                            rows: targetUom.trim().toLowerCase() ===
                                current.targetUom.trim().toLowerCase()
                              ? current.rows
                              : current.rows.map((row) => ({
                                ...row,
                                qty: "",
                                value: null,
                                currency: null,
                              })),
                          }
                          : current)}
                  />
                </IonItem>
                <IonList inset>
                  {physicalRecount.rows.map((row, index) => (
                    <IonItem key={row.key}>
                      <IonInput
                        label={row.lot
                          ? `Lotto ${row.lot}`
                          : "Senza numero di lotto"}
                        labelPlacement="stacked"
                        type="number"
                        min="0"
                        inputMode="decimal"
                        value={row.qty}
                        placeholder="0"
                        onIonInput={(event) =>
                          setPhysicalRecount((current) =>
                            current
                              ? {
                                ...current,
                                rows: current.rows.map((candidate, rowIndex) =>
                                  rowIndex === index
                                    ? {
                                      ...candidate,
                                      qty: event.detail.value ?? "",
                                      value: null,
                                      currency: null,
                                    }
                                    : candidate),
                              }
                              : current)}
                      />
                      <IonNote slot="end">
                        {physicalRecount.targetUom}
                      </IonNote>
                    </IonItem>
                  ))}
                </IonList>
                <p
                  style={{
                    color: "var(--ion-color-warning-shade)",
                    fontSize: "0.82em",
                  }}
                >
                  Il valore contabile viene conservato solo per quantità
                  proposte e non modificate. Se correggi manualmente una
                  quantità, il costo non dimostrabile non viene inventato e
                  quel saldo resterà non valorizzato finché un nuovo acquisto
                  o una correzione documentata fornirà il valore.
                  Le spedizioni in bozza devono essere chiuse o liberate prima.
                </p>
                <IonButton
                  expand="block"
                  style={{ marginTop: 20 }}
                  disabled={stockReconciliation.isPending ||
                    physicalRecount.rows.some((row) => row.qty.trim() === "")}
                  onClick={() => void submitPhysicalRecount()}
                >
                  {stockReconciliation.isPending
                    ? <><IonSpinner name="crescent" /> Verifica e salvataggio…</>
                    : physicalRecount.confirmationMode === "verified_projection"
                    ? "Conferma saldo e chiudi lo storico incoerente"
                    : "Conferma fotografia fisica"}
                </IonButton>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonModal isOpen={!!unitFix} onDidDismiss={() => setUnitFix(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Correggi unità di misura</IonTitle>
              <IonButtons slot="end">
                <IonButton
                  disabled={unitFixBusy}
                  onClick={() => setUnitFix(null)}
                >
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {unitFix && (() => {
              const automatic = convertQty(1, unitFix.oldUom, unitFix.newUom);
              const quarantinedLoads = stockProjection.quarantine.filter(
                ({ event }) => event.kind === "stock_in" &&
                  event.payload?.food_id === unitFix.foodId,
              ).length;
              const canAbsorbQuarantinedLoads = quarantinedLoads > 0 &&
                rebaseQuarantineBlock({
                  events: stockProjection.quarantine,
                  foodId: unitFix.foodId,
                  targetUom: unitFix.newUom,
                }) == null;
              const knownValue = unitFix.rows.every(
                (row) => row.onHandValue != null && row.currency === "EUR",
              );
              const totalValue = knownValue
                ? unitFix.rows.reduce(
                  (sum, row) => sum + (row.onHandValue ?? 0),
                  0,
                )
                : null;
              return (
                <>
                  <p style={{ marginBottom: 6 }}>
                    <strong>{unitFix.foodName}</strong>
                  </p>
                  <p
                    style={{
                      color: "var(--ion-color-medium)",
                      fontSize: "0.85em",
                      marginBottom: 16,
                    }}
                  >
                    Unità attuale: <b>{unitFix.oldUom}</b>.{" "}
                    {unitFix.rows.length > 0
                      ? "La conversione riguarda tutti i lotti aperti e viene salvata come un’unica operazione atomica: quantità, valore della giacenza e anagrafica cambiano insieme."
                      : "Il saldo fisico è zero: il cutover aggiorna atomicamente l’anagrafica e l’unità dei prossimi movimenti, senza riscrivere lo storico."}
                  </p>
                  <IonItem>
                    <UomSelect
                      label="Nuova unità"
                      value={unitFix.newUom}
                      onChange={changeUnitFixUom}
                    />
                  </IonItem>
                  {unitFix.rows.length > 0 &&
                    unitFix.newUom.toLowerCase() !==
                      unitFix.oldUom.toLowerCase() &&
                    automatic == null && (
                    <p
                      style={{
                        color: "var(--ion-color-warning-shade)",
                        fontSize: "0.82em",
                        margin: "10px 2px 0",
                      }}
                    >
                      ⚠️ {unitFix.oldUom} → {unitFix.newUom}{" "}
                      richiede una conferma manuale: inserisci la quantità
                      fisica reale per ogni lotto.
                    </p>
                  )}
                  {canAbsorbQuarantinedLoads && (
                    <p
                      style={{
                        color: "var(--ion-color-success-shade)",
                        fontSize: "0.82em",
                        margin: "10px 2px 0",
                      }}
                    >
                      ✓ {quarantinedLoads} carico{quarantinedLoads === 1 ? "" : "i"} storico{quarantinedLoads === 1 ? "" : "i"} già in {unitFix.newUom} verr{quarantinedLoads === 1 ? "à" : "anno"} riallineato{quarantinedLoads === 1 ? "" : "i"} senza riscrivere lo storico.
                    </p>
                  )}
                  {unitFix.rows.length === 0
                    ? (
                      <p
                        style={{
                          color: "var(--ion-color-medium)",
                          fontSize: "0.82em",
                          margin: "10px 2px 0",
                        }}
                      >
                        Nessuna quantità o valore contabile da riproporzionare.
                      </p>
                    )
                    : totalValue != null
                    ? (
                      <p
                        style={{
                          color: "var(--ion-color-medium)",
                          fontSize: "0.82em",
                          margin: "10px 2px 0",
                        }}
                      >
                        Valore contabile preservato: €{" "}
                        {totalValue.toFixed(2)}. Il costo unitario sarà
                        riproporzionato nella nuova UdM.
                      </p>
                    )
                    : (
                      <p
                        style={{
                          color: "var(--ion-color-warning-shade)",
                          fontSize: "0.82em",
                          margin: "10px 2px 0",
                        }}
                      >
                        Una parte della giacenza non ha un costo verificabile:
                        resterà esplicitamente non valorizzata, senza inventare
                        un prezzo.
                      </p>
                    )}
                  {unitFix.rows.length > 0 && (
                    <p
                      style={{
                        color: "var(--ion-color-medium)",
                        fontSize: "0.85em",
                        margin: "16px 2px 4px",
                      }}
                    >
                      Quantità nella nuova unità{unitFix.rows.length > 1
                        ? ` (${unitFix.rows.length} lotti)`
                        : ""}:
                    </p>
                  )}
                  {unitFix.rows.map((row, index) => (
                    <IonItem key={row.key}>
                      <IonLabel>
                        Lotto {row.lot ?? "senza numero"}
                        <p style={{ fontSize: "0.8em" }}>
                          era {row.oldQty.toFixed(3)} {unitFix.oldUom}
                          {row.averageUnitCost != null && row.currency
                            ? ` · ${row.currency} ${
                              formatMoney(row.averageUnitCost)
                            }/${unitFix.oldUom}`
                            : " · costo non disponibile"}
                        </p>
                      </IonLabel>
                      <IonInput
                        slot="end"
                        type="number"
                        inputmode="decimal"
                        disabled={automatic != null}
                        style={{
                          maxWidth: 120,
                          textAlign: "right",
                        } as React.CSSProperties}
                        value={row.newQty}
                        onIonInput={(event) => {
                          const value = String(event.detail.value ?? "");
                          setUnitFix((current) =>
                            current
                              ? {
                                ...current,
                                rows: current.rows.map((
                                  candidate,
                                  candidateIndex,
                                ) => (
                                  candidateIndex === index
                                    ? { ...candidate, newQty: value }
                                    : candidate
                                )),
                              }
                              : current
                          );
                        }}
                      />
                      <IonNote slot="end">{unitFix.newUom}</IonNote>
                    </IonItem>
                  ))}
                  <IonButton
                    expand="block"
                    style={{ marginTop: 24 }}
                    disabled={unitFixBusy ||
                      stockCommand.isPending ||
                      !unitFix.newUom.trim() ||
                      unitFix.newUom.trim().toLowerCase() ===
                        unitFix.oldUom.trim().toLowerCase()}
                    onClick={() => void submitUnitFix()}
                  >
                    {unitFixBusy
                      ? "Conversione…"
                      : unitFix.rows.length > 0
                      ? "Converti giacenza"
                      : "Aggiorna unità"}
                  </IonButton>
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={legacyReconciliationOpen}
          onDidDismiss={() => setLegacyReconciliationOpen(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Movimenti da verificare</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setLegacyReconciliationOpen(false)}>
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p style={{ color: "var(--ion-color-medium)", fontSize: 13 }}>
              Queste operazioni non hanno superato i controlli del server e
              sono state separate dalla coda: tutto il resto continua a
              sincronizzarsi. La giacenza locale provvisoria è stata rimossa
              per non mostrare quantità inesistenti. Verifica la quantità
              fisica e registra l’eventuale Carico, Scarico o Rettifica; solo
              dopo puoi segnare la voce risolta. La traccia resterà conservata.
            </p>
            <IonList>
              {legacyPermanentRejections.map((incident) => (
                <IonItem key={incident.command_id} color="light">
                  <IonLabel className="ion-text-wrap">
                    <h3>Comando precedente {incident.command_id.slice(0, 8)}</h3>
                    <p>{incident.reason ?? "Vincolo server non soddisfatto"}</p>
                    <p>
                      La vecchia versione non conservava il dettaglio: verifica
                      fisicamente prodotto, lotto e quantità prima di registrare
                      nuovamente il movimento.
                    </p>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    size="small"
                    fill="outline"
                    color="warning"
                    onClick={() => setLegacyPermanentResolveConfirm(incident)}
                  >
                    Verificato
                  </IonButton>
                </IonItem>
              ))}
              {stockCommandReconciliations.map((incident) => {
                const movementDetails = incident.events.map((event) => {
                  const payload = event.payload as Record<string, unknown>;
                  const foodId = typeof payload.food_id === "string"
                    ? payload.food_id
                    : "";
                  const foodName = foods.find((food) => food.id === foodId)?.name
                    ?? (foodId || "Prodotto non identificato");
                  const qty = Number(payload.stock_qty ?? payload.qty);
                  const uom = String(payload.stock_uom ?? payload.uom ?? "");
                  const lot = typeof payload.batch_lot === "string"
                    ? payload.batch_lot.trim()
                    : "";
                  return `${foodName}: ${Number.isFinite(qty) ? qty : "—"} ${uom}${lot ? ` · lotto ${lot}` : ""}`;
                });
                return (
                  <IonItem key={incident.command_id} color="light">
                    <IonLabel className="ion-text-wrap">
                      <h3>Comando {incident.command_id.slice(0, 8)}</h3>
                      <p>{incident.reason}</p>
                      {movementDetails.map((detail, index) => (
                        <p key={`${incident.command_id}-${index}`}>{detail}</p>
                      ))}
                      <p>{new Date(incident.created_at).toLocaleString("it-IT")}</p>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      fill="outline"
                      color="warning"
                      onClick={() => setStockCommandResolveConfirm(incident)}
                    >
                      Verificato
                    </IonButton>
                  </IonItem>
                );
              })}
              {legacyReconciliations.map((incident) => {
                const payload = incident.event.payload as Record<
                  string,
                  unknown
                >;
                const foodId = typeof payload.food_id === "string"
                  ? payload.food_id
                  : "";
                const foodNameValue = foods.find((food) =>
                  food.id === foodId
                )?.name ??
                  foodId ??
                  "Prodotto non identificato";
                const qtyValue = Number(payload.stock_qty ?? payload.qty);
                const uomValue = String(payload.stock_uom ?? payload.uom ?? "");
                const lotValue = typeof payload.batch_lot === "string"
                  ? payload.batch_lot.trim()
                  : "";
                const action = incident.event_kind === "stock_in"
                  ? "Carico"
                  : incident.event_kind === "stock_out"
                  ? "Scarico"
                  : "Prenotazione";
                return (
                  <IonItem key={incident.client_uuid}>
                    <IonLabel className="ion-text-wrap">
                      <h3>
                        {action} ·{" "}
                        {foodNameValue || "Prodotto non identificato"}
                      </h3>
                      <p>
                        {Number.isFinite(qtyValue) ? qtyValue : "—"} {uomValue}
                        {lotValue ? ` · lotto ${lotValue}` : ""}
                      </p>
                      <p>
                        {new Date(incident.event.client_ts).toLocaleString(
                          "it-IT",
                        )}
                      </p>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      fill="outline"
                      color="warning"
                      onClick={() => setLegacyResolveConfirm(incident)}
                    >
                      Verificato
                    </IonButton>
                  </IonItem>
                );
              })}
            </IonList>
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!legacyResolveConfirm}
          header="Conferma verifica fisica"
          message="Conferma solo se hai verificato la quantità reale e hai già registrato l’eventuale Carico, Scarico o Rettifica con i pulsanti attuali."
          buttons={[
            {
              text: "Annulla",
              role: "cancel",
              handler: () => setLegacyResolveConfirm(null),
            },
            {
              text: "Conferma",
              handler: () => {
                const incident = legacyResolveConfirm;
                setLegacyResolveConfirm(null);
                if (!incident || !profile?.active_tenant_id) return;
                void resolveLegacyStockReconciliation({
                  clientUuid: incident.client_uuid,
                  tenantId: profile.active_tenant_id,
                }).then(async () => {
                  await refreshLegacyReconciliations();
                  setToast("Movimento precedente archiviato come verificato");
                }).catch((error) => setToast(toUserMessage(error)));
              },
            },
          ]}
          onDidDismiss={() => setLegacyResolveConfirm(null)}
        />

        <IonAlert
          isOpen={!!stockCommandResolveConfirm}
          header="Conferma verifica fisica"
          message="Conferma solo se hai controllato la quantità reale e hai già registrato l’eventuale Carico, Scarico o Rettifica. Il comando originale resterà nello storico locale."
          buttons={[
            {
              text: "Annulla",
              role: "cancel",
              handler: () => setStockCommandResolveConfirm(null),
            },
            {
              text: "Conferma",
              handler: () => {
                const incident = stockCommandResolveConfirm;
                setStockCommandResolveConfirm(null);
                if (!incident || !profile?.active_tenant_id) return;
                void resolveStockCommandReconciliation({
                  commandId: incident.command_id,
                  tenantId: profile.active_tenant_id,
                }).then(async () => {
                  await refreshStockCommandReconciliations();
                  setToast("Operazione archiviata come verificata");
                }).catch((error) => setToast(toUserMessage(error)));
              },
            },
          ]}
          onDidDismiss={() => setStockCommandResolveConfirm(null)}
        />

        <IonAlert
          isOpen={!!legacyPermanentResolveConfirm}
          header="Conferma verifica fisica"
          message="Questa vecchia versione non ha conservato prodotto, lotto e quantità. Conferma solo dopo il controllo fisico e l’eventuale registrazione correttiva. L’incidente resterà tracciato come verificato."
          buttons={[
            {
              text: "Annulla",
              role: "cancel",
              handler: () => setLegacyPermanentResolveConfirm(null),
            },
            {
              text: "Conferma",
              handler: () => {
                const incident = legacyPermanentResolveConfirm;
                setLegacyPermanentResolveConfirm(null);
                if (!incident) return;
                void acknowledgeLegacyPermanentStockCommandRejection({
                  commandId: incident.command_id,
                  reason: incident.reason,
                }).then(async () => {
                  await refreshStockCommandReconciliations();
                  setToast("Incidente precedente archiviato come verificato");
                }).catch((error) => setToast(toUserMessage(error)));
              },
            },
          ]}
          onDidDismiss={() => setLegacyPermanentResolveConfirm(null)}
        />

        <IonAlert
          isOpen={!!confirmArchive}
          header="Archivia"
          message={confirmArchive
            ? confirmArchive.table === "foods"
              ? confirmArchive.supplierMatchCount === null
                ? `Archiviare "${confirmArchive.name}"? Non è stato possibile verificare i match fornitore. L’archiviazione scollegherà comunque gli eventuali collegamenti e richiede saldo e prenotazioni a zero.`
                : (confirmArchive.supplierMatchCount ?? 0) > 0
                ? `“${confirmArchive.name}” è collegato a ${confirmArchive.supplierMatchCount} articol${confirmArchive.supplierMatchCount === 1 ? "o" : "i"} fornitore. Puoi spostare i collegamenti su un altro alimento oppure archiviare e scollegarli. Saldo e prenotazioni devono essere a zero.`
                : `Archiviare "${confirmArchive.name}"? Non risultano match fornitore. L’operazione richiede saldo e prenotazioni a zero.`
              : `Archiviare "${confirmArchive.name}"?`
            : ""}
          buttons={[
            { text: "Annulla", role: "cancel" },
            ...(confirmArchive?.table === "foods" &&
                confirmArchive.supplierMatchCount !== 0
              ? [{
                text: "Sposta collegamenti",
                handler: () => {
                  const pending = confirmArchive;
                  setConfirmArchive(null);
                  openStockReassignment(
                    pending.id,
                    true,
                    pending.supplierMatchCount ?? null,
                  );
                },
              }]
              : []),
            {
              text: confirmArchive?.table === "foods" &&
                  confirmArchive.supplierMatchCount !== 0
                ? "Archivia e scollega"
                : "Archivia",
              role: "destructive",
              handler: () => {
                if (confirmArchive) {
                  void archiveItem(
                    confirmArchive.table,
                    confirmArchive.id,
                    confirmArchive.version,
                  );
                }
                setConfirmArchive(null);
              },
            },
          ]}
          onDidDismiss={() => setConfirmArchive(null)}
        />

        {/* Modal "Aggiungi lotto" — piano base (warehouse OFF). */}
        <IonModal isOpen={addLotOpen} onDidDismiss={() => setAddLotOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Aggiungi lotto</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setAddLotOpen(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonItem>
              <PickerTriggerField
                label="Prodotto *"
                placeholder="Tocca per cercare un prodotto"
                value={foods.find((f) => f.id === addLotForm.food_id)?.name}
                onClick={() => setFoodPicker("addLot")}
              />
            </IonItem>
            {
              /* T-146: "Aggiorna alimento". Il food in lista è spesso precaricato
                dal menù con conservazione/allergeni/categoria errati. Da qui si
                apre l'editor del MASTER food (FoodEditorModal) per correggerli
                senza uscire dal flusso "Aggiungi lotto": la modale food si apre
                sopra questa (stesso pattern di "+ Crea nuovo alimento" nello
                stock_in) e al salvataggio si torna qui col food invariato. */
            }
            {(() => {
              const selected = foods.find((f) => f.id === addLotForm.food_id);
              if (!selected) return null;
              return (
                <IonItem
                  button
                  detail={false}
                  onClick={() => openFoodEditor(selected)}
                  lines="none"
                >
                  <IonIcon icon={createOutline} slot="start" color="primary" />
                  <IonLabel color="primary" style={{ fontSize: 13 }}>
                    {t("warehouse.updateFood", "Aggiorna alimento")}
                    <p
                      style={{
                        color: "#94a3b8",
                        fontSize: 11,
                        margin: "2px 0 0",
                        fontWeight: 400,
                      }}
                    >
                      {t(
                        "warehouse.updateFoodHint",
                        "Correggi conservazione, allergeni o categoria",
                      )}
                    </p>
                  </IonLabel>
                </IonItem>
              );
            })()}
            <IonItem>
              {
                /* T-090 (cont.): seconda occorrenza del campo Lotto nella
                  scheda "Aggiungi lotto" (modale dedicata, separata dal
                  flow stock_in). Stesso label allineato + placeholder con
                  entrambe le forme accettate. */
              }
              <IonLabel position="stacked">Lotto / N° Fattura *</IonLabel>
              <IonInput
                value={addLotForm.batch_lot}
                placeholder="es. LT-2024-089 oppure FATT 2024/0156"
                onIonInput={(ev) => setAddLotForm((s) => ({
                  ...s,
                  batch_lot: String(ev.detail.value ?? ""),
                }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Scadenza</IonLabel>
              <IonInput
                type="date"
                value={addLotForm.expiration_date}
                onIonInput={(ev) => setAddLotForm((s) => ({
                  ...s,
                  expiration_date: String(ev.detail.value ?? ""),
                }))}
              />
            </IonItem>
            <IonItem>
              {
                /* T-136: riferimento fattura/documento OPZIONALE anche
                  nell'aggiunta lotto manuale (piano base). Persistito sul
                  carico (stock_in) per tenere il collegamento lotto→fattura. */
              }
              <IonLabel position="stacked">
                {t(
                  "warehouse.docRef.label",
                  "Riferimento fattura / N° documento",
                )}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                  ({t("common.optional", "opzionale")})
                </span>
              </IonLabel>
              <IonInput
                value={addLotForm.document_number}
                placeholder={t(
                  "warehouse.docRef.placeholder",
                  "es. FATT 2024/0156, DDT 887",
                )}
                onIonInput={(ev) => setAddLotForm((s) => ({
                  ...s,
                  document_number: String(ev.detail.value ?? ""),
                }))}
              />
            </IonItem>
            {/* T-136: collegamento OPZIONALE a un documento già caricato. */}
            {docOptions.length > 0 && (
              <IonItem>
                <IonLabel position="stacked">
                  {t("warehouse.docRef.link", "Collega documento")}
                  <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                    ({t("common.optional", "opzionale")})
                  </span>
                </IonLabel>
                <IonSelect
                  value={addLotDocId}
                  interface="action-sheet"
                  placeholder={t(
                    "warehouse.docRef.linkPlaceholder",
                    "Nessun documento collegato",
                  )}
                  onIonChange={(ev) => {
                    const id = String(ev.detail.value ?? "");
                    setAddLotDocId(id);
                    const doc = docOptions.find((o) => o.id === id);
                    if (doc?.number) {
                      setAddLotForm((s) => ({
                        ...s,
                        document_number: doc.number as string,
                      }));
                    }
                  }}
                >
                  <IonSelectOption value="">
                    {t("warehouse.docRef.linkNone", "Nessuno")}
                  </IonSelectOption>
                  {docOptions.map((o) => (
                    <IonSelectOption key={o.id} value={o.id}>
                      {o.label}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            )}
            <IonButton
              expand="block"
              style={{ marginTop: 16 }}
              disabled={!addLotForm.food_id || !addLotForm.batch_lot.trim()}
              onClick={() => {
                const f = foods.find((x) => x.id === addLotForm.food_id);
                if (!f) return;
                const lotTrim = addLotForm.batch_lot.trim();
                const exp = addLotForm.expiration_date || null;
                const choice = { food: f, lot: lotTrim, expiration_date: exp };
                // Se l'alimento non ha lotto attivo → aggiungi senza chiedere.
                if (!activeLotFor(f)) {
                  void commitManualLot("add", choice);
                } else {
                  setAddLotChoice(choice);
                }
              }}
            >
              <IonIcon icon={checkmarkCircleOutline} slot="start" />
              Conferma
            </IonButton>
          </IonContent>
        </IonModal>

        {/* ActionSheet: lotto già attivo presente, scegli sostituisci o aggiungi residuo. */}
        <IonActionSheet
          isOpen={!!addLotChoice && !!activeLotFor(addLotChoice.food)}
          onDidDismiss={() => setAddLotChoice(null)}
          header={addLotChoice
            ? `${addLotChoice.food.name}: lotto attivo "${
              activeLotFor(addLotChoice.food) ?? "—"
            }"`
            : ""}
          subHeader="Cosa vuoi fare col vecchio lotto?"
          buttons={[
            {
              text: "Sostituisci lotto attivo",
              role: "destructive" as const,
              handler: () => {
                void commitManualLot("replace");
              },
            },
            {
              text: "Aggiungi come nuovo (tieni il vecchio)",
              handler: () => {
                void commitManualLot("add");
              },
            },
            { text: "Annulla", role: "cancel" as const },
          ]}
        />

        {
          /* ActionSheet: tap su lotto in lista (set active / close /
            T-074: vedi documento originale). */
        }
        <IonActionSheet
          isOpen={!!lotMenuFor}
          onDidDismiss={() => setLotMenuFor(null)}
          header={lotMenuFor ? `Lotto ${lotMenuFor.lot}` : ""}
          // T-241: oltre al lotto, mostra fornitore + n. documento del carico
          // (dallo stock_in del lotto: supplier_id → nome, document_number).
          subHeader={(() => {
            if (!lotMenuFor) return undefined;
            const ev = (stockIn as StockEvent[]).find(
              (e) => e.payload.food_id === lotMenuFor.food.id &&
                (e.payload.batch_lot ?? null) === lotMenuFor.lot,
            );
            if (!ev) return undefined;
            const supName = ev.payload.supplier_id
              ? (suppliers.find((s) => s.id === ev.payload.supplier_id)?.name ??
                null)
              : null;
            const docNum = ev.payload.document_number ?? null;
            const parts: string[] = [];
            if (supName) parts.push(`Fornitore: ${supName}`);
            if (docNum) parts.push(`Documento: ${docNum}`);
            return parts.length ? parts.join("  ·  ") : undefined;
          })()}
          buttons={lotMenuFor
            ? [
              ...(!lotMenuFor.food.archived_at && !lotMenuFor.isActive
                ? [{
                  text: "Imposta come attivo",
                  handler: () => {
                    const fl = lotsByFood.get(lotMenuFor.food.id)?.find((l) =>
                      l.lot === lotMenuFor.lot
                    );
                    if (fl) void handleSetActiveLot(lotMenuFor.food, fl);
                  },
                }]
                : []),
              ...(!lotMenuFor.food.archived_at ? [{
                text: "Sposta sul prodotto corretto",
                icon: swapHorizontalOutline,
                handler: () => {
                  openStockReassignment(lotMenuFor.food.id);
                },
              }] : []),
              // T-146: "Aggiorna alimento" anche dal menu del lotto. Il food è
              // spesso precaricato da menù con conservazione/allergeni errati;
              // da qui si apre l'editor del MASTER food per correggerli. Catturo
              // la reference prima che onDidDismiss azzeri lotMenuFor.
              ...(!lotMenuFor.food.archived_at ? [{
                text: "✏️ Aggiorna prodotto",
                handler: () => {
                  const food = lotMenuFor.food;
                  openFoodEditor(food);
                },
              }] : []),
              // T-074: "Vedi documento originale" — solo se il lotto ha un
              // document_id collegato (popolato da fiscal-photo al commit).
              ...((() => {
                if (!lotMenuFor) return [];
                const fl = lotsByFood.get(lotMenuFor.food.id)?.find((l) =>
                  l.lot === lotMenuFor.lot
                );
                if (!fl?.document_id) return [];
                return [{
                  text: "📄 Vedi DDT/fattura di carico",
                  handler: () => {
                    void openLotDocument(fl.document_id!);
                  },
                }];
              })()),
              {
                text: lotMenuFor.food.archived_at
                  ? "Scarica tutto il saldo residuo"
                  : "Cancella (consumato)",
                role: "destructive" as const,
                disabled: !!lotMenuFor.food.archived_at &&
                  currentLotOnHandQty(
                    lotMenuFor.food.id,
                    lotMenuFor.lot,
                  ) <= 1e-9,
                handler: () => {
                  const fl = lotsByFood.get(lotMenuFor.food.id)?.find((l) =>
                    l.lot === lotMenuFor.lot
                  );
                  if (fl) void handleCloseLot(lotMenuFor.food, fl);
                },
              },
              { text: "Annulla", role: "cancel" as const },
            ]
            : []}
        />

        <IonToast
          isOpen={!!toast}
          message={toast ?? ""}
          duration={1500}
          onDidDismiss={() => setToast(null)}
        />
        <AiSuggestionsFab suggestions={suggestions} />
      </IonContent>
    </IonPage>
  );
}
